<template>
    <div ref="top" :class="'studio' + (isTerminalMode ? ' is-kiosk' : '')">
        <countdown
            v-if="requestTokenHeaders"
            class="uk-hidden"
            :end-time="new Date(requestTokenHeaders['expiry'] * 1000).getTime()"
            @finish="handleTokenExpiry">
            <template v-slot:process="countdown">
                {{ countdown.timeObj.ceil.s }}
            </template>
        </countdown>

        <Screensaver
            v-if="isTerminalMode && !isTerminalWithAds"
            @start="screensaverStart"
            :terminal="getInfos.terminal" />

        <div v-if="isTerminalMode">
            <Modal
                :title="$t('terminal.connector')"
                is-strict
                is-round
                :id="modalIdTerminalConnectorChecker">
                <TerminalConnnectorNotRunnning is-round />
            </Modal>
        </div>

        <div class="uk-hidden">
            <input
                id="pictures"
                ref="pictures"
                :accept="'.' + fileExtensions.join(', .')"
                multiple
                type="file"
                v-on:change="handleFilesUpload()"
                v-on:click="$refs.pictures.value = null" />
            <input
                ref="replacePicture"
                :accept="'.' + fileExtensions.join(', .')"
                type="file"
                v-on:change="replacePicture()" />
        </div>

        <header ref="nav" class="studio-nav">
            <div class="studio-container studio-nav-inner">
                <div
                    class="studio-brand"
                    v-touch:touchhold="showTerminalHandler">
                    <img
                        v-if="getInfos.logo"
                        :src="apiUrl + '/logos/' + getInfos.logo"
                        :alt="getInfos.name"
                        class="studio-logo" />
                    <span v-if="getInfos.name" class="studio-name">
                        {{ getInfos.name }}
                    </span>
                </div>

                <div class="studio-nav-end">
                    <template v-if="pictures.length > 0 && !successOrder">
                        <div class="studio-nav-total">
                            <span class="studio-muted uk-visible@s">
                                {{ getTotalSamplesCount }}
                                {{
                                    $t(
                                        getTotalSamplesCount === 1
                                            ? 'picture'
                                            : 'pictures'
                                    )
                                }}
                                ·
                            </span>
                            <strong>{{ getTotal }}</strong>
                        </div>
                        <Button
                            :action="goToCheckout"
                            :text="$t('studio.to.checkout')"
                            has-action
                            is-round
                            mode="primary" />
                    </template>
                    <Button
                        v-if="isTerminalMode"
                        :action="cancelOrder"
                        :text="$t('cancel')"
                        has-action
                        is-round
                        mode="default"
                        class="studio-nav-cancel" />
                </div>
            </div>
            <progress
                v-if="isUploadInProgress"
                class="uk-progress studio-nav-progress"
                :value="getUnifiedUploadPercentage"
                max="100" />
        </header>

        <main class="studio-container studio-main">
            <div
                v-if="
                    getInfos.absence &&
                    isDateStringPast(getAbsenceStart(getInfos.absence.start)) &&
                    isDateStringFuture(getInfos.absence.end)
                "
                class="studio-notice"
                v-html="
                    $t('absence.message', {
                        start: getFormattedDateByDateString(
                            getInfos.absence.start
                        ),
                        end: getFormattedDateByDateString(getInfos.absence.end),
                    })
                " />

            <TerminalAdsHeader
                v-if="isTerminalWithAds"
                class="uk-margin-bottom studio-ads-header"
                :terminal="getInfos.terminal" />

            <Card
                v-if="!getInfos.articles || getInfos.articles.length === 0"
                is-round
                is-dark
                mode="default">
                <div
                    v-if="isTerminalMode && !isTerminalConnectorRunning"
                    class="uk-flex uk-flex-center"
                    uk-spinner />
                <div v-else>{{ $t('article.none') }}</div>
            </Card>

            <template v-else-if="!successOrder">
                <section id="studio-pictures" class="studio-section">
                    <div class="studio-section-head is-start">
                        <h1
                            v-if="isTerminalMode && pictures.length === 0"
                            class="studio-title">
                            {{ $t('studio.sources.title') }}
                            <span class="studio-muted studio-title-sub">
                                {{ $t('studio.sources.subtitle') }}
                            </span>
                        </h1>
                        <h1 v-else class="studio-title">
                            <template v-if="pictures.length > 0">
                                {{ pictures.length }}
                            </template>
                            {{
                                $t(pictures.length === 1 ? 'picture' : 'pictures')
                            }}
                        </h1>
                        <button
                            v-if="
                                pictures.length > 0 &&
                                pictures.length < getInfos.maxPicturesPerOrder
                            "
                            type="button"
                            class="studio-add-button"
                            v-on:click="addMorePictures()">
                            <span class="studio-add-icon">
                                <Icon icon="plus" :ratio="0.7" />
                            </span>
                            {{ $t('studio.add.more') }}
                        </button>
                    </div>

                    <div
                        v-if="isTerminalMode && pictures.length === 0"
                        class="studio-sources">
                        <div
                            v-if="terminalSources === null"
                            class="uk-flex uk-flex-center"
                            uk-spinner />
                        <InfoBox
                            v-else-if="terminalSources.length === 0"
                            mode="danger"
                            icon="warning">
                            <Translate translation-key="terminal.no.source" />
                        </InfoBox>
                        <div v-else class="studio-source-grid">
                            <button
                                v-for="terminalSource in terminalSources"
                                :key="terminalSource"
                                type="button"
                                class="studio-source"
                                v-on:click="
                                    selectTerminalSource(terminalSource)
                                ">
                                <span class="studio-source-icon">
                                    <Icon
                                        :ratio="2"
                                        :icon="getIconByName(terminalSource)" />
                                </span>
                                <Translate :translation-key="terminalSource" />
                            </button>
                        </div>
                    </div>

                    <template v-if="pictures.length === 0 && !isTerminalMode">
                        <div
                            class="studio-dropzone"
                            v-on:click="triggerInputDialog()">
                            <Icon
                                :ratio="2.4"
                                class="studio-accent"
                                icon="cloud-upload" />
                            <div class="studio-dropzone-title">
                                <span class="studio-if-pointer">
                                    {{ $t('studio.empty.title') }}
                                </span>
                                <span class="studio-if-touch">
                                    {{ $t('studio.empty.touch.title') }}
                                </span>
                            </div>
                            <div class="studio-muted uk-margin-bottom">
                                <span class="studio-if-pointer">
                                    {{ $t('studio.empty.subtitle') }}
                                </span>
                                <span class="studio-if-touch">
                                    {{ $t('studio.empty.touch.subtitle') }}
                                </span>
                            </div>
                            <Button
                                :text="$t('studio.choose.pictures')"
                                is-round
                                is-large
                                mode="primary" />
                            <div
                                class="studio-muted studio-small uk-margin-top">
                                {{ uploadTypesText }}
                                · {{ $t('max') }} {{ getMaxFileSizeInMB }} MB
                                {{ $t('per.x', {x: $t('picture')}) }} ·
                                {{ $t('max') }}
                                {{ getInfos.maxPicturesPerOrder }}
                                {{ $t('pictures') }}
                            </div>
                            <div
                                v-if="picturesError"
                                class="input-error uk-margin-top">
                                {{ picturesError }}
                            </div>
                        </div>

                        <div class="studio-prices-head">
                            <button
                                type="button"
                                class="studio-add-button"
                                v-on:click="showPrices(true)">
                                <span class="studio-add-icon">
                                    <Icon icon="tag" :ratio="0.7" />
                                </span>
                                {{ $t('studio.formats.available') }}
                            </button>
                        </div>
                    </template>

                    <template v-if="pictures.length > 0">
                        <div v-if="pictures.length > 1" class="studio-bulk">
                            <span class="studio-small studio-muted">
                                {{ $t('studio.all.pictures') }}
                            </span>
                            <button
                                type="button"
                                class="studio-chip is-strong"
                                v-on:click="openFormatSheet(null)">
                                <span v-if="sharedArticleID">
                                    {{
                                        getArticleFormat(
                                            getArticleByID(sharedArticleID)
                                        )
                                    }}
                                    ·
                                    {{
                                        getPaperName(
                                            getArticleByID(sharedArticleID).paper
                                        )
                                    }}
                                </span>
                                <span v-else>{{ $t('format.and.paper') }}</span>
                                <Icon icon="chevron-down" :ratio="0.6" />
                            </button>
                            <div
                                v-if="!isTerminalMode"
                                class="studio-dropdown"
                                uk-dropdown="mode: click; pos: bottom-left; offset: 6; flip: false"
                                v-on:beforeshow.self="
                                    openOverviewGroup($event, sharedArticleID)
                                ">
                                <ul
                                    class="studio-overview"
                                    uk-accordion="duration: 200"
                                    v-on:beforeshow="followDropdown"
                                    v-on:beforehide="followDropdown"
                                    v-on:shown="followDropdown"
                                    v-on:hidden="followDropdown">
                                    <li
                                        v-for="group in articleOverview"
                                        :key="'uo' + group.format"
                                        class="studio-overview-row">
                                        <a
                                            class="uk-accordion-title studio-overview-format"
                                            href="#">
                                            {{ group.format }}
                                            <Icon
                                                icon="chevron-down"
                                                :ratio="0.6" />
                                        </a>
                                        <div
                                            class="uk-accordion-content studio-overview-papers">
                                            <button
                                                v-for="paper in group.papers"
                                                :key="'uop' + paper.value"
                                                type="button"
                                                :class="
                                                    'studio-overview-paper' +
                                                    (sharedArticleID ===
                                                    paper.value
                                                        ? ' active'
                                                        : '') +
                                                    (paper.tierHint
                                                        ? ' has-tiers'
                                                        : '') +
                                                    (paper.badge
                                                        ? ' has-badge badge-' +
                                                          paper.badge
                                                        : '')
                                                "
                                                v-on:click="
                                                    setUnifiedArticle(
                                                        paper.value
                                                    );
                                                    applyBulk();
                                                    hideDropdown($event);
                                                ">
                                                <span>{{
                                                    getPaperName(paper.text)
                                                }}</span>
                                                <small>{{ paper.price }}</small>
                                                <span
                                                    v-if="paper.tierHint"
                                                    class="studio-overview-tiers">
                                                    {{ paper.tierHint }}
                                                </span>
                                                <span
                                                    v-if="paper.badge"
                                                    class="studio-overview-badge">
                                                    {{ $t('badge.' + paper.badge) }}
                                                </span>
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <transition-group
                            name="studio-grid"
                            tag="div"
                            class="studio-grid"
                            v-on:before-leave="fixLeavingTile">
                            <template v-for="group in pictureGroups">
                                <div
                                    v-if="pictureGroups.length > 1"
                                    :key="'g' + group.articleID"
                                    class="studio-group-title">
                                    {{ group.label }}
                                </div>
                                <div
                                    v-for="item in group.pictures"
                                    :key="'p' + item.id"
                                    :id="'picture-' + item.id"
                                    :class="
                                        'studio-tile' +
                                        (isPictureTooLength(item)
                                            ? ' has-warning'
                                            : '') +
                                        (item.id === newPictureId ? ' is-new' : '') +
                                        (item.error ? ' has-error' : '')
                                    ">
                                    <div
                                        class="studio-tile-stage"
                                        v-on:click="openEditor(item)">
                                        <div
                                            v-if="!item.coordinates && !item.error"
                                            class="studio-skeleton" />
                                        <div
                                            v-else-if="item.coordinates"
                                            class="studio-crop-frame"
                                            :style="getCropFrameStyle(item, 1.25)">
                                            <div
                                                :class="
                                                    'studio-crop-box' +
                                                    getPrintBorderClass(
                                                        item.originKey
                                                    )
                                                "
                                                :style="getCropBoxStyle(item)" />
                                        </div>

                                        <div
                                            v-if="item.coordinates && !item.error"
                                            class="studio-tile-hover">
                                            <span>
                                                <Icon
                                                    icon="crosshairs"
                                                    :ratio="0.8" />
                                                {{ $t('studio.crop') }}
                                            </span>
                                        </div>

                                        <div
                                            class="studio-tile-tools"
                                            v-on:click.stop>
                                            <button
                                                v-if="item.coordinates && !item.error"
                                                type="button"
                                                class="studio-tile-crop"
                                                :uk-tooltip="
                                                    'title:' + $t('studio.crop')
                                                "
                                                :aria-label="$t('studio.crop')"
                                                v-on:click="openEditor(item)">
                                                <Icon
                                                    icon="crosshairs"
                                                    :ratio="0.75" />
                                            </button>
                                            <button
                                                v-if="
                                                    !item.error &&
                                                    pictures.length <
                                                        getInfos.maxPicturesPerOrder
                                                "
                                                type="button"
                                                :class="
                                                    item.cloneInProgress
                                                        ? 'is-busy'
                                                        : ''
                                                "
                                                :aria-disabled="
                                                    item.cloneInProgress
                                                "
                                                :uk-tooltip="
                                                    'title:' +
                                                    $t(
                                                        item.cloneInProgress
                                                            ? 'studio.duplicating'
                                                            : 'duplicate'
                                                    )
                                                "
                                                :aria-label="$t('duplicate')"
                                                v-on:click="
                                                    clonePicture(item.originKey)
                                                ">
                                                <Icon icon="copy" :ratio="0.75" />
                                            </button>
                                            <button
                                                type="button"
                                                :uk-tooltip="
                                                    'title:' + $t('delete')
                                                "
                                                :aria-label="$t('delete')"
                                                v-on:click="
                                                    deletePicture(item.originKey)
                                                ">
                                                <Icon icon="trash" :ratio="0.75" />
                                            </button>
                                        </div>

                                        <span
                                            v-if="isPictureTooLength(item)"
                                            class="studio-flag danger">
                                            <Icon icon="warning" :ratio="0.6" />
                                            {{ $t('studio.too.long') }}
                                        </span>
                                        <span
                                            v-else-if="isLowResolution(item)"
                                            class="studio-flag warning"
                                            :uk-tooltip="
                                                'title:' +
                                                $t('resolution.too.low.message')
                                            ">
                                            <Icon icon="warning" :ratio="0.6" />
                                            {{ $t('studio.low.resolution') }}
                                        </span>

                                        <progress
                                            v-if="!item.error && !item.isDone"
                                            class="uk-progress studio-tile-progress"
                                            :value="item.uploadPercentage"
                                            max="100" />

                                        <div
                                            v-if="item.error"
                                            class="studio-tile-error"
                                            v-on:click.stop>
                                            <Icon icon="warning" :ratio="1.1" />
                                            <span v-if="isUploadTypeError(item)">
                                                {{
                                                    $t('files.type.error', {
                                                        name: $t('pictures'),
                                                        types: uploadTypesText,
                                                    })
                                                }}
                                            </span>
                                            <span
                                                v-else-if="
                                                    isPictureUnreadable(item)
                                                ">
                                                {{
                                                    $t(
                                                        'studio.picture.unreadable'
                                                    )
                                                }}
                                            </span>
                                            <span v-else>{{
                                                $t('studio.upload.failed')
                                            }}</span>
                                            <!-- the same file fails again:
                                                 only removing it helps -->
                                            <a
                                                v-if="
                                                    !isUploadTypeError(item) &&
                                                    !isPictureUnreadable(item)
                                                "
                                                class="studio-link"
                                                v-on:click="retryUpload(item)">
                                                {{ $t('retry') }}
                                            </a>
                                        </div>
                                    </div>

                                    <div class="studio-tile-body">
                                        <div class="studio-chips">
                                            <button
                                                type="button"
                                                class="studio-chip is-strong"
                                                v-on:click="
                                                    openFormatSheet(
                                                        item.originKey
                                                    )
                                                ">
                                                <span>
                                                    {{ item.format }} ·
                                                    {{
                                                        getPaperName(
                                                            getArticleByID(
                                                                item.articleID
                                                            ).paper
                                                        )
                                                    }}
                                                </span>
                                                <Icon
                                                    icon="chevron-down"
                                                    :ratio="0.6" />
                                            </button>
                                            <div
                                                v-if="!isTerminalMode"
                                                class="studio-dropdown"
                                                uk-dropdown="mode: click; pos: bottom-left; offset: 6; flip: false"
                                                v-on:beforeshow.self="
                                                    openOverviewGroup(
                                                        $event,
                                                        item.articleID
                                                    )
                                                ">
                                                <ul
                                                    class="studio-overview"
                                                    uk-accordion="duration: 200"
                                                    v-on:beforeshow="
                                                        followDropdown
                                                    "
                                                    v-on:beforehide="
                                                        followDropdown
                                                    "
                                                    v-on:shown="followDropdown"
                                                    v-on:hidden="
                                                        followDropdown
                                                    ">
                                                    <li
                                                        v-for="group in articleOverview"
                                                        :key="
                                                            item.id + group.format
                                                        "
                                                        class="studio-overview-row">
                                                        <a
                                                            class="uk-accordion-title studio-overview-format"
                                                            href="#">
                                                            {{ group.format }}
                                                            <Icon
                                                                icon="chevron-down"
                                                                :ratio="0.6" />
                                                        </a>
                                                        <div
                                                            class="uk-accordion-content studio-overview-papers">
                                                            <button
                                                                v-for="paper in group.papers"
                                                                :key="
                                                                    item.id +
                                                                    paper.value
                                                                "
                                                                type="button"
                                                                :class="
                                                                    'studio-overview-paper' +
                                                                    (item.articleID ===
                                                                    paper.value
                                                                        ? ' active'
                                                                        : '') +
                                                                    (paper.tierHint
                                                                        ? ' has-tiers'
                                                                        : '') +
                                                                    (paper.badge
                                                                        ? ' has-badge badge-' +
                                                                          paper.badge
                                                                        : '')
                                                                "
                                                                v-on:click="
                                                                    setPictureArticle(
                                                                        item.originKey,
                                                                        paper.value
                                                                    );
                                                                    hideDropdown(
                                                                        $event
                                                                    );
                                                                ">
                                                                <span>{{
                                                                    getPaperName(
                                                                        paper.text
                                                                    )
                                                                }}</span>
                                                                <small>{{
                                                                    paper.price
                                                                }}</small>
                                                                <span
                                                                    v-if="
                                                                        paper.tierHint
                                                                    "
                                                                    class="studio-overview-tiers">
                                                                    {{
                                                                        paper.tierHint
                                                                    }}
                                                                </span>
                                                                <span
                                                                    v-if="
                                                                        paper.badge
                                                                    "
                                                                    class="studio-overview-badge">
                                                                    {{
                                                                        $t(
                                                                            'badge.' +
                                                                                paper.badge
                                                                        )
                                                                    }}
                                                                </span>
                                                            </button>
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>

                                            <template
                                                v-if="
                                                    getPrintBorders(item.articleID)
                                                        .length > 0
                                                ">
                                                <button
                                                    type="button"
                                                    :class="
                                                        'studio-chip' +
                                                        (item.printBorderID
                                                            ? ''
                                                            : ' is-ghost')
                                                    ">
                                                    <Icon
                                                        v-if="!item.printBorderID"
                                                        icon="plus"
                                                        :ratio="0.7" />
                                                    <span>{{
                                                        getPrintBorderByKey(
                                                            item.originKey
                                                        )
                                                            ? getPrintBorderTitle(
                                                                  getPrintBorderByKey(
                                                                      item.originKey
                                                                  )
                                                              )
                                                            : $t('studio.frame')
                                                    }}</span>
                                                    <Icon
                                                        v-if="item.printBorderID"
                                                        icon="chevron-down"
                                                        :ratio="0.6" />
                                                </button>
                                                <div
                                                    class="studio-dropdown is-narrow"
                                                    uk-dropdown="mode: click; pos: bottom-left; offset: 6; flip: false">
                                                    <button
                                                        type="button"
                                                        :class="
                                                            'studio-overview-paper' +
                                                            (item.printBorderID
                                                                ? ''
                                                                : ' active')
                                                        "
                                                        v-on:click="
                                                            item.printBorderID = '';
                                                            hideDropdown($event);
                                                        ">
                                                        <span>{{
                                                            $t('without.frame')
                                                        }}</span>
                                                    </button>
                                                    <button
                                                        v-for="printBorder in getPrintBorders(
                                                            item.articleID
                                                        )"
                                                        :key="
                                                            item.id + printBorder.id
                                                        "
                                                        type="button"
                                                        :class="
                                                            'studio-overview-paper' +
                                                            (item.printBorderID ===
                                                            printBorder.id
                                                                ? ' active'
                                                                : '')
                                                        "
                                                        v-on:click="
                                                            item.printBorderID =
                                                                printBorder.id;
                                                            hideDropdown($event);
                                                        ">
                                                        <span
                                                            class="studio-swatch"
                                                            :style="{
                                                                background:
                                                                    printBorder.color,
                                                            }" />
                                                        <span>{{
                                                            getPrintBorderTitle(
                                                                printBorder
                                                            )
                                                        }}</span>
                                                    </button>
                                                </div>
                                            </template>
                                        </div>

                                        <div
                                            v-if="
                                                isVariableHeight(item.originKey) &&
                                                item.measurementValue
                                            "
                                            :class="
                                                'studio-small' +
                                                (isPictureTooLength(item)
                                                    ? ' studio-danger'
                                                    : ' studio-muted')
                                            ">
                                            {{ $t('pictureLength') }}
                                            {{
                                                formatMeasurement(
                                                    item.measurementValue
                                                )
                                            }}
                                            {{ $t(getInfos.measurementUnit) }}
                                            <span v-if="isPictureTooLength(item)">
                                                ({{ $t('max') }}
                                                {{
                                                    getArticleByID(item.articleID)
                                                        .maxLength
                                                }})
                                            </span>
                                        </div>

                                        <div class="studio-tile-footer">
                                            <div class="studio-stepper">
                                                <button
                                                    type="button"
                                                    :disabled="item.amount <= 1"
                                                    :aria-label="
                                                        $t('amount') + ' -1'
                                                    "
                                                    v-on:click="
                                                        manipulateAmount(
                                                            item.originKey,
                                                            -1
                                                        )
                                                    ">
                                                    <Icon
                                                        icon="minus"
                                                        :ratio="0.7" />
                                                </button>
                                                <input
                                                    v-model.number="item.amount"
                                                    type="number"
                                                    min="1"
                                                    :aria-label="$t('amount')"
                                                    v-on:input="
                                                        validateAmount(
                                                            item.originKey
                                                        )
                                                    " />
                                                <button
                                                    type="button"
                                                    :aria-label="
                                                        $t('amount') + ' +1'
                                                    "
                                                    v-on:click="
                                                        manipulateAmount(
                                                            item.originKey,
                                                            +1
                                                        )
                                                    ">
                                                    <Icon
                                                        icon="plus"
                                                        :ratio="0.7" />
                                                </button>
                                            </div>
                                            <div class="studio-tile-price">
                                                <div class="studio-strong">
                                                    {{
                                                        getNumberWithCurrency(
                                                            getCostsByAmountAndArticleId(
                                                                item
                                                            )
                                                        )
                                                    }}
                                                </div>
                                                <small
                                                    v-if="getNextTierHint(item)"
                                                    class="studio-tier-hint">
                                                    {{ getNextTierHint(item) }}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <div
                                v-if="
                                    pictures.length <
                                    getInfos.maxPicturesPerOrder
                                "
                                key="add"
                                class="studio-tile studio-tile-add"
                                v-on:click="addMorePictures()">
                                <Icon icon="plus" :ratio="1.4" />
                                <span>{{ $t('studio.add.more') }}</span>
                                <small
                                    v-if="isDeviceMobile && isUploadInProgress">
                                    {{ $t('upload.in.progress.hint') }}
                                </small>
                            </div>
                        </transition-group>

                        <InfoBox
                            v-if="
                                pictures.length >= getInfos.maxPicturesPerOrder
                            "
                            class="uk-margin-top"
                            mode="warning">
                            {{
                                $t('pictures.max.reached', {
                                    x: getInfos.maxPicturesPerOrder,
                                })
                            }}
                        </InfoBox>
                    </template>
                </section>

                <section
                    v-if="pictures.length > 0"
                    id="studio-checkout"
                    class="studio-section">
                    <div class="studio-checkout">
                        <div class="studio-section-head">
                            <h1 class="studio-title">{{ $t('order') }}</h1>
                        </div>
                        <Card
                            is-round
                            is-dark
                            mode="default"
                            class="studio-checkout-card">
                            <div id="checkout-contact" class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('contact.details') }}
                                </div>
                                <div class="uk-grid-small" uk-grid>
                                    <div
                                        v-for="(
                                            inputField, key
                                        ) in contactFields"
                                        :key="'i' + inputField.name"
                                        :class="
                                            key === 0 || isTerminalMode
                                                ? 'uk-width-1-1'
                                                : 'uk-width-1-2@s uk-width-1-1'
                                        ">
                                        <TextField
                                            v-model="inputField.value"
                                            is-round
                                            :autocomplete="
                                                inputField.autocomplete
                                            "
                                            :field-key="key"
                                            :icon="inputField.icon"
                                            :is-optional="inputField.optional"
                                            :label="inputField.label"
                                            :name="inputField.name"
                                            :on-enter="complete"
                                            :rules="inputField.rules"
                                            :type="inputField.type"
                                            :disabled="
                                                successOrder || loadingOrder
                                            "
                                            :validate="triggerContactFields"
                                            @setValid="setValidContactFields" />
                                    </div>
                                </div>
                            </div>

                            <div id="payment-method" class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('payment.method') }}
                                </div>
                                <div class="studio-options wide">
                                    <span
                                        v-for="method in getPaymentMethodOptions"
                                        :key="method"
                                        v-on:click="setPaymentMethod(method)">
                                        <LabelSelect
                                            is-round
                                            fill-selected
                                            class="lunacart-theme-selected"
                                            :is-disabled="
                                                successOrder || loadingOrder
                                            "
                                            :is-selected="
                                                paymentMethod === method
                                            ">
                                            <div
                                                class="uk-text-center studio-option">
                                                <Icon
                                                    :icon="
                                                        getIconByName(method)
                                                    "
                                                    :ratio="1.2" />
                                                <div class="paper-text">
                                                    {{ $t(method) }}
                                                </div>
                                            </div>
                                        </LabelSelect>
                                    </span>
                                </div>

                                <div
                                    v-if="isAddressInPayment"
                                    key="payment-address"
                                    class="uk-margin-medium-top">
                                    <AddressFields
                                        v-model="country"
                                        :fields="addressFields"
                                        :disabled="successOrder || loadingOrder"
                                        :validate="triggerAddressFields"
                                        :on-enter="complete"
                                        :is-full="isTerminalMode"
                                        @setValid="setValidAddressFields" />
                                </div>

                                <div
                                    v-if="isQRCodePayment"
                                    class="uk-margin-medium-top studio-qr-pay-hint">
                                    <Icon icon="phone" :ratio="1.4" />
                                    <span>{{ $t('studio.qr.pay.hint') }}</span>
                                </div>
                                <div
                                    class="uk-margin-medium-top"
                                    v-else-if="paymentMethod === 'online'">
                                    <div
                                        v-if="
                                            paymentPickRequired && !paymentPicked
                                        "
                                        class="studio-danger studio-payment-pick">
                                        {{ $t('studio.payment.pick') }}
                                    </div>
                                    <stripe-element-payment
                                        v-if="paymentKey"
                                        :key="elementsOptions.clientSecret"
                                        :create-options="paymentCreateOptions"
                                        @element-ready="setPaymentRendered"
                                        @element-change="onElementChange"
                                        @element-focus="cardKeyboardShown = true"
                                        @element-blur="cardKeyboardShown = false"
                                        ref="paymentRef"
                                        :pk="getInfos.stripe.key"
                                        :elements-options="elementsOptions"
                                        :confirm-params="confirmParams" />
                                    <div
                                        v-else
                                        class="uk-flex uk-flex-center"
                                        uk-spinner />
                                </div>
                            </div>

                            <div class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('studio.order.summary') }}
                                </div>
                                <div
                                    v-for="row in summaryRows"
                                    :key="'sr' + row.articleID"
                                    class="studio-summary-row">
                                    <div class="uk-grid-small" uk-grid>
                                        <div class="uk-width-expand" uk-leader>
                                            <span class="uk-badge badge">{{
                                                row.amount
                                            }}</span>
                                            {{ row.label }}
                                        </div>
                                        <div>
                                            {{
                                                getNumberWithCurrency(row.total)
                                            }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="row.hint"
                                        class="studio-hint-tag">
                                        <Icon icon="tag" :ratio="0.65" />
                                        {{ row.hint }}
                                    </div>
                                </div>

                                <div
                                    v-if="getFeeBySource() > 0"
                                    key="summary-fee"
                                    class="studio-summary-row is-extra">
                                    <div class="uk-grid-small" uk-grid>
                                        <div class="uk-width-expand" uk-leader>
                                            {{ $t('service.fee') }}
                                        </div>
                                        <div>
                                            {{ getNumberWithCurrency(getFee) }}
                                        </div>
                                    </div>
                                    <div
                                        v-if="getFeeDiscountBySource() != null"
                                        class="studio-small studio-muted">
                                        {{ getFeeDiscountText }}
                                    </div>
                                </div>

                                <div
                                    v-if="deliveryType === 'shipping'"
                                    key="summary-shipping"
                                    class="studio-summary-row is-extra">
                                    <div class="uk-grid-small" uk-grid>
                                        <div class="uk-width-expand" uk-leader>
                                            {{ $t('shipping.costs') }}
                                        </div>
                                        <div>
                                            {{
                                                getNumberWithCurrency(
                                                    getShippingCosts
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    key="summary-total"
                                    class="uk-grid-small total-costs-text"
                                    uk-grid>
                                    <div class="uk-width-expand" uk-leader>
                                        {{ $t('total') }}
                                    </div>
                                    <div>{{ getTotal }}</div>
                                </div>
                                <div class="vat">
                                    {{
                                        $t('vat.message', {
                                            vat: getNumberFormatted(
                                                getInfos.vat,
                                                0
                                            ),
                                        })
                                    }}
                                </div>
                            </div>

                            <div id="checkout-terms" class="studio-block">
                                <div class="studio-card-title">
                                    {{ $t('confirm') }}
                                </div>
                                <Checkbox
                                    v-model="confirmTerms"
                                    class="studio-terms"
                                    is-round
                                    :disabled="successOrder || loadingOrder"
                                    :is-disabled="successOrder || loadingOrder"
                                    :validate="triggerTerms"
                                    label="termsAndConditions.confirm"
                                    name="terms"
                                    rules="required"
                                    @setValid="setTermsValid" />

                            <InfoBox
                                v-if="isUploadInProgress"
                                class="uk-margin-bottom uk-animation-fade"
                                mode="warning">
                                {{ $t('upload.in.progress') }}
                                <progress
                                    :value="getUnifiedUploadPercentage"
                                    class="uk-progress studio-progress"
                                    max="100" />
                            </InfoBox>

                            <InfoBox
                                v-if="isAnyUploadError"
                                class="uk-margin-bottom uk-animation-fade"
                                mode="danger">
                                {{ $t('any.pictures.error') }}
                                <div class="uk-margin-small-top" />
                                <Button
                                    :action="retryFailedPictures"
                                    :text="$t('retry')"
                                    has-action
                                    is-round
                                    mode="primary" />
                            </InfoBox>

                            <Button
                                :action="complete"
                                :is-disabled="
                                    isUploadInProgress ||
                                    isAnyUploadError ||
                                    isPaymentInProgress ||
                                    !paymentRendered
                                "
                                :is-loading="loadingOrder"
                                :text="$t('paid.order')"
                                class="studio-pay"
                                has-action
                                is-round
                                is-large
                                is-max
                                mode="success">
                                <span class="studio-pay-amount">
                                    {{ getTotal }}
                                </span>
                            </Button>

                            <div v-if="errorOrder" class="uk-margin-top" />
                            <ErrorMessage
                                :custom-errors="[
                                    {
                                        code: 408,
                                        message: $t('session.timed.out'),
                                    },
                                ]"
                                :error="errorOrder" />

                            <InfoBox
                                v-if="paymentError"
                                class="uk-margin-top uk-animation-fade"
                                mode="danger">
                                <Translate
                                    translation-key="payment.denied.error" />
                            </InfoBox>
                            </div>
                        </Card>
                    </div>
                </section>
            </template>

            <Card
                v-else
                is-round
                is-dark
                mode="default"
                class="studio-success uk-text-center">
                <div class="studio-success-icon">
                    <Icon icon="check" :ratio="2" />
                </div>
                <div class="studio-title">{{ $t('order.created.title') }}</div>
                <div class="studio-muted uk-margin-small-top">
                    {{
                        $t(
                            contactFields[1].value
                                ? 'paid.order.success'
                                : 'order.created.message'
                        )
                    }}
                </div>
                <div class="uk-margin-medium-top" />
                <Button
                    :action="reload"
                    :text="$t('order.more.pictures')"
                    has-action
                    is-round
                    is-large
                    mode="primary" />

                <div
                    v-if="!isApp && !isTerminalMode"
                    class="uk-text-left uk-margin-medium-top">
                    <PWAPromptForm
                        v-if="getInfos && getInfos.name"
                        :name="getInfos.name"
                        :prompt-installer-event="promptInstallerEvent" />
                    <PWAHintForm
                        v-if="getInfos && getInfos.name"
                        :name="getInfos.name" />
                </div>
            </Card>

            <TerminalAdsFooter
                v-if="isTerminalWithAds && getInfos.terminal.ads"
                class="uk-margin-top studio-ads-footer"
                :terminal="getInfos.terminal" />
        </main>

        <footer class="studio-footer">
            <div class="studio-container">
                <span v-if="getInfos.footerText">{{
                    getInfos.footerText
                }}</span>
                <ToggleItem is-dark toggle="target: #termsAndConditions">
                    {{ $t('termsAndConditions') }}
                </ToggleItem>
                <ToggleItem is-dark toggle="target: #dataPrivacy">
                    {{ $t('dataPrivacy') }}
                </ToggleItem>
                <ToggleItem is-dark toggle="target: #siteNotice">
                    {{ $t('siteNotice') }}
                </ToggleItem>
                <ToggleItem
                    v-if="isCookieSettingsShown"
                    is-dark
                    v-on:click.native="openCookieSettings()">
                    {{ $t('cookies.settings') }}
                </ToggleItem>
                <LanguageSelector id="studio" is-round />
            </div>
        </footer>

        <!-- one keyboard for every field, docked like a device keyboard; the
             spacer lets the page scroll the field above it -->
        <div
            v-if="keyboardTarget"
            :style="{height: dockedKeyboardHeight + 'px'}" />
        <!-- the inner div is moved to <body> on mount, like UIkit's modals:
             inside the view's faded wrapper it stayed under the control
             centre's modal. The outer one stays, as Vue inserts siblings
             before it -->
        <div>
            <div ref="keyboardLayer">
                <!-- leaves with the same slide reversed -->
                <transition
                    enter-active-class="uk-animation-slide-bottom-small"
                    leave-active-class="uk-animation-slide-bottom-small uk-animation-reverse">
                    <div
                        v-if="keyboardTarget"
                        ref="dockedKeyboard"
                        :class="
                            'studio-docked-keyboard' +
                            (isNumpad ? ' is-numpad' : '')
                        "
                        v-on:mousedown.prevent>
                        <SimpleKeyboard
                            :key="isNumpad ? 'numpad' : 'keys'"
                            keyboard-class="simple-keyboard-studio"
                            :has-numpad="isNumpad"
                            prevent-mouse-down
                            @onKeyPress="pressKey" />
                    </div>
                </transition>
            </div>
        </div>

        <!-- Abbrechen with pictures: asked here, not by the browser's
             leave dialog -->
        <transition name="studio-editor" :duration="300">
            <div
                v-if="isCancelAskShown"
                class="studio-editor"
                v-on:mousedown.self="showCancelAsk(false)">
                <div class="studio-editor-panel is-auto studio-cancel-ask">
                    <div class="studio-editor-bar">
                        <div class="studio-editor-title">
                            <div>{{ $t('studio.cancel.title') }}</div>
                            <div class="studio-editor-sub">
                                {{ $t('studio.cancel.message') }}
                            </div>
                        </div>
                    </div>
                    <div class="studio-cancel-ask-actions">
                        <button
                            type="button"
                            class="studio-soft-button"
                            v-on:click="showCancelAsk(false)">
                            {{ $t('studio.cancel.back') }}
                        </button>
                        <Button
                            :action="confirmCancel"
                            :text="$t('studio.cancel.confirm')"
                            has-action
                            is-round
                            mode="danger" />
                    </div>
                </div>
            </div>
        </transition>

        <transition name="studio-editor" :duration="300">
            <div
                v-if="isPricesShown"
                class="studio-editor"
                v-on:mousedown.self="showPrices(false)">
                <div class="studio-editor-panel is-auto">
                    <div class="studio-editor-bar">
                        <div class="studio-editor-title">
                            <div>{{ $t('studio.formats.available') }}</div>
                            <div class="studio-editor-sub">
                                {{ $t('studio.formats.hint') }}
                            </div>
                        </div>
                        <button
                            type="button"
                            class="studio-round-button"
                            :aria-label="$t('close')"
                            v-on:click="showPrices(false)">
                            <Icon icon="close" />
                        </button>
                    </div>
                    <div class="studio-prices">
                        <div
                            v-for="group in articleOverview"
                            :key="'pl' + group.format"
                            class="studio-price">
                            <div class="studio-price-format">
                                {{ group.format }}
                            </div>
                            <template v-for="paper in group.papers">
                                <div
                                    :key="'plp' + paper.value"
                                    class="studio-price-row">
                                    <span>{{ getPaperName(paper.text) }}</span>
                                    <span class="studio-strong">
                                        {{ paper.price }}
                                    </span>
                                </div>
                                <div
                                    v-if="paper.tierHint"
                                    :key="'plt' + paper.value"
                                    class="studio-overview-tiers">
                                    {{ paper.tierHint }}
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="studio-editor" :duration="300">
            <div
                v-if="editorPicture"
                class="studio-editor"
                v-on:mousedown.self="closeEditor">
                <div class="studio-editor-panel is-crop">
                    <div class="studio-editor-bar">
                        <div class="studio-editor-title">
                            <div>{{ $t('studio.crop') }}</div>
                            <div class="studio-editor-sub">
                                {{
                                    $t('studio.picture.x.of.y', {
                                        x: editorIndex + 1,
                                        y: groupedPictures.length,
                                    })
                                }}
                                · {{ editorPicture.format }} ·
                                {{
                                    getPaperName(
                                        getArticleByID(editorPicture.articleID).paper
                                    )
                                }}
                            </div>
                        </div>
                        <div class="studio-editor-nav">
                            <span
                                :uk-tooltip="
                                    'title:' +
                                    $t(
                                        editorIndex <= 0
                                            ? 'studio.picture.first'
                                            : 'studio.picture.previous'
                                    )
                                ">
                                <button
                                    type="button"
                                    class="studio-round-button"
                                    :disabled="editorIndex <= 0"
                                    :aria-label="$t('previous')"
                                    v-on:click="editorGo(-1)">
                                    <Icon icon="chevron-left" />
                                </button>
                            </span>
                            <span
                                :uk-tooltip="
                                    'title:' +
                                    $t(
                                        editorIndex >= groupedPictures.length - 1
                                            ? 'studio.picture.last'
                                            : 'studio.picture.next'
                                    )
                                ">
                                <button
                                    type="button"
                                    class="studio-round-button"
                                    :disabled="
                                        editorIndex >= groupedPictures.length - 1
                                    "
                                    :aria-label="$t('next')"
                                    v-on:click="editorGo(1)">
                                    <Icon icon="chevron-right" />
                                </button>
                            </span>
                            <button
                                type="button"
                                class="studio-round-button studio-editor-close"
                                :aria-label="$t('close')"
                                :uk-tooltip="'title:' + $t('close')"
                                v-on:click="closeEditor()">
                                <Icon icon="close" />
                            </button>
                        </div>
                    </div>

                    <div class="studio-editor-body">
                        <div class="studio-editor-stage">
                            <cropper
                                v-if="editorPicture.coordinates"
                                :key="editorCropperKey"
                                ref="editorCropper"
                                class="studio-cropper"
                                :src="editorPicture.thumbnailUrl"
                                :debounce="300"
                                :move-image="true"
                                :auto-zoom="false"
                                :transitions="true"
                                default-boundaries="fit"
                                :resize-image="{
                                    wheel: true,
                                    adjustStencil: true,
                                    touch: true,
                                }"
                                :default-size="editorDefaultSize"
                                :default-position="editorDefaultPosition"
                                :style="getPrintBorderVars(editorKey)"
                                :stencil-props="{
                                    aspectRatio:
                                        getCropperRatioByPictureKey(editorKey),
                                    class: 'cropper-stencil',
                                    previewClass:
                                        'cropper-stencil__preview' +
                                        getPrintBorderClass(editorKey, true),
                                    draggingClass: 'cropper-stencil--dragging',
                                }"
                                @change="onEditorCropperChange" />
                            <div v-else uk-spinner />
                        </div>

                        <!-- like the file browser: the formats as tiles, one
                             opens its papers, "Zurück" goes back to the formats -->
                        <div v-if="isTerminalMode" class="studio-editor-side">
                            <template v-if="editorFormat === null">
                                <div class="studio-sheet-group-title">
                                    {{ $t('format') }}
                                </div>
                                <button
                                    v-for="group in articleOverview"
                                    :key="'ef' + group.format"
                                    type="button"
                                    :class="
                                        'studio-editor-side-tile' +
                                        (group.format === editorPicture.format
                                            ? ' active'
                                            : '')
                                    "
                                    v-on:click="editorFormat = group.format">
                                    <span>{{ group.format }}</span>
                                    <Icon icon="chevron-right" />
                                </button>
                            </template>
                            <template v-else>
                                <button
                                    type="button"
                                    class="studio-editor-side-tile is-back"
                                    v-on:click="editorFormat = null">
                                    <Icon icon="arrow-left" />
                                    <span>{{ $t('back') }}</span>
                                </button>
                                <div class="studio-sheet-group-title">
                                    {{ editorFormat }}
                                </div>
                                <div class="studio-sheet-papers">
                                    <button
                                        v-for="paper in editorFormatPapers"
                                        :key="'ep' + paper.value"
                                        type="button"
                                        :class="
                                            'studio-overview-paper studio-sheet-paper' +
                                            (editorPicture.articleID ===
                                            paper.value
                                                ? ' active'
                                                : '') +
                                            (paper.badge
                                                ? ' has-badge badge-' +
                                                  paper.badge
                                                : '')
                                        "
                                        v-on:click="
                                            setPictureArticle(
                                                editorKey,
                                                paper.value
                                            )
                                        ">
                                        <span class="studio-sheet-paper-name">
                                            {{ getPaperName(paper.text) }}
                                        </span>
                                        <small>{{ paper.price }}</small>
                                        <span
                                            v-if="paper.tierHint"
                                            class="studio-overview-tiers">
                                            {{ paper.tierHint }}
                                        </span>
                                        <span
                                            v-if="paper.badge"
                                            class="studio-overview-badge">
                                            {{ $t('badge.' + paper.badge) }}
                                        </span>
                                    </button>
                                </div>
                            </template>
                        </div>
                    </div>

                    <div class="studio-editor-tools">
                        <div v-if="!isTerminalMode" class="studio-editor-format">
                            <button
                                type="button"
                                class="studio-chip is-strong"
                                v-on:click="openFormatSheet(editorKey)">
                                <span>
                                    {{ editorPicture.format }} ·
                                    {{
                                        getPaperName(
                                            getArticleByID(editorPicture.articleID)
                                                .paper
                                        )
                                    }}
                                </span>
                                <Icon icon="chevron-down" :ratio="0.6" />
                            </button>
                            <div
                                v-if="!isTerminalMode"
                                class="studio-dropdown is-upward"
                                uk-dropdown="mode: click; pos: top-left; offset: 6"
                                v-on:beforeshow.self="
                                    openOverviewGroup($event, editorPicture.articleID)
                                ">
                                <ul
                                    class="studio-overview"
                                    uk-accordion="duration: 200"
                                    v-on:beforeshow="followDropdown"
                                    v-on:beforehide="followDropdown"
                                    v-on:shown="followDropdown"
                                    v-on:hidden="followDropdown">
                                    <li
                                        v-for="group in articleOverview"
                                        :key="'eo' + group.format"
                                        class="studio-overview-row">
                                        <a
                                            class="uk-accordion-title studio-overview-format"
                                            href="#">
                                            {{ group.format }}
                                            <Icon icon="chevron-down" :ratio="0.6" />
                                        </a>
                                        <div
                                            class="uk-accordion-content studio-overview-papers">
                                            <button
                                                v-for="paper in group.papers"
                                                :key="'eop' + paper.value"
                                                type="button"
                                                :class="
                                                    'studio-overview-paper' +
                                                    (editorPicture.articleID ===
                                                    paper.value
                                                        ? ' active'
                                                        : '') +
                                                    (paper.tierHint
                                                        ? ' has-tiers'
                                                        : '') +
                                                    (paper.badge
                                                        ? ' has-badge badge-' +
                                                          paper.badge
                                                        : '')
                                                "
                                                v-on:click="
                                                    setPictureArticle(
                                                        editorKey,
                                                        paper.value
                                                    );
                                                    hideDropdown($event);
                                                ">
                                                <span>{{
                                                    getPaperName(paper.text)
                                                }}</span>
                                                <small>{{ paper.price }}</small>
                                                <span
                                                    v-if="paper.tierHint"
                                                    class="studio-overview-tiers">
                                                    {{ paper.tierHint }}
                                                </span>
                                                <span
                                                    v-if="paper.badge"
                                                    class="studio-overview-badge">
                                                    {{ $t('badge.' + paper.badge) }}
                                                </span>
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div
                            v-if="getPrintBorders(editorPicture.articleID).length > 0"
                            class="studio-editor-frame">
                            <button
                                type="button"
                                :class="
                                    'studio-chip' +
                                    (editorPicture.printBorderID ? '' : ' is-ghost')
                                ">
                                <Icon
                                    v-if="!editorPicture.printBorderID"
                                    icon="plus"
                                    :ratio="0.7" />
                                <span>{{
                                    getPrintBorderByKey(editorKey)
                                        ? getPrintBorderTitle(
                                              getPrintBorderByKey(editorKey)
                                          )
                                        : $t('studio.frame')
                                }}</span>
                                <Icon
                                    v-if="editorPicture.printBorderID"
                                    icon="chevron-down"
                                    :ratio="0.6" />
                            </button>
                            <div
                                class="studio-dropdown is-narrow is-upward"
                                uk-dropdown="mode: click; pos: top-left; offset: 6">
                                <button
                                    type="button"
                                    :class="
                                        'studio-overview-paper' +
                                        (editorPicture.printBorderID ? '' : ' active')
                                    "
                                    v-on:click="
                                        editorPicture.printBorderID = '';
                                        hideDropdown($event);
                                    ">
                                    <span>{{ $t('without.frame') }}</span>
                                </button>
                                <button
                                    v-for="printBorder in getPrintBorders(
                                        editorPicture.articleID
                                    )"
                                    :key="'eb' + printBorder.id"
                                    type="button"
                                    :class="
                                        'studio-overview-paper' +
                                        (editorPicture.printBorderID ===
                                        printBorder.id
                                            ? ' active'
                                            : '')
                                    "
                                    v-on:click="
                                        editorPicture.printBorderID = printBorder.id;
                                        hideDropdown($event);
                                    ">
                                    <span
                                        class="studio-swatch"
                                        :style="{background: printBorder.color}" />
                                    <span>{{
                                        getPrintBorderTitle(printBorder)
                                    }}</span>
                                </button>
                            </div>
                        </div>
                        <div class="studio-editor-amount">
                            <div class="studio-stepper">
                                <button
                                    type="button"
                                    :disabled="editorPicture.amount <= 1"
                                    :aria-label="$t('amount') + ' -1'"
                                    v-on:click="manipulateAmount(editorKey, -1)">
                                    <Icon icon="minus" :ratio="0.7" />
                                </button>
                                <input
                                    v-model.number="editorPicture.amount"
                                    type="number"
                                    min="1"
                                    :aria-label="$t('amount')"
                                    v-on:input="validateAmount(editorKey)" />
                                <button
                                    type="button"
                                    :aria-label="$t('amount') + ' +1'"
                                    v-on:click="manipulateAmount(editorKey, +1)">
                                    <Icon icon="plus" :ratio="0.7" />
                                </button>
                            </div>
                            <div class="studio-tile-price">
                                <div class="studio-strong">
                                    {{
                                        getNumberWithCurrency(
                                            getCostsByAmountAndArticleId(
                                                editorPicture
                                            )
                                        )
                                    }}
                                </div>
                                <small
                                    v-if="getNextTierHint(editorPicture)"
                                    class="studio-tier-hint">
                                    {{ getNextTierHint(editorPicture) }}
                                </small>
                            </div>
                        </div>
                        <div class="studio-editor-break" />
                        <div
                            v-if="isOrientationSwitchable(editorKey)"
                            class="studio-segmented">
                            <button
                                type="button"
                                :class="
                                    getPictureFormatName(editorKey) === 'vertical'
                                        ? 'active'
                                        : ''
                                "
                                v-on:click="setOrientation(editorKey, 'vertical')">
                                <Icon icon="phone" :ratio="0.8" />
                                {{ $t('vertical.format') }}
                            </button>
                            <button
                                type="button"
                                :class="
                                    getPictureFormatName(editorKey) === 'horizontal'
                                        ? 'active'
                                        : ''
                                "
                                v-on:click="setOrientation(editorKey, 'horizontal')">
                                <Icon icon="phone-landscape" :ratio="0.8" />
                                {{ $t('horizontal.format') }}
                            </button>
                        </div>
                        <button
                            type="button"
                            class="studio-soft-button"
                            v-on:click="resetCroppingByKey(editorKey)">
                            <Icon icon="refresh" :ratio="0.8" />
                            {{ $t('studio.crop.reset') }}
                        </button>
                        <span class="studio-editor-hint">
                            <span class="studio-if-pointer">
                                {{ $t('studio.crop.hint') }}
                            </span>
                            <span class="studio-if-touch">
                                {{ $t('studio.crop.hint.touch') }}
                            </span>
                        </span>
                        <span
                            v-if="
                                isVariableHeight(editorKey) &&
                                editorPicture.measurementValue
                            "
                            :class="
                                'studio-small' +
                                (isPictureTooLength(editorPicture)
                                    ? ' studio-danger'
                                    : ' studio-strong')
                            ">
                            {{ $t('pictureLength') }}
                            {{ formatMeasurement(editorPicture.measurementValue) }}
                            {{ $t(getInfos.measurementUnit) }}
                            <span v-if="isPictureTooLength(editorPicture)">
                                ({{ $t('max') }}
                                {{
                                    getArticleByID(editorPicture.articleID)
                                        .maxLength
                                }})
                            </span>
                        </span>
                        <div class="studio-editor-break" />
                        <Button
                            :action="closeEditor"
                            :text="$t('studio.done')"
                            has-action
                            is-round
                            mode="primary"
                            class="studio-editor-done" />
                    </div>
                </div>
            </div>
        </transition>

        <transition name="studio-editor" :duration="300">
            <div
                v-if="formatSheet"
                class="studio-editor"
                v-on:mousedown.self="closeFormatSheet()">
                <div class="studio-editor-panel is-auto studio-sheet">
                    <div class="studio-editor-bar">
                        <div class="studio-editor-title">
                            <div>{{ $t('format.and.paper') }}</div>
                            <div
                                v-if="formatSheet.key === null"
                                class="studio-editor-sub">
                                {{ $t('studio.all.pictures') }}
                            </div>
                        </div>
                        <button
                            type="button"
                            class="studio-round-button"
                            :aria-label="$t('close')"
                            v-on:click="closeFormatSheet()">
                            <Icon icon="close" />
                        </button>
                    </div>
                    <!-- like the web's list: every format a heading with its
                         papers, all open, scrolling inside the sheet -->
                    <div ref="formatSheetBody" class="studio-sheet-body">
                        <div
                            v-for="group in articleOverview"
                            :key="'sf' + group.format"
                            class="studio-sheet-group">
                            <div class="studio-sheet-group-title">
                                {{ group.format }}
                            </div>
                            <div class="studio-sheet-papers">
                                <button
                                    v-for="paper in group.papers"
                                    :key="'sp' + paper.value"
                                    type="button"
                                    :class="
                                        'studio-overview-paper studio-sheet-paper' +
                                        (formatSheet.articleID === paper.value
                                            ? ' active'
                                            : '') +
                                        (paper.badge
                                            ? ' has-badge badge-' + paper.badge
                                            : '')
                                    "
                                    v-on:click="
                                        pickFormatSheetPaper(paper.value)
                                    ">
                                    <span class="studio-sheet-paper-name">
                                        {{ getPaperName(paper.text) }}
                                    </span>
                                    <small>{{ paper.price }}</small>
                                    <span
                                        v-if="paper.tierHint"
                                        class="studio-overview-tiers">
                                        {{ paper.tierHint }}
                                    </span>
                                    <span
                                        v-if="paper.badge"
                                        class="studio-overview-badge">
                                        {{ $t('badge.' + paper.badge) }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- online on the terminal: the customer pays on the phone, Stripe's
             Checkout page behind the code; the order goes through once paid -->
        <transition name="studio-editor" :duration="300">
            <div v-if="checkoutUrl" class="studio-editor">
                <div class="studio-editor-panel is-auto studio-qr-pay">
                    <div class="studio-editor-bar">
                        <div class="studio-editor-title">
                            <div>{{ $t('studio.qr.pay.title') }}</div>
                            <div class="studio-editor-sub">{{ getTotal }}</div>
                        </div>
                    </div>
                    <div class="studio-qr-pay-body">
                        <template v-if="!checkoutExpired">
                            <div
                                ref="checkoutQrcode"
                                class="studio-qr-pay-code" />
                            <div class="studio-qr-pay-text">
                                {{ $t('studio.qr.pay.message') }}
                            </div>
                            <div class="studio-qr-pay-wait studio-muted">
                                <div uk-spinner="ratio: 0.8" />
                                {{ $t('studio.qr.pay.waiting') }}
                            </div>
                        </template>
                        <template v-else>
                            <!-- ProcessOrderBackups removed the expired order
                                 with its uploads, so only a new start helps -->
                            <InfoBox mode="danger">
                                {{ $t('studio.qr.pay.expired') }}
                            </InfoBox>
                            <Button
                                :action="reload"
                                :text="$t('studio.qr.pay.restart')"
                                has-action
                                is-round
                                mode="primary" />
                        </template>
                        <Button
                            v-if="!checkoutExpired"
                            :action="cancelCheckout"
                            :text="$t('cancel')"
                            :is-loading="loadingCheckoutCancel"
                            has-action
                            is-round
                            mode="default" />
                    </div>
                </div>
            </div>
        </transition>

        <div
            v-if="isDraggingFiles"
            class="studio-dropoverlay"
            v-on:dragover.prevent
            v-on:dragleave="isDraggingFiles = false"
            v-on:drop.prevent="dropListener">
            <div class="studio-dropoverlay-box">
                <Icon icon="cloud-upload" :ratio="3" />
                <div>{{ $t('studio.drop.release') }}</div>
            </div>
        </div>

        <transition name="studio-editor" :duration="300">
            <div
                v-if="isTerminalMode && isTerminalBrowserShown"
                class="studio-editor">
                <div class="studio-editor-panel studio-browser">
                    <div class="studio-editor-bar">
                        <div class="studio-segmented studio-browser-sources">
                            <button
                                v-for="tab in terminalSources"
                                :key="tab"
                                type="button"
                                :class="
                                    tab === selectedTerminalSource
                                        ? 'active'
                                        : ''
                                "
                                v-on:click="selectTerminalSource(tab)">
                                <Icon :icon="getIconByName(tab)" :ratio="0.9" />
                                <Translate :translation-key="tab" />
                            </button>
                        </div>
                        <button
                            type="button"
                            class="studio-round-button"
                            :aria-label="$t('close')"
                            v-on:click="cancelSelection()">
                            <Icon icon="close" />
                        </button>
                    </div>

                    <div
                        v-if="selectedTerminalSource !== 'smartphone'"
                        class="studio-browser-path">
                        <template v-for="(dir, key) in partedTerminalPaths">
                            <Icon
                                v-if="key > 0"
                                :key="'ds' + key"
                                icon="chevron-right"
                                :ratio="0.7"
                                class="studio-muted" />
                            <button
                                :key="'d' + key"
                                type="button"
                                :class="
                                    'studio-chip' +
                                    (key === partedTerminalPaths.length - 1
                                        ? ' is-strong'
                                        : '')
                                "
                                v-on:click="selectDir(key)">
                                <span>{{ getSlimDirName(dir) }}</span>
                            </button>
                        </template>
                    </div>

                    <div class="studio-browser-body">
                        <div
                            v-if="selectedTerminalSource === 'smartphone'"
                            class="studio-browser-state">
                            <div>
                                <div class="studio-smartphone-title">
                                    {{ $t('smartphone.qrcode') }}
                                </div>
                                <Translate
                                    translation-key="smartphone.qrcode.message" />
                            </div>
                            <div ref="qrcode" class="studio-smartphone-code" />
                        </div>
                        <div
                            v-else-if="terminalFilesError"
                            class="studio-browser-state">
                            <Icon
                                :icon="getIconByName(selectedTerminalSource)"
                                :ratio="3" />
                            <Translate
                                translation-key="medium.missing.message"
                                :parameter="{
                                    x: $t(selectedTerminalSource),
                                }" />
                        </div>
                        <div
                            v-else-if="terminalFilesLoading"
                            class="studio-browser-state">
                            <div uk-spinner="ratio: 2" />
                        </div>
                        <div
                            v-else-if="
                                terminalFiles.length === 0 &&
                                terminalFilesDir === ''
                            "
                            class="studio-browser-state">
                            {{ $t('studio.browser.empty') }}
                        </div>
                        <div v-else class="studio-browser-grid">
                            <button
                                v-if="terminalFilesDir !== ''"
                                type="button"
                                class="studio-browser-folder"
                                v-on:click="backTerminalFolder()">
                                <Icon icon="arrow-left" :ratio="2" />
                                <span>{{ $t('back') }}</span>
                            </button>

                            <template
                                v-for="(terminalFile, key) in terminalFiles">
                                <button
                                    v-if="terminalFile.isDir"
                                    :key="'tf' + key"
                                    type="button"
                                    class="studio-browser-folder"
                                    v-on:click="
                                        selectTerminalFolder(
                                            terminalFile.path,
                                            terminalFile.name
                                        )
                                    ">
                                    <Icon icon="folder" :ratio="2" />
                                    <span>{{ terminalFile.name }}</span>
                                </button>
                                <button
                                    v-else
                                    :key="'tf' + key"
                                    type="button"
                                    :class="
                                        'studio-browser-file' +
                                        (isFileSelected(key)
                                            ? ' is-selected'
                                            : '') +
                                        (terminalFile.loaded
                                            ? ''
                                            : ' is-loading')
                                    "
                                    v-on:click="toggleFileSelect(key)">
                                    <img
                                        loading="lazy"
                                        v-on:load="terminalFile.loaded = true"
                                        v-on:error="terminalFile.loaded = true"
                                        :src="
                                            apiUrlTerminal +
                                            '/thumbnails/' +
                                            terminalFile.device +
                                            terminalFile.path +
                                            '/' +
                                            terminalFile.name
                                        "
                                        :alt="terminalFile.name" />
                                    <span class="studio-browser-check">
                                        <Icon icon="check" :ratio="0.9" />
                                    </span>
                                </button>
                            </template>

                            <div
                                v-if="terminalFiles.length === 0"
                                class="studio-browser-note">
                                {{ $t('studio.browser.empty') }}
                            </div>
                        </div>
                    </div>

                    <!-- none on the QR tab; picks from a medium stay for when
                         it is back -->
                    <div
                        v-if="selectedTerminalSource !== 'smartphone'"
                        class="studio-browser-footer">
                        <!-- like the X: closes and drops the picks -->
                        <button
                            type="button"
                            class="studio-soft-button"
                            v-on:click="cancelSelection()">
                            {{ $t('cancel') }}
                        </button>
                        <!-- why the add button is grey -->
                        <span class="studio-browser-hint">
                            <template v-if="selectedTerminalFiles.length === 0">
                                {{ $t('studio.browser.pick.hint') }}
                            </template>
                        </span>
                        <!-- disabled ones say why in a tooltip on their wrapper -->
                        <span
                            class="studio-browser-why"
                            :uk-tooltip="
                                hasSelectedInFolder
                                    ? false
                                    : 'title: ' + $t('studio.browser.none.picked')
                            ">
                            <button
                                type="button"
                                class="studio-soft-button"
                                :disabled="!hasSelectedInFolder"
                                v-on:click="deselectAllInFolder()">
                                <Icon icon="close" :ratio="0.8" />
                                {{ $t('deselect.all') }}
                            </button>
                        </span>
                        <span
                            class="studio-browser-why"
                            :uk-tooltip="
                                hasUnselectedInFolder
                                    ? false
                                    : 'title: ' +
                                      $t(
                                          terminalFiles.some((file) => !file.isDir)
                                              ? 'studio.browser.all.picked'
                                              : 'studio.browser.empty'
                                      )
                            ">
                            <button
                                type="button"
                                class="studio-soft-button"
                                :disabled="!hasUnselectedInFolder"
                                v-on:click="selectAllInFolder()">
                                <Icon icon="check" :ratio="0.8" />
                                {{ $t('select.all') }}
                            </button>
                        </span>
                        <span
                            class="studio-browser-why"
                            :uk-tooltip="
                                selectedTerminalFiles.length > 0
                                    ? false
                                    : 'title: ' + $t('studio.browser.pick.hint')
                            ">
                            <Button
                                mode="primary"
                                has-action
                                is-round
                                is-large
                                :is-disabled="selectedTerminalFiles.length === 0"
                                :is-loading="loadingFetch"
                                :action="processSelectedPictures">
                                <span v-if="selectedTerminalFiles.length > 0">
                                    {{ selectedTerminalFiles.length }}
                                </span>
                                <Translate
                                    translation-key="x.add"
                                    :parameter="{
                                        x: $t(
                                            selectedTerminalFiles.length === 1
                                                ? 'picture'
                                                : 'pictures'
                                        ),
                                    }" />
                            </Button>
                        </span>
                    </div>
                </div>
            </div>
        </transition>

        <TerminalAdsScreensaver
            v-if="isTerminalWithAds && successOrder"
            is-studio
            :pictures="sortedPictures"
            @ended="reload"
            :terminal="getInfos.terminal" />

        <Modal
            is-round
            id="termsAndConditions"
            :title="$t('termsAndConditions')"
            is-extended>
            <div v-html="getInfos.termsAndConditions" />
        </Modal>
        <Modal is-round id="dataPrivacy" :title="$t('dataPrivacy')" is-extended>
            <div v-html="getInfos.dataPrivacy" />
        </Modal>
        <Modal is-round id="siteNotice" :title="$t('siteNotice')" is-extended>
            <div v-html="getInfos.siteNotice" />
        </Modal>

    </div>
</template>

<script>
import QRCode from 'easyqrcodejs';
import TextField from '@/components/generic/TextField';
import Card from '@/components/generic/Card';
import LabelSelect from '@/components/LabelSelect';
import Checkbox from '@/components/generic/Checkbox.vue';
import AddressFields from '@/components/AddressFields.vue';
import ToggleItem from '@/components/generic/ToggleItem';
import Button from '@/components/generic/Button';
import UIkit from 'uikit';
import Icon from '@/components/generic/Icon';
import InfoBox from '@/components/generic/InfoBox';
import axios from 'axios';
import {v4 as uuidv4} from 'uuid';
import ErrorMessage from '@/components/generic/ErrorMessage';
import {isMobileOnly} from 'mobile-device-detect';
import Vue from 'vue';
import Modal from '../components/generic/Modal.vue';
import dayjs from 'dayjs';
import PWAHintForm from '@/components/PWAHintForm.vue';
import PWAPromptForm from '@/components/PWAPromptForm.vue';
import Translate from '@/components/Translate.vue';
import Screensaver from '@/components/Screensaver.vue';
import TerminalConnnectorNotRunnning from '@/components/TerminalConnnectorNotRunnning.vue';
import TerminalAdsHeader from '@/components/TerminalAdsHeader.vue';
import TerminalAdsFooter from '@/components/TerminalAdsFooter.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import TerminalAdsScreensaver from '@/components/TerminalAdsScreensaver.vue';
import {StripeElementPayment} from '@vue-stripe/vue-stripe';
import SimpleKeyboard from '@/components/SimpleKeyboard.vue';

// a picture the browser cannot decode: retrying the same file does not help
const unreadableError = 'cannot load image';

export default {
    name: 'MainTerminal',
    components: {
        Card,
        LabelSelect,
        Checkbox,
        AddressFields,
        ToggleItem,
        LanguageSelector,
        StripeElementPayment,
        SimpleKeyboard,
        TerminalAdsScreensaver,
        TerminalAdsFooter,
        TerminalAdsHeader,
        TerminalConnnectorNotRunnning,
        Screensaver,
        Translate,
        PWAPromptForm,
        PWAHintForm,
        Modal,
        ErrorMessage,
        InfoBox,
        Icon,
        Button,
        TextField,
    },
    data() {
        return {
            paymentRendered: false,
            cardKeyboardShown: false,
            paymentType: '',
            // the focused field of the page the docked keyboard types into
            keyboardField: null,
            dockedKeyboardHeight: 0,
            cardKeys: Promise.resolve(),
            paymentCompleted: false,
            paymentPicked: false,
            paymentPickRequired: false,

            paymentKey: false,
            elementsOptions: {
                appearance: {
                    variables: {
                        colorPrimary: '#092633',
                        colorBackground: '#ffffff',
                        colorText: '#30313d',
                        colorDanger: '#df1b41',
                        borderRadius: '12px',
                    },
                },
            },
            // a list with radios: tabs put every method that doesn't fit into a
            // dropdown. Collapsed, so the card form doesn't push the others down
            paymentCreateOptions: {
                layout: {
                    type: 'accordion',
                    defaultCollapsed: true,
                    radios: true,
                    spacedAccordionItems: true,
                },
            },
            confirmParams: {
                return_url:
                    window.location.href + 'orders/' + this.requestToken + '/',
            },

            loadingInfos: true,

            intervalThumbnail: 0,
            intervalUpload: 0,

            isDeviceMobile: isMobileOnly,

            modalIdTerminalConnectorChecker: 'modal-terminal-connector-checker',
            modalIdTerminalAdsScreensaver: 'modal-terminal-ads-screensaver',

            terminalSources: null,
            selectedTerminalSource: null,
            terminalMediumSource: null,
            terminalFilesLoading: true,
            terminalFilesKey: '',
            terminalFiles: [],
            selectedTerminalFiles: [],
            terminalFilesDir: '',
            terminalFilesReplacing: false,
            terminalFilesReplacingInterval: 0,
            terminalFilesError: null,
            intervalPluginListener: 0,

            smartphoneLink: '',

            loadingFetch: false,
            terminalImportFailures: [],
            isTerminalImportDetached: false,

            errorOrder: null,
            loadingOrder: false,
            successOrder: false,

            disableLeaveProtection: false,

            pictures: [],
            picturesError: null,

            pictureTypeErrors: [],
            pictureSizeErrors: [],
            pictureLimitErrors: [],

            triggerContactFields: 0,
            contactFields: [
                {
                    value: '',
                    valid: false,

                    name: 'name',
                    label: 'name',
                    icon: 'user',
                    rules: 'required|max:320',
                },
                // optional on the terminal; no telephone there, ever
                {
                    value: '',
                    valid: true,
                    optional: true,

                    name: 'email',
                    label: 'emailAddress',
                    icon: 'mail',
                    rules: 'emailV2|max:320',
                },
            ],

            triggerAddressFields: 0,
            addressFields: [
                {
                    value: '',
                    valid: false,

                    name: 'street',
                    label: 'street',
                    icon: 'home',
                    rules: 'required|max:320',
                },
                {
                    value: '',
                    valid: false,

                    name: 'zip',
                    label: 'zip',
                    icon: 'location',
                    rules: 'required|numeric|max:320',
                },
                {
                    value: '',
                    valid: false,

                    name: 'city',
                    label: 'city',
                    icon: 'world',
                    rules: 'required|max:320',
                },
            ],

            country: '',

            storeId: '',
            paymentMethod: '',
            // the method shipping switched away from, until it is picked again
            paymentMethodDropped: '',
            deliveryType: '',
            requestToken: '',
            requestTokenHeaders: null,
            comments: '',

            paymentError: null,

            unifiedFormat: '',
            unifiedId: '',
            unifiedPaper: '',
            paymentRefreshTimeout: 0,
            // the Checkout page the QR code opens, '' = not shown
            checkoutUrl: '',
            checkoutExpired: false,
            checkoutPollTimeout: 0,
            loadingCheckoutCancel: false,
            overviewTransitions: 0,

            editorId: null,
            // terminal crop modal: the format whose papers the side shows,
            // null = the list of formats
            editorFormat: null,
            editorRevision: 0,
            newPictureId: null,
            replacePictureId: null,
            isDraggingFiles: false,
            isPricesShown: false,
            isCancelAskShown: false,
            // Abbrechen confirmed: the reload passes the leave protection
            isLeaveConfirmed: false,
            isTerminalBrowserShown: false,
            // terminal only: {key: picture index or null for all, articleID, format}
            formatSheet: null,

            // read by the global showTerminalHandler, which App.vue normally
            // provides together with its TerminalControlForm
            modalIdTerminalControl: 'modal-terminal-control',

            confirmTerms: false,
            triggerTerms: 0,
            termsValid: false,

            isApp: true,
            promptInstallerEvent: null,

            isTerminalConnectorModalShown: false,
        };
    },
    computed: {
        // what the docked keyboard types into: a field of the page, or
        // 'stripe'. Stripe's focus alone also comes from the method radios:
        // PayPal and the like have nothing to type
        keyboardTarget() {
            if (!this.isTerminalMode) {
                return null;
            }

            if (this.keyboardField) {
                return this.keyboardField;
            }

            if (
                this.cardKeyboardShown &&
                ['card', 'sepa_debit'].includes(this.paymentType)
            ) {
                return 'stripe';
            }

            return null;
        },
        isNumpad() {
            const field = this.keyboardField;

            return !!field && field.inputMode === 'numeric';
        },
        // the article every picture has, null when they differ
        sharedArticleID() {
            const ids = this.pictures.map((picture) => picture.articleID);

            return ids.length > 0 && ids.every((id) => id === ids[0])
                ? ids[0]
                : null;
        },
        articleOverview() {
            return this.getFormats.map((format) => ({
                format: format,
                papers: this.getPapersByFormat(format),
            }));
        },
        // a tile's check is a lookup, not a scan of the selection
        selectedTerminalFileIds() {
            return new Set(
                this.selectedTerminalFiles.map(
                    (file) => file.device + file.path + file.name
                )
            );
        },
        hasSelectedInFolder() {
            const selected = this.selectedTerminalFileIds;

            return this.terminalFiles.some((file) =>
                selected.has(file.device + file.path + file.name)
            );
        },
        // a photo here "Alle auswählen" would still add
        hasUnselectedInFolder() {
            const selected = this.selectedTerminalFileIds;

            return this.terminalFiles.some(
                (file) =>
                    !file.isDir &&
                    !selected.has(file.device + file.path + file.name)
            );
        },
        // jpeg and jpg are both accepted but name the same format
        uploadTypesText() {
            return this.getFormattedTypesString(
                this.fileExtensions
                    .filter(
                        (type) =>
                            type !== 'jpeg' ||
                            !this.fileExtensions.includes('jpg')
                    )
                    .map((type) => type.toUpperCase())
            );
        },
        editorKey() {
            if (!this.editorId) {
                return -1;
            }

            return this.pictures.findIndex(
                (picture) => picture.id === this.editorId
            );
        },
        editorPicture() {
            return this.editorKey >= 0 ? this.pictures[this.editorKey] : null;
        },
        editorFormatPapers() {
            const group = this.articleOverview.find(
                (group) => group.format === this.editorFormat
            );

            return group ? group.papers : [];
        },
        editorIndex() {
            return this.groupedPictures.findIndex(
                (picture) => picture.id === this.editorId
            );
        },
        // the tiles under one heading per article, the groups in the order
        // they first appear
        pictureGroups() {
            let groups = [];
            let byArticle = {};

            const pictures = this.sortedPictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                const articleID = pictures[i].articleID;

                if (!byArticle[articleID]) {
                    const article = this.getArticleByID(articleID);

                    byArticle[articleID] = {
                        articleID: articleID,
                        label:
                            this.getArticleFormat(article) +
                            ' · ' +
                            this.getPaperName(article.paper),
                        pictures: [],
                    };
                    groups.push(byArticle[articleID]);
                }

                byArticle[articleID].pictures.push(pictures[i]);
            }

            return groups;
        },
        // the pictures in the order they are shown, which the editor follows
        groupedPictures() {
            return this.pictureGroups.flatMap((group) => group.pictures);
        },
        // a reset or a new aspect ratio remounts the cropper, which then
        // starts from the stored coordinates again
        editorCropperKey() {
            return this.editorId + '-' + this.editorRevision;
        },
        // one line per article, so graduated prices read as they are charged
        summaryRows() {
            let rows = [];
            let byArticle = {};

            const pictures = this.sortedPictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                const articleID = pictures[i].articleID;

                if (!byArticle[articleID]) {
                    byArticle[articleID] = {
                        articleID: articleID,
                        amount: 0,
                        total: 0,
                        pictures: [],
                    };
                    rows.push(byArticle[articleID]);
                }

                if (byArticle[articleID].pictures.length < 3) {
                    byArticle[articleID].pictures.push(pictures[i]);
                }

                byArticle[articleID].amount += pictures[i].amount;
                byArticle[articleID].total += this.getCostsByAmountAndArticleId(
                    pictures[i]
                );
            }

            for (let i = 0, j = rows.length; i < j; i++) {
                const article = this.getArticleByID(rows[i].articleID);

                rows[i].label =
                    this.getArticleFormat(article) +
                    ' · ' +
                    this.getPaperName(article.paper);
                rows[i].unitPrice =
                    this.getPriceByArticleID(rows[i].articleID) +
                    this.getPerUnitSuffix(rows[i]);
                rows[i].hint = null;

                const next = this.nextTierByArticle[rows[i].articleID];
                if (next) {
                    rows[i].hint = this.$t('x.at.x.amount.left.x', {
                        price:
                            this.getNumberWithCurrency(next.price) +
                            this.getPerUnitSuffix(rows[i]),
                        amount: next.amount,
                        required: next.required,
                    });
                }
            }

            return rows;
        },
        // the next graduated price per article, counted over all its pictures
        // as the price is; the tiles and the summary rows show it
        nextTierByArticle() {
            let amounts = {};
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                const articleID = this.pictures[i].articleID;
                amounts[articleID] =
                    (amounts[articleID] || 0) + this.pictures[i].amount;
            }

            let next = {};
            for (const articleID in amounts) {
                const article = this.getArticleByID(articleID);
                if (!article || !article.graduatedPrices) {
                    continue;
                }

                const tier = this.getSortedGraduatedPrices(
                    article.graduatedPrices
                ).find((graduated) => graduated.amount > amounts[articleID]);
                if (tier) {
                    next[articleID] = {
                        amount: tier.amount,
                        price: tier.price,
                        required: tier.amount - amounts[articleID],
                    };
                }
            }

            return next;
        },
        getFeeDiscountText() {
            if (this.isFeeDiscount) {
                return this.$t('studio.fee.waived');
            }

            const feeDiscount = this.getFeeDiscountBySource();

            return (
                this.$t('studio.fee.free.from', {
                    x: this.getNumberWithCurrency(feeDiscount),
                }) +
                ' · ' +
                this.$t('studio.x.left', {
                    x: this.getNumberWithCurrency(
                        feeDiscount - this.getTotalSamplesPrice
                    ),
                })
            );
        },
        // what the online payment amount depends on
        orderSignature() {
            return JSON.stringify([
                this.deliveryType,
                this.pictures.map((picture) => [
                    picture.id,
                    picture.amount,
                    picture.articleID,
                    picture.printBorderID,
                    picture.measurementValue,
                    !!picture.coordinates,
                ]),
            ]);
        },
        isAddressShown() {
            return (
                this.isAddressRequired ||
                this.getInfos.mandatoryFields.includes('address')
            );
        },
        // the terminal has no delivery block (nothing is shipped), so the
        // address always shows under the payment method
        isAddressInPayment() {
            return this.isAddressShown;
        },
        partedTerminalPaths() {
            if (this.terminalFilesDir === '') {
                return [this.$t(this.selectedTerminalSource)];
            }

            let dirs = [];
            dirs.push(this.$t(this.selectedTerminalSource));

            let terminalDirs = this.terminalFilesDir;
            let splitted = terminalDirs.split('/');

            for (let i = 0, j = splitted.length; i < j; i++) {
                if (splitted[i] === '') {
                    continue;
                }

                dirs.push(splitted[i]);
            }

            return dirs;
        },
        sortedPictures() {
            let pictures = this.pictures;

            let parsed = [];
            for (let i = 0, j = pictures.length; i < j; i++) {
                pictures[i].originKey = i;
                parsed.push(pictures[i]);
            }

            return parsed.sort(this.picturestSortFunc);
        },
        isAddressRequired() {
            if (this.paymentMethod === 'invoice') {
                return true;
            }

            if (this.deliveryType === 'shipping') {
                return true;
            }

            return false;
        },
        getDeliveryTypeOptions() {
            let options = [];
            options.push('pickup');

            if (this.getInfos.isShippingAllowed) {
                options.push('shipping');
            }

            return options;
        },
        // the delivery type is picked first, so it decides the payment methods:
        // paying in the shop is impossible for something that gets shipped
        getPaymentMethodOptions() {
            const methods = this.getPaymentMethodsSource();

            if (this.deliveryType === 'shipping') {
                return methods.filter((method) => method !== 'shop');
            }

            return methods;
        },
        getPicturesWithErrorInfo() {
            let pictures = this.pictures;

            /*  let picturesWithoutError = [];
              for (let i = 0, j = pictures.length; i < j; i++) {
                  if (pictures[i].error === null) {
                      picturesWithoutError.push(pictures[i]);
                  }
              }*/

            return pictures;
        },
        getFormats() {
            let formats = [];

            let articles = this.getInfos.articles;

            if (!articles) {
                return formats;
            }

            articles = articles.sort(this.articleFormatSortFunc);

            for (let i = 0, j = articles.length; i < j; i++) {
                let format = this.getArticleFormat(articles[i]);
                if (!formats.includes(format)) {
                    formats.push(format);
                }
            }

            return formats;
        },
        getPapers() {
            let papers = [];

            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                if (!papers.includes(articles[i].paper)) {
                    papers.push(articles[i].paper);
                }
            }

            return papers;
        },
        getTotalSamplesCount() {
            let total = 0;

            let pictures = this.pictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                total += pictures[i].amount;
            }

            return total;
        },
        getTotalSamplesPrice() {
            let total = 0;

            let pictures = this.pictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                total += this.getCostsByAmountAndArticleId(pictures[i]);
            }

            return total;
        },
        getFee() {
            let total = this.getTotalSamplesPrice;
            let fee = this.getFeeBySource();
            let feeDiscount = this.getFeeDiscountBySource();

            if (feeDiscount != null && total >= feeDiscount) {
                fee = 0.0;
            }

            return fee;
        },
        getShippingCosts() {
            if (this.deliveryType !== 'shipping') {
                return 0.0;
            }

            return this.getShippingPrice();
        },
        isFeeDiscount() {
            let total = this.getTotalSamplesPrice;
            let feeDiscount = this.getFeeDiscountBySource();

            if (feeDiscount != null && total >= feeDiscount) {
                return true;
            }

            return false;
        },
        getTotal() {
            let total = this.getTotalSamplesPrice;
            let fee = this.getFee;
            let shipping = this.getShippingCosts;

            return this.getNumberWithCurrency(total + fee + shipping);
        },
        isPicturesValid() {
            if (this.successOrder || this.loadingOrder) {
                return false;
            }

            if (this.pictures.length === 0) {
                return false;
            }

            if (this.picturesError !== null) {
                return false;
            }

            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.isPictureTooLength(this.pictures[i])) {
                    return false;
                }
            }

            return true;
        },
        getUnifiedUploadPercentage() {
            let pictures = this.pictures;
            let total = 0;
            for (let i = 0, j = pictures.length; i < j; i++) {
                total += pictures[i].uploadPercentage;
            }

            let totalPercentage = total / pictures.length;
            return (Math.round(totalPercentage * 100) / 100).toFixed(0);
        },
        isUploadInProgress() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].uploadPercentage !== 100) {
                    return true;
                }
                if (!this.pictures[i].isDone) {
                    return true;
                }
            }

            return false;
        },
        // online on the terminal is paid by QR code on the phone, not in a
        // payment form on the kiosk
        isQRCodePayment() {
            return this.isTerminalMode && this.paymentMethod === 'online';
        },
        isPaymentInProgress() {
            if (this.paymentMethod !== 'online' || this.isQRCodePayment) {
                return false;
            }

            if (this.paymentKey) {
                return false;
            }

            return true;
        },
        isAnyUploadError() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].error !== null) {
                    return true;
                }
            }

            return false;
        },
        isContactDetailsValid() {
            let picturesValid = this.isPicturesValid;
            if (picturesValid === false) {
                return false;
            }

            let contactFields = this.contactFields;

            for (let i = 0, j = contactFields.length; i < j; i++) {
                if (!contactFields[i].valid) {
                    return false;
                }
            }

            return true;
        },
        isStoreRequired() {
            return (
                !this.isTerminalMode &&
                this.deliveryType === 'pickup' &&
                this.getInfos.stores &&
                this.getInfos.stores.length > 0
            );
        },
    },
    watch: {
        // every picture opens on the papers of its own format
        editorId() {
            this.editorFormat = this.editorPicture
                ? this.editorPicture.format
                : null;
        },
        // the field scrolled up to just above the docked keyboard
        keyboardTarget(target) {
            if (!target) {
                return;
            }

            this.$nextTick(() => {
                const keyboard = this.$refs.dockedKeyboard;
                const element =
                    target === 'stripe'
                        ? this.$refs.paymentRef && this.$refs.paymentRef.$el
                        : target;
                // a modal's field (the control centre's PIN) does not move
                // with the page
                if (!keyboard || !element || element.closest('.uk-modal')) {
                    return;
                }

                this.dockedKeyboardHeight = keyboard.offsetHeight;
                this.$nextTick(() => {
                    const rect = element.getBoundingClientRect();
                    // Stripe with the other methods below the open one can
                    // be higher than the room left: its top (where the card
                    // fields are) stays below the bar
                    const overlap = Math.min(
                        rect.bottom -
                            (window.innerHeight - this.dockedKeyboardHeight) +
                            16,
                        rect.top - this.$refs.nav.offsetHeight - 16
                    );
                    if (overlap > 0) {
                        window.scrollBy({top: overlap, behavior: 'smooth'});
                    }
                });
            });
        },
        // the order form sits on the same page as the pictures, so the payment
        // intent follows every change instead of being created once
        orderSignature() {
            clearTimeout(this.paymentRefreshTimeout);
            this.paymentRefreshTimeout = setTimeout(this.refreshPayment, 800);
        },
    },
    mounted() {
        let self = this;
        window.onunload = function () {
            if (!self.successOrder && !self.disableLeaveProtection) {
                self.deleteRequestToken();
            }
        };

        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            self.isApp = false;
            self.promptInstallerEvent = e;
        });

        const ua = window.navigator.userAgent;
        const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
        if (iOSSafari && !navigator.standalone) {
            this.isApp = false;
        }

        window.addEventListener('dragenter', this.dragenterListener, false);
        window.addEventListener('keydown', this.editorKeyListener, false);
        // the document: the control centre's modal lives outside this view
        document.addEventListener('focusin', this.onFieldFocus);
        document.addEventListener('focusout', this.onFieldBlur);
        // a field removed or hidden while focused sends no focusout
        document.addEventListener('pointerdown', this.onFieldBlur);
        document.body.appendChild(this.$refs.keyboardLayer);
        document.documentElement.classList.add('studio-page');
        document.documentElement.classList.remove('studio-boot');
    },
    beforeRouteLeave(to, from, next) {
        if (this.isStayInForm()) {
            next(false);
            window.location = to.path;
        } else {
            next();
        }
    },
    beforeDestroy() {
        clearTimeout(this.checkoutPollTimeout);
        window.removeEventListener('beforeunload', this.beforeWindowUnload);
        window.removeEventListener('dragenter', this.dragenterListener);
        window.removeEventListener('keydown', this.editorKeyListener);
        document.removeEventListener('focusin', this.onFieldFocus);
        document.removeEventListener('focusout', this.onFieldBlur);
        document.removeEventListener('pointerdown', this.onFieldBlur);
        // in <body>, so Vue's removal of the view would leave it behind
        this.$refs.keyboardLayer.remove();
        document.documentElement.classList.remove('studio-editor-open');
        document.documentElement.classList.remove('studio-page');
    },
    created() {
        window.addEventListener('beforeunload', this.beforeWindowUnload);

        if (this.isTerminalMode) {
            this.getTerminalSources();

            let self = this;
            setInterval(function () {
                if (self.isStart && self.isTerminalConnectorRunning !== null) {
                    if (self.isTerminalConnectorRunning) {
                        if (self.isTerminalConnectorModalShown) {
                            self.isTerminalConnectorModalShown = false;
                            UIkit.modal(
                                '#' + self.modalIdTerminalConnectorChecker
                            ).hide();
                        }

                        self.getTerminalSources();
                    } else {
                        if (!self.isTerminalConnectorModalShown) {
                            self.isTerminalConnectorModalShown = true;
                            UIkit.modal(
                                '#' + self.modalIdTerminalConnectorChecker
                            ).show();
                        }
                    }
                }
            }, 500);
        }

        if (this.isTerminalWithAds) {
            this.$store.state.start = true;
        }

        dayjs.locale(this.$i18n.locale);
        this.receiveInfosLocale();
        this.deliveryType = this.getDeliveryTypeOptions[0];
        this.paymentMethod = this.getPaymentMethodOptions[0];

        if (this.paymentMethod !== 'online' || this.isQRCodePayment) {
            this.paymentRendered = true;
        }

        this.postRequestToken();

        this.thumbnailQueue();
        this.uploadQueue();

        if (this.getInfos.articles) {
            let firstFormat = this.getFirstFormat();
            this.unifiedFormat = firstFormat;
            this.unifiedId = this.getFirstArticleIDByFormat(firstFormat);
            this.unifiedPaper = this.getArticleByID(this.unifiedId).paper;
        }
    },
    methods: {
        setPaymentRendered() {
            this.paymentRendered = true;
            this.cardKeyboardShown = false;
            this.paymentType = '';
            // a new element starts collapsed, also with the same client secret
            this.paymentPicked = false;
            this.paymentCompleted = false;
        },
        isTypedField(element) {
            return (
                !!element &&
                !element.disabled &&
                !element.readOnly &&
                (element.tagName === 'TEXTAREA' ||
                    (element.tagName === 'INPUT' &&
                        [
                            'text',
                            'email',
                            'tel',
                            'password',
                            'search',
                            'url',
                            // no 'number': the amounts have +/- and the crop
                            // editor's sits in a fixed overlay the numpad
                            // would cover
                        ].includes(element.type)))
            );
        },
        onFieldFocus(e) {
            if (this.isTypedField(e.target)) {
                this.keyboardField = e.target;
            }
        },
        // to another field its focusin comes first; to Stripe's iframe or
        // to nothing, the page's field is done
        onFieldBlur() {
            setTimeout(() => {
                const active = document.activeElement;
                if (!this.isTypedField(active) || active.offsetParent === null) {
                    this.keyboardField = null;
                }
            });
        },
        pressKey(button) {
            if (button === '{shift}' || button === '{lock}') {
                return;
            }

            if (this.keyboardField) {
                this.typeIntoField(this.keyboardField, button);
                return;
            }

            this.pressCardKey(button);
        },
        // the page's own fields take the key directly, as an input event
        // their v-model and validation listen to
        typeIntoField(field, button) {
            if (button === '{tab}') {
                const fields = Array.from(
                    document.querySelectorAll('input, textarea')
                ).filter(
                    (element) =>
                        this.isTypedField(element) &&
                        element.offsetParent !== null
                );
                const next = fields[fields.indexOf(field) + 1];
                if (next) {
                    next.focus();
                }
                return;
            }

            if (button === '{enter}' && field.tagName !== 'TEXTAREA') {
                field.dispatchEvent(
                    new KeyboardEvent('keyup', {key: 'Enter', bubbles: true})
                );
                return;
            }

            const value = field.value;
            // email inputs have no caret: typed at the end
            let start = field.selectionStart;
            let end = field.selectionEnd;
            if (start === null) {
                start = value.length;
                end = value.length;
            }

            let next;
            let caret;
            if (button === '{bksp}') {
                if (start === end && start > 0) {
                    start--;
                }
                next = value.slice(0, start) + value.slice(end);
                caret = start;
            } else {
                const text =
                    button === '{enter}'
                        ? '\n'
                        : button === '{space}'
                        ? ' '
                        : button.replace(/^\{numpad(\d)\}$/, '$1');
                next = value.slice(0, start) + text + value.slice(end);
                caret = start + text.length;
                if (field.maxLength > 0 && next.length > field.maxLength) {
                    return;
                }
            }

            field.value = next;
            if (field.selectionStart !== null) {
                field.setSelectionRange(caret, caret);
            }
            field.dispatchEvent(new Event('input', {bubbles: true}));
        },
        pressCardKey(button) {
            // Stripe's card fields are a cross-origin iframe; the terminal
            // connector types into them. One at a time, or keys can reorder
            const key = button === '{space}' ? ' ' : button;
            this.cardKeys = this.cardKeys.then(() =>
                axios
                    .post(this.apiUrlTerminal + '/keyboard', {key})
                    .catch(() => {}),
            );
        },
        onElementChange(event) {
            // collapsed, Stripe still reports complete for a first method
            // without fields (PayPal) and would pay with it
            this.paymentPicked = !event.collapsed;
            this.paymentCompleted = event.complete && this.paymentPicked;
            this.paymentType =
                this.paymentPicked && event.value ? event.value.type : '';

            if (this.paymentPicked) {
                this.paymentPickRequired = false;
            }
        },
        getPaymentMethodsSource() {
            if (this.isTerminalMode) {
                return this.getInfos.terminal.paymentMethods;
            }

            return this.getInfos.paymentMethods;
        },
        getFeeBySource() {
            if (this.isTerminalMode) {
                return this.getInfos.terminal.fee;
            }

            return this.getInfos.fee;
        },
        getFeeDiscountBySource() {
            if (this.isTerminalMode) {
                return this.getInfos.terminal.feeDiscount;
            }

            return this.getInfos.feeDiscount;
        },
        isStayInForm() {
            let sessionInvalid =
                localStorage.getItem('session_invalid') !== null;

            return (
                this.pictures.length > 0 &&
                !this.successOrder &&
                !sessionInvalid &&
                !this.disableLeaveProtection
            );
        },
        // the top bar's Abbrechen. With pictures it asks in the page first;
        // the browser's leave dialog is for closing and F5, not for this
        cancelOrder() {
            if (!this.isStayInForm()) {
                this.reload();
                return;
            }

            this.showCancelAsk(true);
        },
        showCancelAsk(shown) {
            this.isCancelAskShown = shown;
            document.documentElement.classList.toggle('studio-editor-open', shown);
        },
        // not disableLeaveProtection: with it onunload would keep the
        // request token
        confirmCancel() {
            this.isLeaveConfirmed = true;
            this.reload();
        },
        beforeWindowUnload(e) {
            if (this.isStayInForm() && !this.isLeaveConfirmed) {
                // Cancel the event
                e.preventDefault();
                // Chrome requires returnValue to be set
                e.returnValue = '';
            }
        },

        getSlimDirName(dir) {
            const limit = 20;
            if (dir.length <= limit) {
                return dir;
            }

            return dir.substring(0, limit - 3) + '...';
        },
        selectDir(key) {
            if (key === 0) {
                this.terminalFilesDir = '';
            } else {
                const dirs = this.partedTerminalPaths;

                let targetDir = '';
                for (let i = 1; i < key + 1; i++) {
                    if (i > 1) {
                        targetDir += '/';
                    }
                    targetDir += dirs[i];
                }
                this.terminalFilesDir = targetDir;
            }

            this.getTerminalFiles();
        },
        handleTokenExpiry() {
            localStorage.setItem('session_invalid', 'true');
            this.reload();
        },
        screensaverStart() {
            this.postRequestToken();
        },
        getTerminalSources() {
            if (this.terminalSources && this.terminalSources.length > 0) {
                return;
            }

            let http = axios.create();
            http.get(this.apiUrlTerminal + '/sources')
                .then((response) => {
                    this.terminalSources = response.data.sources
                        ? response.data.sources
                        : [];

                    // one smartphone source and one code: with ads and a
                    // QR link both added one, and the cards are keyed by name
                    const qrLink =
                        this.getInfos.terminal.QRCodeLink ||
                        (this.isTerminalWithAds ? window.location.origin : '');
                    if (qrLink) {
                        this.terminalSources.push('smartphone');
                        this.smartphoneLink = qrLink;
                    }

                    this.receiveInfosLocale(response.data.token);
                })
                .catch((e) => {
                    this.checkGenericError(e);
                });
        },
        replaceTerminalFiles(files) {
            this.terminalFilesReplacing = true;

            let self = this;
            this.terminalFilesReplacingInterval = setInterval(function () {
                for (let i = 0, j = self.terminalFiles.length; i < j; i++) {
                    if (self.terminalFiles[i].thumbnailing) {
                        return;
                    }
                }

                self.terminalFiles = files;
                self.terminalFilesReplacing = false;
                self.terminalFilesLoading = false;

                clearInterval(self.terminalFilesReplacingInterval);
            }, 100);
        },
        backTerminalFolder() {
            let parts = this.terminalFilesDir.split('/');

            parts.pop();
            this.terminalFilesDir = parts.join('/');

            this.getTerminalFiles();
        },
        selectTerminalFolder(terminalPath, terminalFolder) {
            let path = terminalPath + '/' + terminalFolder;
            this.terminalFilesDir = path.substring(1);
            this.getTerminalFiles();
        },
        toggleFileSelect(key) {
            if (this.isFileSelected(key)) {
                this.deselectFile(key);
                return;
            }

            this.selectFile(key);
        },
        selectFile(key) {
            const terminalFile = this.terminalFiles[key];
            this.selectedTerminalFiles.push(terminalFile);
        },
        deselectFile(key) {
            const selectedTerminalFiles = this.selectedTerminalFiles;
            const terminalFile = this.terminalFiles[key];

            const terminalFileId =
                terminalFile.device + terminalFile.path + terminalFile.name;

            for (let i = 0, j = selectedTerminalFiles.length; i < j; i++) {
                let selectedTerminalFileId =
                    selectedTerminalFiles[i].device +
                    selectedTerminalFiles[i].path +
                    selectedTerminalFiles[i].name;

                if (terminalFileId === selectedTerminalFileId) {
                    this.selectedTerminalFiles.splice(i, 1);
                    return;
                }
            }
        },
        processSelectedPictures() {
            this.loadingFetch = true;
            this.terminalImportFailures = [];
            this.isTerminalImportDetached = false;

            const selectedTerminalFiles = this.selectedTerminalFiles;
            for (let i = 0, j = selectedTerminalFiles.length; i < j; i++) {
                let terminalFile = selectedTerminalFiles[i];
                this.fetchBlob(
                    this.apiUrlTerminal +
                        '/files/' +
                        terminalFile.device +
                        terminalFile.path +
                        '/' +
                        terminalFile.name,
                    terminalFile.name,
                    selectedTerminalFiles.length - 1 === i
                );
            }

            this.selectedTerminalFiles = [];
        },
        selectAllInFolder() {
            const selected = this.selectedTerminalFileIds;

            this.selectedTerminalFiles.push(
                ...this.terminalFiles.filter(
                    (file) =>
                        !file.isDir &&
                        !selected.has(file.device + file.path + file.name)
                )
            );
        },
        // the counterpart of selectAllInFolder: picks elsewhere stay
        deselectAllInFolder() {
            const inFolder = new Set(
                this.terminalFiles.map(
                    (file) => file.device + file.path + file.name
                )
            );

            this.selectedTerminalFiles = this.selectedTerminalFiles.filter(
                (file) => !inFolder.has(file.device + file.path + file.name)
            );
        },
        cancelSelection() {
            // an import still running must not close the browser again
            // later, over the picks of a reopened one
            this.isTerminalImportDetached = true;
            this.selectedTerminalFiles = [];
            this.showTerminalBrowser(false);
        },
        isFileSelected(key) {
            const terminalFile = this.terminalFiles[key];

            return this.selectedTerminalFileIds.has(
                terminalFile.device + terminalFile.path + terminalFile.name
            );
        },
        selectTerminalSource(terminalSource) {
            if (this.selectedTerminalSource !== 'smartphone') {
                this.terminalMediumSource = this.selectedTerminalSource;
            }
            this.selectedTerminalSource = terminalSource;

            // a tab of the browser like the media; its ref is new each time
            if (terminalSource === 'smartphone') {
                clearInterval(this.intervalPluginListener);
                this.intervalPluginListener = 0;
                this.showTerminalBrowser(true);
                this.$nextTick(() => {
                    const qrcode = this.$refs.qrcode;
                    // the tab tapped again: the code is there already
                    if (qrcode && !qrcode.hasChildNodes()) {
                        new QRCode(qrcode, {text: this.smartphoneLink});
                    }
                });
                return;
            }

            this.terminalFilesDir = '';
            this.getTerminalFiles();
            this.showTerminalBrowser(true);
        },
        showTerminalBrowser(shown) {
            this.isTerminalBrowserShown = shown;

            if (shown) {
                document.documentElement.classList.add('studio-editor-open');
                return;
            }

            // a missing medium is polled only while the browser shows it
            clearInterval(this.intervalPluginListener);
            this.intervalPluginListener = 0;

            // the crop view and the sheet keep the scroll lock they set
            if (!this.editorPicture && !this.formatSheet) {
                document.documentElement.classList.remove('studio-editor-open');
            }
        },
        // the terminal has no file dialog: the last medium, else the first
        addMorePictures() {
            if (!this.isTerminalMode) {
                this.triggerInputDialog();
                return;
            }

            const sources = (this.terminalSources || []).filter(
                (source) => source !== 'smartphone'
            );
            // closed on the QR tab: the medium before it, its folder kept
            if (
                this.selectedTerminalSource === 'smartphone' &&
                sources.includes(this.terminalMediumSource)
            ) {
                this.selectedTerminalSource = this.terminalMediumSource;
            }
            // back in the folder of the last round; a medium swapped meanwhile
            // fails to list it and getTerminalFiles falls back to its root
            if (sources.includes(this.selectedTerminalSource)) {
                this.getTerminalFiles();
                this.showTerminalBrowser(true);
                return;
            }

            this.selectTerminalSource(sources[0] || 'smartphone');
        },
        getTerminalFiles() {
            // a medium's poll or answer still running when the QR tab opened
            if (this.selectedTerminalSource === 'smartphone') {
                clearInterval(this.intervalPluginListener);
                this.intervalPluginListener = 0;
                return;
            }

            // the same folder again (the browser reopened) keeps its list
            // and thumbnails on screen while it is listed anew
            const filesKey =
                this.selectedTerminalSource + '/' + this.terminalFilesDir;
            this.terminalFilesLoading = filesKey !== this.terminalFilesKey;

            let http = axios.create();
            http.get(
                this.apiUrlTerminal +
                    '/sources/' +
                    this.selectedTerminalSource +
                    '/' +
                    this.terminalFilesDir
            )
                .then((response) => {
                    clearInterval(this.intervalPluginListener);
                    this.intervalPluginListener = 0;
                    this.terminalFilesError = null;

                    let terminalFiles = response.data;

                    if (!terminalFiles) {
                        terminalFiles = [];
                    }

                    // a file listed again keeps its <img> and src, which
                    // fires no second load: it keeps its loaded state
                    const loadedIds = new Set(
                        this.terminalFiles
                            .filter((file) => file.loaded)
                            .map((file) => file.device + file.path + file.name)
                    );

                    for (let i = 0, j = terminalFiles.length; i < j; i++) {
                        terminalFiles[i].file = null;
                        terminalFiles[i].thumbnail = null;
                        terminalFiles[i].thumbnailUrl = null;
                        terminalFiles[i].thumbnailing = false;
                        // the browser tile shimmers until its thumbnail is in
                        terminalFiles[i].loaded = loadedIds.has(
                            terminalFiles[i].device +
                                terminalFiles[i].path +
                                terminalFiles[i].name
                        );
                    }

                    this.terminalFilesKey = filesKey;
                    this.replaceTerminalFiles(terminalFiles);
                })
                .catch((e) => {
                    this.terminalFiles = [];
                    this.terminalFilesKey = '';
                    this.terminalFilesLoading = false;
                    this.terminalFilesError = e;
                    this.terminalFilesDir = '';

                    if (
                        this.intervalPluginListener === 0 &&
                        this.isTerminalBrowserShown
                    ) {
                        this.initPluginListener();
                    }
                });
        },
        initPluginListener() {
            this.intervalPluginListener = setInterval(() => {
                this.getTerminalFiles();
            }, 500);
        },
        fetchBlob(url, name, last) {
            let self = this;
            axios({
                method: 'get',
                url: url,
                responseType: 'blob',
            }).then(function (response) {
                let file = new File([response.data], name);
                self.processFilesUpload([file]);

                if (last) {
                    self.loadingFetch = false;

                    if (!self.isTerminalImportDetached) {
                        self.selectedTerminalFiles.splice(0);
                        self.showTerminalBrowser(false);
                    }
                }
            }).catch(function (e) {
                self.checkGenericError(e);

                // the selection is gone already: say which photos are
                // missing, in one notification, not one per photo
                self.terminalImportFailures.push(name);
                UIkit.notification.closeAll('terminal-import', true);
                UIkit.notification({
                    message:
                        self.$t('studio.upload.failed') +
                        ': ' +
                        self.terminalImportFailures.join(', '),
                    status: 'danger',
                    pos: 'top-center',
                    timeout: 0,
                    group: 'terminal-import',
                });

                if (last) {
                    self.loadingFetch = false;
                }
            });
        },
        rotateCropping(key) {
            Vue.set(this.pictures[key], 'rotate', !this.pictures[key].rotate);
            this.resetCroppingByKey(key);
        },
        // the largest centered crop in the article's aspect ratio, which is
        // what the cropper itself starts with. Coordinates are in thumbnail
        // pixels, like the ones the cropper reports
        getDefaultCoordinates(key) {
            const picture = this.pictures[key];
            const imageWidth = picture.thumbnailWidth;
            const imageHeight = picture.thumbnailHeight;
            const aspectRatio = this.getCropperRatioByPictureKey(key);

            let width = imageWidth;
            let height = imageHeight;
            if (aspectRatio) {
                if (imageWidth / imageHeight > aspectRatio) {
                    width = imageHeight * aspectRatio;
                } else {
                    height = imageWidth / aspectRatio;
                }
            }

            width = Math.round(width);
            height = Math.round(height);

            return {
                width: width,
                height: height,
                left: Math.round((imageWidth - width) / 2),
                top: Math.round((imageHeight - height) / 2),
            };
        },
        resetCroppingByKey(key) {
            if (!this.pictures[key].thumbnailWidth) {
                return;
            }

            this.setPictureCoordinates(key, this.getDefaultCoordinates(key));
            this.editorRevision++;
        },
        setPictureCoordinates(key, coordinates) {
            Vue.set(this.pictures[key], 'coordinates', coordinates);
            this.getAndSetMeasurement(key);
            this.renderCroppedThumbnail(this.pictures[key]);
        },
        // only the ads screensaver still shows cropped thumbnails as images,
        // everywhere else the crop is drawn by getCropFrameStyle
        renderCroppedThumbnail(picture) {
            if (!this.isTerminalWithAds) {
                return;
            }

            const coordinates = picture.coordinates;
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = coordinates.width;
                canvas.height = coordinates.height;
                canvas
                    .getContext('2d')
                    .drawImage(
                        img,
                        coordinates.left,
                        coordinates.top,
                        coordinates.width,
                        coordinates.height,
                        0,
                        0,
                        coordinates.width,
                        coordinates.height
                    );
                canvas.toBlob(
                    (blob) => {
                        if (picture.coordinates === coordinates) {
                            Vue.set(
                                picture,
                                'thumbnailCroppedUrl',
                                this.getLocalFileUrl(blob)
                            );
                        }
                    },
                    picture.file.type,
                    1.0
                );
            };
            img.src = picture.thumbnailUrl;
        },
        // draws the crop with CSS, so the grid needs no cropper per picture:
        // the whole thumbnail, fitted into a stage of the given aspect ratio
        // (the crop-only tile of 2026-10-25 was taken back on 2026-10-27)
        getCropFrameStyle(item, stageRatio) {
            const ratio = item.thumbnailWidth / item.thumbnailHeight;
            const size = 88;

            let width = size;
            let height = size;
            if (ratio > stageRatio) {
                height = (size * stageRatio) / ratio;
            } else {
                width = (size * ratio) / stageRatio;
            }

            return {
                width: width + '%',
                height: height + '%',
                backgroundImage: 'url(' + item.thumbnailUrl + ')',
            };
        },
        // the crop on top of it: its part of the picture again, over the
        // frame's dark, struck-through layer like the crop modal's
        getCropBoxStyle(item) {
            const coordinates = item.coordinates;
            const restX = item.thumbnailWidth - coordinates.width;
            const restY = item.thumbnailHeight - coordinates.height;

            return {
                left: (coordinates.left / item.thumbnailWidth) * 100 + '%',
                top: (coordinates.top / item.thumbnailHeight) * 100 + '%',
                width: (coordinates.width / item.thumbnailWidth) * 100 + '%',
                height: (coordinates.height / item.thumbnailHeight) * 100 + '%',
                backgroundImage: 'url(' + item.thumbnailUrl + ')',
                backgroundSize:
                    (item.thumbnailWidth / coordinates.width) * 100 +
                    '% ' +
                    (item.thumbnailHeight / coordinates.height) * 100 +
                    '%',
                backgroundPosition:
                    (restX > 0 ? (coordinates.left / restX) * 100 : 0) +
                    '% ' +
                    (restY > 0 ? (coordinates.top / restY) * 100 : 0) +
                    '%',
                ...this.getPrintBorderVars(item.originKey),
            };
        },
        getPerUnitSuffix(item) {
            if (!this.getArticleByID(item.articleID).isHeightVariable) {
                return '';
            }

            return (
                ' ' +
                this.$t('per.x', {x: this.$t(this.getInfos.measurementUnit)})
            );
        },
        isLowResolution(item) {
            return (
                this.getPictureWidthLowDiff(item) > 0 ||
                this.getPictureHeightLowDiff(item) > 0
            );
        },
        isUploadTypeError(item) {
            return (
                item.error &&
                item.error.response &&
                item.error.response.status === 422
            );
        },
        isPictureUnreadable(item) {
            return item.error === unreadableError;
        },
        getAbsenceStart(date) {
            return dayjs(date).subtract(3, 'day');
        },
        setPictureFormat(key, format) {
            const picture = this.pictures[key];
            if (picture.format === format) {
                return;
            }

            picture.format = format;
            picture.articleID = this.getFirstArticleIDByFormat(
                format,
                picture.paper
            );
            picture.paper = this.getArticleByID(picture.articleID).paper;
            picture.printBorderID = '';

            this.resetCroppingByKey(key);
        },
        setPicturePaper(key, paper) {
            const picture = this.pictures[key];
            if (picture.articleID === paper.value) {
                return;
            }

            const ratio = this.getCropperRatioByPictureKey(key);

            picture.articleID = paper.value;
            picture.paper = paper.text;
            picture.printBorderID = '';

            if (ratio !== this.getCropperRatioByPictureKey(key)) {
                this.resetCroppingByKey(key);
                return;
            }

            this.getAndSetMeasurement(key);
        },
        isOrientationSwitchable(key) {
            const article = this.getArticleByID(this.pictures[key].articleID);

            return (
                !article.isHeightVariable && article.width !== article.height
            );
        },
        setOrientation(key, orientation) {
            if (this.getPictureFormatName(key) === orientation) {
                return;
            }

            this.rotateCropping(key);
        },
        openEditor(item) {
            if (item.error || !item.thumbnailUrl) {
                return;
            }

            this.editorId = item.id;
            document.documentElement.classList.add('studio-editor-open');
        },
        closeEditor() {
            this.commitEditorCrop();
            this.editorId = null;
            document.documentElement.classList.remove('studio-editor-open');
        },
        showPrices(shown) {
            this.isPricesShown = shown;
            document.documentElement.classList.toggle('studio-editor-open', shown);
        },
        // the terminal picks format and paper in a sheet instead of the
        // dropdown, on the format of the picked article
        openFormatSheet(key) {
            if (!this.isTerminalMode) {
                return;
            }

            const articleID =
                key === null
                    ? this.sharedArticleID
                    : this.pictures[key].articleID;

            this.formatSheet = {
                key: key,
                articleID: articleID,
            };
            document.documentElement.classList.add('studio-editor-open');

            // the list opens at the group of the picked article
            this.$nextTick(() => {
                const body = this.$refs.formatSheetBody;
                const group =
                    body &&
                    body.querySelectorAll('.studio-sheet-group')[
                        this.getOverviewGroupIndex(articleID)
                    ];
                if (group) {
                    body.scrollTop =
                        group.getBoundingClientRect().top -
                        body.getBoundingClientRect().top;
                }
            });
        },
        // the overview group with the article, else the first
        getOverviewGroupIndex(articleID) {
            return Math.max(
                0,
                this.articleOverview.findIndex((group) =>
                    group.papers.some((paper) => paper.value === articleID)
                )
            );
        },
        closeFormatSheet() {
            this.formatSheet = null;

            if (!this.editorPicture) {
                document.documentElement.classList.remove('studio-editor-open');
            }
        },
        pickFormatSheetPaper(articleID) {
            if (this.formatSheet.key === null) {
                this.setUnifiedArticle(articleID);
                this.applyBulk();
            } else {
                this.setPictureArticle(this.formatSheet.key, articleID);
            }

            this.closeFormatSheet();
        },
        editorGo(step) {
            const pictures = this.groupedPictures;

            for (
                let i = this.editorIndex + step;
                i >= 0 && i < pictures.length;
                i += step
            ) {
                if (pictures[i].thumbnailUrl && !pictures[i].error) {
                    this.editorGoTo(pictures[i]);
                    return;
                }
            }
        },
        editorGoTo(item) {
            if (item.id === this.editorId || item.error || !item.thumbnailUrl) {
                return;
            }

            this.commitEditorCrop();
            this.editorId = item.id;
        },
        deleteEditorPicture() {
            const neighbor =
                this.groupedPictures[this.editorIndex + 1] ||
                this.groupedPictures[this.editorIndex - 1];

            this.deletePicture(this.editorKey);

            if (neighbor) {
                this.editorId = neighbor.id;
                return;
            }

            this.closeEditor();
        },
        // the cropper reports changes debounced, so a crop adjusted right
        // before switching pictures is read directly
        commitEditorCrop() {
            const cropper = this.$refs.editorCropper;
            if (!cropper || this.editorKey < 0) {
                return;
            }

            const {coordinates} = cropper.getResult();
            this.storeEditorCoordinates(coordinates);
        },
        onEditorCropperChange({coordinates, canvas}) {
            if (!this.storeEditorCoordinates(coordinates)) {
                return;
            }

            if (!this.isTerminalWithAds || !canvas) {
                return;
            }

            const picture = this.editorPicture;
            canvas.toBlob(
                (blob) => {
                    Vue.set(
                        picture,
                        'thumbnailCroppedUrl',
                        this.getLocalFileUrl(blob)
                    );
                },
                picture.file.type,
                1.0
            );
        },
        storeEditorCoordinates(coordinates) {
            const key = this.editorKey;

            if (
                key < 0 ||
                !coordinates ||
                (coordinates.height === 0 &&
                    coordinates.width === 0 &&
                    coordinates.left === 0 &&
                    coordinates.top === 0)
            ) {
                return false;
            }

            if (
                this.areCoordinatesEqual(
                    this.pictures[key].coordinates,
                    coordinates
                )
            ) {
                return false;
            }

            Vue.set(this.pictures[key], 'coordinates', coordinates);
            this.getAndSetMeasurement(key);

            return true;
        },
        editorDefaultSize() {
            const coordinates = this.editorPicture.coordinates;

            return {width: coordinates.width, height: coordinates.height};
        },
        editorDefaultPosition() {
            const coordinates = this.editorPicture.coordinates;

            return {left: coordinates.left, top: coordinates.top};
        },
        editorKeyListener(e) {
            // the sheet lies over the crop view: nothing reaches the view
            if (this.formatSheet) {
                if (e.key === 'Escape') {
                    this.closeFormatSheet();
                }
                return;
            }

            if (this.isPricesShown && e.key === 'Escape') {
                this.showPrices(false);
                return;
            }

            if (this.isCancelAskShown && e.key === 'Escape') {
                this.showCancelAsk(false);
                return;
            }

            // like its X
            if (this.isTerminalBrowserShown && e.key === 'Escape') {
                this.cancelSelection();
                return;
            }
            if (!this.editorPicture) {
                return;
            }

            if (e.key === 'Escape') {
                this.closeEditor();
                return;
            }

            const tag = e.target && e.target.tagName;
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) {
                return;
            }

            if (e.key === 'ArrowLeft') {
                this.editorGo(-1);
            }

            if (e.key === 'ArrowRight') {
                this.editorGo(1);
            }
        },
        goToCheckout() {
            if (!this.isPicturesValid) {
                const invalid = this.groupedPictures.find((picture) =>
                    this.isPictureTooLength(picture)
                );
                if (invalid) {
                    this.scrollToSection('picture-' + invalid.id);
                }
                return;
            }

            // the watcher keeps an existing payment current; refreshing it here
            // would remount Stripe and shift the page while it scrolls
            if (!this.paymentKey) {
                this.refreshPayment();
            }
            this.scrollToSection('studio-checkout');
        },
        refreshPayment() {
            if (
                this.paymentMethod !== 'online' ||
                this.pictures.length === 0 ||
                this.pictures.some(
                    (picture) => !picture.coordinates && !picture.error
                )
            ) {
                return Promise.resolve();
            }

            return this.postOrderRequestPayment();
        },
        applyBulk() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                const picture = this.pictures[i];

                if (
                    picture.format === this.unifiedFormat &&
                    picture.articleID === this.unifiedId
                ) {
                    continue;
                }

                picture.format = this.unifiedFormat;
                picture.articleID = this.unifiedId;
                picture.paper = this.unifiedPaper;
                picture.printBorderID = '';

                this.resetCroppingByKey(i);
            }

            UIkit.notification({
                message: this.$t('all.pictures.changed'),
                status: 'info',
                pos: 'top-center',
                timeout: this.notificationTimeout,
            });
        },
        // format and paper in one pick from the overview
        setPictureArticle(key, articleID) {
            const format = this.getArticleFormat(this.getArticleByID(articleID));

            this.setPictureFormat(key, format);
            this.setPicturePaperById(key, articleID);
        },
        setUnifiedArticle(articleID) {
            const article = this.getArticleByID(articleID);

            this.unifiedId = articleID;
            this.unifiedFormat = this.getArticleFormat(article);
            this.unifiedPaper = article.paper;
        },
        hideDropdown(event) {
            const dropdown = event.target.closest('.uk-dropdown');
            if (dropdown) {
                UIkit.dropdown(dropdown).hide(false);
            }
        },
        // one group open at a time: on every open the one with the picked
        // article, else the first
        // a tile or heading that goes leaves the grid's flow at once, where it
        // stands, so the others slide into its place while it fades
        fixLeavingTile(el) {
            el.style.left = el.offsetLeft + 'px';
            el.style.top = el.offsetTop + 'px';
            el.style.width = el.offsetWidth + 'px';
            el.style.height = el.offsetHeight + 'px';
            el.style.position = 'absolute';
        },
        openOverviewGroup(event, articleID) {
            const accordion = UIkit.accordion(
                event.target.querySelector('.studio-overview')
            );
            const i = this.getOverviewGroupIndex(articleID);

            if (
                accordion.items[i] &&
                !accordion.items[i].classList.contains('uk-open')
            ) {
                accordion.toggle(i, false);
            }
        },
        // while a group opens or closes, from the first before* to the last
        // shown/hidden: drop.position() hides the drop for a moment, which
        // cancels the accordion's height transition, and UIkit calls it on
        // any visualViewport event (a scrollbar coming or going). The drop
        // skips that while it has its enter class, so it gets that class; an
        // upward drop (the editor's) gets its bottom kept on the chip, as
        // UIkit does not see the drop itself grow
        followDropdown(event) {
            const el = event.currentTarget.closest('.uk-dropdown');
            const drop = UIkit.dropdown(el);
            const done = event.type === 'shown' || event.type === 'hidden';

            // the group openOverviewGroup opens while the drop shows is not
            // followed: the drop is not placed yet
            if (!done && !drop.isToggled()) {
                return;
            }

            this.overviewTransitions = Math.max(
                0,
                this.overviewTransitions + (done ? -1 : 1)
            );
            if (done || this.overviewTransitions > 1) {
                return;
            }

            const bottom = el.getBoundingClientRect().bottom;
            const isUpwards =
                bottom <= drop.targetEl.getBoundingClientRect().top + 1;

            el.classList.add(drop.clsEnter);

            // a cancelled transition may never send its shown/hidden; a drop
            // closed meanwhile must lose the class at once, with it UIkit
            // takes it for open and the next chip click would hide it
            const until = Date.now() + 1000;
            const step = () => {
                const isOpen = el.classList.contains(drop.cls);
                if (isUpwards && isOpen) {
                    const shift = el.getBoundingClientRect().bottom - bottom;
                    el.style.top = parseFloat(el.style.top) - shift + 'px';
                }
                if (
                    isOpen &&
                    this.overviewTransitions > 0 &&
                    Date.now() < until
                ) {
                    requestAnimationFrame(step);
                    return;
                }
                this.overviewTransitions = 0;
                el.classList.remove(drop.clsEnter);
                drop.$emit();
            };
            requestAnimationFrame(step);
        },
        setPicturePaperById(key, articleID) {
            const paper = this.getPapersByFormat(
                this.pictures[key].format
            ).find((option) => option.value === articleID);
            if (paper) {
                this.setPicturePaper(key, paper);
            }
        },
        scrollToTop() {
            this.$nextTick(() => {
                this.scrollToElement(this.$refs.top);
            });
        },
        scrollToSection(id) {
            this.scrollToElement(document.getElementById(id));
        },
        // UIkit's eased scroll: the browser's smooth scroll jumps when the
        // system has animations turned off. It leaves out the sticky top bar
        // by itself, the offset is only the gap below it
        scrollToElement(element) {
            if (!element) {
                return Promise.resolve();
            }

            // an open UIkit tooltip moves along on every scroll event and puts
            // back the scroll position it saw, which undid the animation (down,
            // then back up). Close the open ones (the button just pressed) and
            // let nothing under the pointer open new ones while the page moves
            document
                .querySelectorAll('[uk-tooltip][aria-describedby]')
                .forEach((el) => UIkit.tooltip(el).hide());

            const root = document.documentElement;
            const done = () => root.classList.remove('studio-scrolling');
            root.classList.add('studio-scrolling');
            return UIkit.util
                .scrollIntoView(element, {offset: 16})
                .then(done, done);
        },
        getShippingPrice() {
            let samples = this.getTotalSamplesCount;

            let shippingGraduatedPrices = this.getSortedGraduatedPrices(
                this.getInfos.shippingGraduatedPrices,
                true
            );
            for (let i = 0, j = shippingGraduatedPrices.length; i < j; i++) {
                if (samples >= shippingGraduatedPrices[i].amount) {
                    return shippingGraduatedPrices[i].price;
                }
            }

            return 0.0;
        },
        receiveInfosLocale(token) {
            this.loadingInfos = true;

            const clickAndBuyID = this.$route.params.clickandbuy;
            let withclickandbuys = '';
            if (clickAndBuyID) {
                withclickandbuys = '?withclickandbuys=' + clickAndBuyID;
            }

            let http = axios.create();
            http.get(this.apiUrl + '/infos' + withclickandbuys, {
                headers: {
                    token: token,
                    'Content-Language': Vue.localStorage.get('language'),
                },
            })
                .then((response) => {
                    let data = response.data;
                    this.$store.state.infos = data;

                    this.country = data.shippingCountries[0];
                    if (data.shippingCountries.includes(data.country)) {
                        this.country = data.country;
                    }

                    this.loadingInfos = false;
                })
                .catch((e) => {
                    this.checkGenericError(e);
                    this.loadingInfos = false;
                });
        },
        setPaymentMethod(method) {
            if (this.successOrder || this.loadingOrder) {
                return;
            }

            this.paymentMethod = method;
            this.paymentMethodDropped = '';

            if (method === 'invoice') {
                this.country = this.getInfos.shippingCountries[0];
                if (
                    this.getInfos.shippingCountries.includes(
                        this.getInfos.country
                    )
                ) {
                    this.country = this.getInfos.country;
                }
            }

            if (method === 'online' && !this.isQRCodePayment) {
                this.postOrderRequestPayment();
            } else {
                this.paymentRendered = true;
            }
        },
        setDeliveryTypes(method) {
            if (this.successOrder || this.loadingOrder) {
                return;
            }

            const previous = this.paymentMethod;
            const dropped = this.paymentMethodDropped;

            this.deliveryType = method;

            // shipping drops "shop" as a payment method, so move off it
            if (!this.getPaymentMethodOptions.includes(this.paymentMethod)) {
                this.setPaymentMethod(this.getPaymentMethodOptions[0]);
                this.paymentMethodDropped = previous;
                return;
            }

            // and back on pickup the customer gets it again, instead of
            // keeping e.g. "invoice" with its address fields they never chose
            if (dropped && this.getPaymentMethodOptions.includes(dropped)) {
                this.setPaymentMethod(dropped);
                return;
            }

            if (this.paymentMethod === 'online') {
                this.postOrderRequestPayment();
            }
        },
        triggerInputDialog() {
            document.getElementById('pictures').click();
        },
        triggerReplaceDialog(key) {
            this.replacePictureId = this.pictures[key].id;
            this.$refs.replacePicture.value = null;
            this.$refs.replacePicture.click();
        },
        // the drop overlay covers the whole window, so files can be dropped
        // anywhere while the pictures are shown
        dragenterListener(e) {
            if (
                this.isTerminalMode ||
                this.successOrder ||
                this.editorPicture ||
                !e.dataTransfer ||
                !Array.from(e.dataTransfer.types).includes('Files')
            ) {
                return;
            }

            this.isDraggingFiles = true;
        },
        dropListener(e) {
            this.isDraggingFiles = false;
            this.processFilesUpload(e.dataTransfer.files);
        },
        areCoordinatesEqual(oldCoordinates, newCoordinates) {
            if (!oldCoordinates) {
                return false;
            }

            if (oldCoordinates.height !== newCoordinates.height) {
                return false;
            }

            if (oldCoordinates.left !== newCoordinates.left) {
                return false;
            }

            if (oldCoordinates.top !== newCoordinates.top) {
                return false;
            }

            if (oldCoordinates.width !== newCoordinates.width) {
                return false;
            }

            return true;
        },
        getAndSetMeasurement(pictureKey) {
            let picture = this.pictures[pictureKey];

            if (!picture.coordinates) {
                return;
            }

            let article = this.getArticleByID(picture.articleID);
            let originDimensions = this.getOriginDimensions(
                picture.coordinates,
                picture.ratio
            );

            let width = originDimensions.width;
            if (width > picture.width) {
                width = picture.width;
            }

            let height = originDimensions.height;
            if (height > picture.height) {
                height = picture.height;
            }

            // the order is sent with parseInt'ed dimensions and the backend
            // recalculates the measurement from those, so round here as well
            width = parseInt(width);
            height = parseInt(height);

            let originWidth = width;
            let originHeight = height;
            if (originHeight > originWidth) {
                originWidth = height;
                originHeight = width;
            }

            const dpi = article.dpi;

            let measurementFactor = 1.0;
            if (this.getInfos.measurementUnit === 'cm') {
                measurementFactor = 2.54;
            }

            let targetPixel = (dpi / measurementFactor) * article.width;
            let newWidth = Math.ceil(
                (originWidth * targetPixel) / originHeight
            );
            let measurementValue = (newWidth * measurementFactor) / dpi;

            let measurementValueRounded =
                Math.round(measurementValue * 10) / 10;

            Vue.set(
                this.pictures[pictureKey],
                'measurementValue',
                measurementValueRounded
            );
        },
        isVariableHeight(key) {
            let item = this.pictures[key];
            let article = this.getArticleByID(item.articleID);

            return article.isHeightVariable;
        },
        getPictureFormatName(key) {
            let item = this.pictures[key];

            if (item.coordinates) {
                if (item.coordinates.height > item.coordinates.width) {
                    return 'vertical';
                }

                return 'horizontal';
            }

            if (item.height > item.width) {
                return 'vertical';
            }

            return 'horizontal';
        },
        getPrintBorders(articleID) {
            let article = this.getArticleByID(articleID);

            return article && article.printBorders ? article.printBorders : [];
        },
        // without an own title the color name and the width identify it
        getPrintBorderTitle(printBorder) {
            if (printBorder.title) {
                return printBorder.title;
            }

            let name = '';
            let suggested = this.getInfos.printBorder
                ? this.getInfos.printBorder.suggestedColors
                : [];
            for (let i = 0, j = suggested.length; i < j; i++) {
                if (suggested[i].hex === printBorder.color) {
                    name = this.$t(suggested[i].translationKey) + ' ';
                }
            }

            return name + printBorder.inMM + ' mm';
        },
        getPrintBorderByKey(key) {
            let item = this.pictures[key];
            let printBorders = this.getPrintBorders(item.articleID);

            for (let i = 0, j = printBorders.length; i < j; i++) {
                if (printBorders[i].id === item.printBorderID) {
                    return printBorders[i];
                }
            }

            return null;
        },
        getPrintBorderClass(key, withLabel) {
            if (!this.getPrintBorderByKey(key)) {
                return '';
            }

            return ' print-border' + (withLabel ? ' print-border-label' : '');
        },
        // black or white text, whichever stays readable on the border color
        getPrintBorderLabelColor(hex) {
            const value = hex.replace('#', '');
            if (value.length !== 6) {
                return '#000000';
            }

            const luminance =
                (0.299 * parseInt(value.slice(0, 2), 16) +
                    0.587 * parseInt(value.slice(2, 4), 16) +
                    0.114 * parseInt(value.slice(4, 6), 16)) /
                255;

            return luminance > 0.6 ? '#000000' : '#ffffff';
        },
        // the border is uniform in mm, so it is expressed relative to the
        // article side each stencil axis maps to
        getPrintBorderVars(key) {
            let item = this.pictures[key];
            let article = this.getArticleByID(item.articleID);
            let printBorder = this.getPrintBorderByKey(key);

            if (!article || !printBorder) {
                return {};
            }

            let shortSide = article.isHeightVariable
                ? article.width
                : Math.min(article.width, article.height);
            if (!shortSide) {
                return {};
            }

            let border = printBorder.inMM / 10;
            if (this.getInfos.measurementUnit === 'inch') {
                border = printBorder.inMM / 25.4;
            }

            let ratio = this.getCropperRatioByPictureKey(key);
            if (!ratio && item.coordinates && item.coordinates.height) {
                ratio = item.coordinates.width / item.coordinates.height;
            }
            if (!ratio) {
                ratio = 1;
            }

            let share = (border / shortSide) * 100;

            return {
                '--print-border-v': (ratio >= 1 ? share : share * ratio) + '%',
                '--print-border-h': (ratio >= 1 ? share / ratio : share) + '%',
                '--print-border-color': printBorder.color,
                '--print-border-label': JSON.stringify(
                    this.$t('frame.x.mm', {x: printBorder.inMM})
                ),
                '--print-border-label-color': this.getPrintBorderLabelColor(
                    printBorder.color
                ),
            };
        },
        getCropperRatioByPictureKey(key) {
            let item = this.pictures[key];
            let article = this.getArticleByID(item.articleID);

            if (article.isHeightVariable) {
                return null;
            }

            if (item.rotate) {
                return this.rotatedCropperRatio(article, item);
            }

            // hochformat
            if (item.height > item.width) {
                if (article.height > article.width) {
                    return article.width / article.height;
                }

                return article.height / article.width;
            }

            // querformat
            if (article.height > article.width) {
                return article.height / article.width;
            }

            return article.width / article.height;
        },
        rotatedCropperRatio(article, item) {
            // hochformat
            if (item.height > item.width) {
                if (article.height > article.width) {
                    return article.height / article.width;
                }

                return article.width / article.height;
            }

            // querformat
            if (article.height > article.width) {
                return article.width / article.height;
            }

            return article.height / article.width;
        },
        postOrderRequestPayment() {
            // the QR code's Checkout Session comes with the order instead
            if (this.isQRCodePayment) {
                return Promise.resolve();
            }

            // this.paymentRendered = false;

            let platform = 'browser';
            if (this.isApp) {
                platform = 'app';
            }

            if (this.isTerminalMode) {
                platform = 'terminal';
            }

            let parsedPictures = [];
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (
                    this.pictures[i].error !== null ||
                    !this.pictures[i].coordinates
                ) {
                    continue;
                }

                const thumbnailCoordinates = this.pictures[i].coordinates;
                const pictureCoordinates = this.getOriginDimensions(
                    thumbnailCoordinates,
                    this.pictures[i].ratio
                );

                let width = pictureCoordinates.width;
                let height = pictureCoordinates.height;

                if (width > this.pictures[i].width) {
                    width = this.pictures[i].width;
                }
                if (height > this.pictures[i].height) {
                    height = this.pictures[i].height;
                }

                let top = parseInt(pictureCoordinates.top);
                if (top < 0) {
                    top = 0;
                }

                let left = parseInt(pictureCoordinates.left);
                if (left < 0) {
                    left = 0;
                }

                let thumbnailTop = parseInt(thumbnailCoordinates.top);
                if (thumbnailTop < 0) {
                    thumbnailTop = 0;
                }

                let thumbnailLeft = parseInt(thumbnailCoordinates.left);
                if (thumbnailLeft < 0) {
                    thumbnailLeft = 0;
                }

                parsedPictures.push({
                    id: this.pictures[i].id,
                    amount: this.pictures[i].amount,
                    articleId: this.pictures[i].articleID,
                    printBorderId: this.pictures[i].printBorderID,
                    sort: this.pictures[i].sort,
                    measurementValue: this.pictures[i].measurementValue,

                    pictureCroppedWidth: parseInt(width),
                    pictureCroppedHeight: parseInt(height),
                    pictureCroppedTop: top,
                    pictureCroppedLeft: left,

                    thumbnailCroppedHeight: parseInt(
                        thumbnailCoordinates.height
                    ),
                    thumbnailCroppedWidth: parseInt(thumbnailCoordinates.width),
                    thumbnailCroppedTop: thumbnailTop,
                    thumbnailCroppedLeft: thumbnailLeft,
                });
            }

            let http = axios.create();
            return http
                .post(
                    this.apiUrl + '/requests/' + this.requestToken + '/payment',
                    {
                        requestId: this.requestToken,
                        deliveryType: this.deliveryType,
                        pictures: parsedPictures,
                        platform: platform,
                    }
                )
                .then((response) => {
                    if (
                        this.elementsOptions.clientSecret !== response.data.key
                    ) {
                        this.paymentCompleted = false;
                        this.paymentPicked = false;
                    }

                    this.elementsOptions.clientSecret = response.data.key;
                    this.paymentKey = true;

                    this.$refs.paymentRef.update();
                })
                .catch(() => {});
        },
        deleteOrderRequestItem(fileStruct) {
            const config = {
                headers: {
                    index: fileStruct.id,
                },
            };

            this.requestTokenHeaders = null;

            let http = axios.create();
            http.patch(
                this.apiUrl + '/requests/' + this.requestToken,
                null,
                config
            )
                .then((response) => {
                    this.requestTokenHeaders = response.headers;
                })
                .catch(() => {});
        },
        deleteRequestToken() {
            let http = axios.create();
            http.delete(this.apiUrl + '/requests/' + this.requestToken)
                .then(() => {})
                .catch(() => {});
        },
        postRequestToken() {
            this.requestTokenHeaders = null;

            let http = axios.create();
            http.post(this.apiUrl + '/requests')
                .then((response) => {
                    this.requestToken = response.data.token;
                    this.requestTokenHeaders = response.headers;
                })
                .catch((e) => {
                    this.checkGenericError(e);
                });
        },
        retryFailedPictures() {
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].error !== null) {
                    this.pictures[i].error = null;
                    this.pictures[i].thumbnail = null;
                    this.pictures[i].thumbnailUrl = null;
                    this.pictures[i].thumbnailWidth = null;
                    this.pictures[i].thumbnailHeight = null;
                    this.pictures[i].coordinates = null;
                    this.pictures[i].thumbnailCroppedUrl = null;
                    this.pictures[i].uploadPercentage = 0;
                    this.pictures[i].isDone = false;
                    this.pictures[i].inProgress = false;
                }
            }
        },
        clonePicture(key) {
            if (this.pictures[key].cloneInProgress) {
                return;
            }

            if (this.pictures[key].uploadPercentage === 100) {
                this.cloneRequestToken(key);
                return;
            }

            const newId = uuidv4();
            this.clonePictureSuccess(
                key,
                newId,
                false,
                this.pictures[key].thumbnailUrl !== null
            );
            this.pictures[key].cloneInProgress = false;
        },
        cloneRequestToken(key) {
            const picture = this.pictures[key];
            picture.cloneInProgress = true;

            let self = this;
            this.requestTokenHeaders = null;
            const newId = uuidv4();

            let http = axios.create();
            http.put(
                this.apiUrl + '/requests/' + self.requestToken + '/clone',
                {
                    sourceId: picture.id,
                    destinationId: newId,
                }
            )
                .then((response) => {
                    // a delete meanwhile shifts the keys
                    const currentKey = self.pictures.indexOf(picture);
                    if (currentKey >= 0) {
                        self.clonePictureSuccess(currentKey, newId, true, true);
                    }
                    picture.cloneInProgress = false;

                    this.requestTokenHeaders = response.headers;
                })
                .catch((e) => {
                    picture.cloneInProgress = false;

                    self.checkGenericError(e);
                });
        },
        retryUpload(fileStruct) {
            fileStruct.error = null;
            fileStruct.thumbnail = null;
            fileStruct.thumbnailUrl = null;
            fileStruct.thumbnailWidth = null;
            fileStruct.thumbnailHeight = null;
            fileStruct.coordinates = null;
            fileStruct.thumbnailCroppedUrl = null;
            fileStruct.uploadPercentage = 0;
            fileStruct.isDone = false;
            fileStruct.inProgress = false;
        },
        putRequestToken(fileStruct) {
            let self = this;

            let formData = new FormData();
            formData.append('file', fileStruct.file, fileStruct.file.name);
            formData.append('meta', fileStruct.thumbnail, fileStruct.file.name);

            const config = {
                onUploadProgress: function (progressEvent) {
                    fileStruct.uploadPercentage = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                },
                headers: {
                    index: fileStruct.id,
                },
            };
            this.requestTokenHeaders = null;
            let http = axios.create();
            http.put(
                this.apiUrl + '/requests/' + self.requestToken,
                formData,
                config
            )
                .then((response) => {
                    fileStruct.uploadPercentage = 100;

                    fileStruct.inProgress = false;
                    fileStruct.isDone = true;

                    this.requestTokenHeaders = response.headers;
                })
                .catch((e) => {
                    self.checkGenericError(e);
                    fileStruct.uploadPercentage = 0;
                    fileStruct.error = e;
                    fileStruct.inProgress = false;
                    fileStruct.isDone = true;
                });
        },
        manipulateAmount(key, manipulator) {
            this.pictures[key].amount += manipulator;
            this.validateAmount(key);
        },
        validateAmount(key) {
            if (!this.pictures[key].amount || this.pictures[key].amount === 0) {
                this.pictures[key].amount = 1;
            }

            if (this.pictures[key].amount < 0) {
                this.pictures[key].amount *= -1;
            }
        },
        getNextTierHint(item) {
            const next = this.nextTierByArticle[item.articleID];
            if (!next) {
                return null;
            }

            return this.$t('studio.tier.next', {
                amount: next.amount,
                price:
                    this.getNumberWithCurrency(next.price) +
                    this.getPerUnitSuffix(item),
            });
        },
        getGroupedPicturesAmountByArticleID(articleID) {
            let amount = 0;

            let pictures = this.pictures;
            for (let i = 0, j = pictures.length; i < j; i++) {
                if (pictures[i].articleID === articleID) {
                    amount += pictures[i].amount;
                }
            }

            return amount;
        },
        getSortedGraduatedPrices(graduatedPrices, invert = false) {
            let graduatedPricesParsed = JSON.parse(
                JSON.stringify(graduatedPrices)
            );

            function sortNodes(a, b) {
                if (a.amount < b.amount) return -1;
                if (a.amount > b.amount) return 1;
                return 0;
            }

            function sortNodesInvert(a, b) {
                if (a.amount > b.amount) return -1;
                if (a.amount < b.amount) return 1;
                return 0;
            }

            if (invert) {
                return graduatedPricesParsed.sort(sortNodesInvert);
            }

            return graduatedPricesParsed.sort(sortNodes);
        },
        getCostsByAmountAndArticleId(item) {
            let articleID = item.articleID;
            let amount = item.amount;

            let article = this.getArticleByID(articleID);
            let price = article.price;

            if (article.graduatedPrices) {
                let graduatedPrices = this.getSortedGraduatedPrices(
                    article.graduatedPrices
                );
                let picturesAmount =
                    this.getGroupedPicturesAmountByArticleID(articleID);

                for (let k = 0, l = graduatedPrices.length; k < l; k++) {
                    if (picturesAmount >= graduatedPrices[k].amount) {
                        price = graduatedPrices[k].price;
                    }
                }
            }

            if (article.isHeightVariable) {
                let measurementValue = item.measurementValue;
                let measurementValueRounded = Math.round(measurementValue);

                price = price * measurementValueRounded;

                price = Math.round(price * 10) / 10;

                if (article.minPrice && article.minPrice > price) {
                    price = article.minPrice;
                }
            }

            return amount * price;
        },
        getPriceByArticleID(articleID) {
            let article = this.getArticleByID(articleID);

            let price = article.price;
            if (article.graduatedPrices) {
                let graduatedPrices = this.getSortedGraduatedPrices(
                    article.graduatedPrices
                );
                let picturesAmount =
                    this.getGroupedPicturesAmountByArticleID(articleID);

                for (let k = 0, l = graduatedPrices.length; k < l; k++) {
                    if (picturesAmount >= graduatedPrices[k].amount) {
                        price = graduatedPrices[k].price;
                    }
                }
            }

            return this.getNumberWithCurrency(price);
        },
        getArticleByID(articleID) {
            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                if (articles[i].id === articleID) {
                    return articles[i];
                }
            }
        },
        getPictureWidthLowDiff(picture) {
            let article = this.getArticleByID(picture.articleID);
            let rec = this.getPixelFromMeasurement(article.width, article.dpi);

            if (picture.width < rec) {
                return parseInt(rec - picture.width);
            }

            return 0;
        },
        getPictureHeightLowDiff(picture) {
            let article = this.getArticleByID(picture.articleID);
            let rec = this.getPixelFromMeasurement(article.height, article.dpi);

            if (picture.height < rec) {
                return parseInt(rec - picture.height);
            }

            return 0;
        },
        getPapersByFormat(format) {
            let papers = [];

            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                let rowFormat = this.getArticleFormat(articles[i]);

                if (format && format !== rowFormat) {
                    continue;
                }

                let perUnit = '';
                if (articles[i].isHeightVariable) {
                    perUnit =
                        ' ' +
                        this.$t('per.x', {
                            x: this.$t(this.getInfos.measurementUnit),
                        });
                }

                // the single price, and the top graduated price as one line
                // under it ("ab 50 Stk. 0,30 €")
                let price = articles[i].price;
                let tierHint = null;
                if (articles[i].graduatedPrices) {
                    const graduatedPrices = this.getSortedGraduatedPrices(
                        articles[i].graduatedPrices
                    );
                    let top = null;
                    for (let k = 0, l = graduatedPrices.length; k < l; k++) {
                        // a tier at 1 replaces the base price
                        if (graduatedPrices[k].amount <= 1) {
                            price = graduatedPrices[k].price;
                        } else {
                            top = graduatedPrices[k];
                        }
                    }

                    if (top !== null && top.price < price) {
                        tierHint = this.$t('studio.tier.next', {
                            amount: top.amount,
                            price:
                                this.getNumberWithCurrency(top.price) + perUnit,
                        });
                    }
                }

                let priceString = this.getNumberWithCurrency(price) + perUnit;

                let minPriceString = null;
                if (articles[i].isHeightVariable && articles[i].minPrice) {
                    minPriceString = this.getNumberWithCurrency(
                        articles[i].minPrice
                    );
                }

                papers.push({
                    value: articles[i].id,
                    text: articles[i].paper,
                    price: priceString,
                    tierHint: tierHint,
                    badge: articles[i].badge || '',
                    minPrice: minPriceString,
                });
            }

            papers = papers.sort((a, b) =>
                this.comparePaperNames(a.text, b.text)
            );

            return papers;
        },
        getFormatsByPaper(paper) {
            let formats = [];

            let articles = this.getInfos.articles;
            for (let i = 0, j = articles.length; i < j; i++) {
                if (paper && articles[i].paper !== paper) {
                    continue;
                }

                formats.push({
                    value: articles[i].id,
                    width: articles[i].width,
                    height: articles[i].height,
                    text:
                        this.getArticleFormat(articles[i]) +
                        ' (' +
                        this.getNumberWithCurrency(articles[i].price) +
                        ')',
                });
            }

            return formats.sort(this.articleFormatSortFunc);
        },
        replacePicture() {
            const file = this.$refs.replacePicture.files[0];
            const i = this.pictures.findIndex(
                (picture) => picture.id === this.replacePictureId
            );
            if (!file || i < 0) {
                return;
            }

            this.pictures[i].file = file;
            this.pictures[i].thumbnail = null;
            this.pictures[i].thumbnailUrl = null;
            this.pictures[i].thumbnailWidth = null;
            this.pictures[i].thumbnailHeight = null;
            this.pictures[i].coordinates = null;
            this.pictures[i].thumbnailCroppedUrl = null;
            this.pictures[i].uploadPercentage = 0;
            this.pictures[i].error = null;
            this.pictures[i].isDone = false;
            this.pictures[i].inProgress = false;
        },
        deletePicture(key) {
            this.deleteOrderRequestItem(this.pictures[key]);
            this.pictures.splice(key, 1);
        },
        clonePictureSuccess(key, newId, copyStatus, copyMeta) {
            let picture = this.pictures[key];

            let uploadPercentage = 0;
            let error = null;
            let isDone = false;
            let inProgress = false;

            if (copyStatus) {
                uploadPercentage = picture.uploadPercentage;
                error = picture.error;
                isDone = picture.isDone;
                inProgress = picture.inProgress;
            }

            let thumbnail = null;
            let thumbnailUrl = null;
            let thumbnailWidth = null;
            let thumbnailHeight = null;
            let coordinates = null;
            let width = null;
            let height = null;
            let thumbnailCroppedUrl = null;
            let ratio = null;

            if (copyMeta) {
                thumbnail = picture.thumbnail;
                thumbnailUrl = picture.thumbnailUrl;
                thumbnailWidth = picture.thumbnailWidth;
                thumbnailHeight = picture.thumbnailHeight;
                coordinates = picture.coordinates;
                width = picture.width;
                height = picture.height;
                thumbnailCroppedUrl = picture.thumbnailCroppedUrl;
                ratio = picture.ratio;
            }

            // the copy goes right after its original: renumber the order, so
            // repeated copies never run out of room between two sort values
            const sorted = this.sortedPictures;
            const index = sorted.indexOf(picture);
            for (let i = 0, j = sorted.length; i < j; i++) {
                sorted[i].sort = i <= index ? i : i + 1;
            }
            const sortVal = index + 1;

            // the copy keeps format and crop, it is mostly made to order the
            // same picture a second way
            let newFileStruct = {
                sort: sortVal,

                id: newId,
                format: picture.format,
                paper: picture.paper,
                amount: 1,
                articleID: picture.articleID,
                printBorderID: picture.printBorderID,
                rotate: picture.rotate,

                measurementValue: picture.measurementValue,

                file: picture.file,

                thumbnail: thumbnail,
                thumbnailUrl: thumbnailUrl,
                thumbnailWidth: thumbnailWidth,
                thumbnailHeight: thumbnailHeight,
                coordinates: coordinates,
                width: width,
                height: height,
                thumbnailCroppedUrl: thumbnailCroppedUrl,
                ratio: ratio,

                uploadPercentage: uploadPercentage,
                error: error,
                isDone: isDone,
                inProgress: inProgress,
            };

            this.pictures.push(newFileStruct);

            if (this.editorPicture) {
                return;
            }

            // it sits next to the original: scroll to it only when it is cut
            // off, and ring it briefly, since it is the same photo
            this.newPictureId = newId;
            this.$nextTick(() => {
                const element = document.getElementById('picture-' + newId);
                const rect = element ? element.getBoundingClientRect() : null;
                const done = () => {
                    setTimeout(() => {
                        if (this.newPictureId === newId) {
                            this.newPictureId = null;
                        }
                    }, 1200);
                };

                if (
                    rect &&
                    (rect.top < this.$refs.nav.offsetHeight ||
                        rect.bottom > window.innerHeight)
                ) {
                    this.scrollToElement(element).then(done);
                    return;
                }

                done();
            });
        },
        handleFilesUpload() {
            let files = this.$refs['pictures'].files;

            /*
            if (files.length > this.getInfos.maxPicturesPerOrder) {
                this.picturesError = this.$t('pictures.max.reached.drop', {x: this.getInfos.maxPicturesPerOrder});
                return;
            }
             */

            this.processFilesUpload(files);
        },
        getFirstFormat() {
            let formats = this.getFormats;

            let favoriteArticle = this.getFavoriteArticle();
            if (favoriteArticle) {
                return this.getArticleFormat(favoriteArticle);
            }

            return formats[0];
        },
        getFavoriteArticle() {
            let articles = this.getInfos.articles;

            if (articles) {
                for (let i = 0, j = articles.length; i < j; i++) {
                    if (articles[i].isFavorite) {
                        return articles[i];
                    }
                }
            }

            return null;
        },
        getFirstArticleIDByFormat(format, paper) {
            if (!format) {
                if (!this.getInfos.articles) {
                    return 0;
                }

                let favoriteArticle = this.getFavoriteArticle();
                if (favoriteArticle) {
                    return favoriteArticle.id;
                }

                return this.getInfos.articles[0].id;
            }

            let articles = this.getInfos.articles;

            articles = articles.sort(
                (a, b) => Number(b.isFavorite) - Number(a.isFavorite)
            );

            articles = articles.sort(this.articleFormatSortFunc);

            if (paper) {
                paper = this.getPaperName(paper);

                for (let i = 0, j = articles.length; i < j; i++) {
                    let parsedFormat = this.getArticleFormat(articles[i]);
                    if (
                        format === parsedFormat &&
                        paper === this.getPaperName(articles[i].paper)
                    ) {
                        return articles[i].id;
                    }
                }
            }

            // a name groups several sizes and the width sort puts the smallest first
            const favoriteArticle = this.getFavoriteArticle();
            if (
                favoriteArticle &&
                format === this.getArticleFormat(favoriteArticle)
            ) {
                return favoriteArticle.id;
            }

            for (let i = 0, j = articles.length; i < j; i++) {
                let parsedFormat = this.getArticleFormat(articles[i]);
                if (format === parsedFormat) {
                    return articles[i].id;
                }
            }
        },
        getArticleFormat(article) {
            if (article.name) {
                return article.name;
            }

            if (article.isHeightVariable) {
                return (
                    this.roundWidth(article.width) +
                    ' ' +
                    this.$t(this.getInfos.measurementUnit) +
                    ' x ' +
                    this.$t('pictureLength')
                );
            }

            return (
                this.roundWidth(article.width) +
                ' x ' +
                this.roundHeight(article.height) +
                ' ' +
                this.$t(this.getInfos.measurementUnit)
            );
        },
        articleFormatSortFunc(a, b) {
            if (a.width < b.width) return -1;
            if (a.width > b.width) return 1;
            return 0;
        },
        picturestSortFunc(a, b) {
            if (a.sort < b.sort) return -1;
            if (a.sort > b.sort) return 1;
            return 0;
        },
        getFirstArticleIDByPaper(paper) {
            if (!paper) {
                return this.getInfos.articles[0].id;
            }

            let articles = this.getInfos.articles;

            articles = articles.sort(this.articleFormatSortFunc);

            for (let i = 0, j = articles.length; i < j; i++) {
                if (articles[i].paper === paper) {
                    return articles[i].id;
                }
            }
        },
        processFileEntries(files) {
            let self = this;

            let firstFormat = this.getFirstFormat();
            let firstArticleID = this.getFirstArticleIDByFormat(firstFormat);
            let firstPaper = this.getArticleByID(firstArticleID).paper;

            // after the highest sort: a copy is the last in the list, but sorted
            // next to its original
            let sortModifier = 0;
            for (let i = 0, j = self.pictures.length; i < j; i++) {
                sortModifier = Math.max(sortModifier, self.pictures[i].sort + 1);
            }

            for (let i = 0, j = files.length; i < j; i++) {
                if (self.pictures.length >= this.getInfos.maxPicturesPerOrder) {
                    this.pictureLimitErrors.push(files[i].name);
                    continue;
                }

                let newFileStruct = {
                    sort: sortModifier + i,

                    id: uuidv4(),
                    format: firstFormat,
                    paper: firstPaper,
                    amount: 1,
                    articleID: firstArticleID,
                    printBorderID: '',
                    measurementValue: 0.0,

                    file: files[i],
                    thumbnail: null,

                    thumbnailUrl: null,
                    thumbnailWidth: null,
                    thumbnailHeight: null,
                    coordinates: null,
                    thumbnailCroppedUrl: null,
                    ratio: null,

                    uploadPercentage: 0,
                    error: null,
                    isDone: false,
                    inProgress: false,
                };

                self.pictures.push(newFileStruct);
            }
        },
        setPictureError() {
            if (this.pictureTypeErrors.length > 0) {
                this.picturesError = this.$t('files.type.error', {
                    name: this.$t('pictures'),
                    types: this.uploadTypesText,
                });
                return;
            }

            if (this.pictureSizeErrors.length > 0) {
                this.picturesError = this.$t('files.size.error', {
                    name: this.$t('pictures'),
                    mb: this.getMaxFileSizeInMB,
                });
                return;
            }

            if (this.pictureLimitErrors.length > 0) {
                this.picturesError = this.$t('pictures.max.reached.drop', {
                    x: this.getInfos.maxPicturesPerOrder,
                });
            }
        },
        showPictureErrors() {
            // this.setPictureError();
            const messageTimeout = 0;

            if (this.pictureTypeErrors.length > 0) {
                UIkit.notification({
                    message:
                        this.$t('files.type.error', {
                            name: this.$t('pictures'),
                            types: this.uploadTypesText,
                        }) + '.',
                    status: 'danger',
                    pos: 'top-center',
                    timeout: messageTimeout,
                });

                this.pictureTypeErrors = [];
            }

            if (this.pictureSizeErrors.length > 0) {
                UIkit.notification({
                    message:
                        this.$t('files.size.error', {
                            name: this.$t('pictures'),
                            mb: this.getMaxFileSizeInMB,
                        }) + '.',
                    status: 'danger',
                    pos: 'top-center',
                    timeout: messageTimeout,
                });

                this.pictureSizeErrors = [];
            }

            if (this.pictureLimitErrors.length > 0) {
                UIkit.notification({
                    message:
                        this.$t('pictures.max.reached.drop', {
                            x: this.getInfos.maxPicturesPerOrder,
                        }) + '.',
                    status: 'danger',
                    pos: 'top-center',
                    timeout: messageTimeout,
                });

                this.pictureLimitErrors = [];
            }
        },
        processFilesUpload(files) {
            this.picturesError = null;
            let parsedFiles = [];

            for (let i = 0, j = files.length; i < j; i++) {
                let fileExtension = files[i].name.split('.').pop();
                fileExtension = fileExtension.toLowerCase();
                if (!this.fileExtensions.includes(fileExtension)) {
                    this.pictureTypeErrors.push(files[i].name);
                    continue;

                    /*
                    this.picturesError = this.$t("files.type.error", {
                        name: this.$t('pictures'),
                        types: this.getFormattedTypesString(this.fileExtensions)
                    });

                    return;
                     */
                }

                if (files[i].size > this.getMaxFileSizeInMB * 1024 * 1024) {
                    this.pictureSizeErrors.push(files[i].name);
                    continue;

                    /*
                    this.picturesError = this.$t("files.size.error", {
                        name: this.$t('pictures'),
                        mb: this.getMaxFileSizeInMB
                    });
                    return;
                */
                }

                parsedFiles.push(files[i]);
            }

            if (parsedFiles.length === 0) {
                this.showPictureErrors();
            }

            /*
            let checkedFiles = [];
            for (let i = 0, j = parsedFiles.length; i < j; i++) {
                let self = this;

                try {
                let fileReader = new FileReader();
                fileReader.onloadend = function (e) {
                    let arr = (new Uint8Array(e.target.result)).subarray(0, 4);
                    let header = '';
                    for (let i = 0; i < arr.length; i++) {
                        header += arr[i].toString(16);
                    }

                    let type = 'unknown';
                    switch (header) {
                        case '89504e47':
                            type = 'image/png';
                            break;
                        case "ffd8ffe0":
                        case "ffd8ffe1":
                        case "ffd8ffe2":
                        case "ffd8ffe3":
                        case "ffd8ffe8":
                        case "ffd8ffdb":
                            type = 'image/jpeg';
                            break;
                    }

                    if (type === 'unknown') {

                        self.pictureTypeErrors.push(parsedFiles[i].name);


                        //self.picturesError = self.$t("files.type.error", {
                        //    name: self.$t('pictures'),
                      //      types: self.getFormattedTypesString(self.fileExtensions)
                        //});
                        //return;

                    } else {
                        checkedFiles.push(parsedFiles[i]);
                    }


                   // if (self.picturesError === null && i === (files.length - 1)) {
                     //   self.processFileEntries(files);
                    //}


                    if (i === (parsedFiles.length - 1)) {
                        self.processFileEntries(checkedFiles);
                        self.showPictureErrors();
                    }

                };
                fileReader.readAsArrayBuffer(parsedFiles[i]);

                } catch (e) {
                    self.pictureTypeErrors.push(parsedFiles[i].name);

                }

            }
            // onload
            */

            this.processFileEntries(parsedFiles);
            this.showPictureErrors();
        },
        thumbnailQueue() {
            let self = this;
            this.intervalThumbnail = setInterval(() => {
                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (self.pictures[i].thumbnailing) {
                        return;
                    }
                }

                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (
                        self.pictures[i].thumbnailUrl ||
                        self.pictures[i].error
                    ) {
                        continue;
                    }

                    self.pictures[i].thumbnailing = true;
                    self.imageScaleDown(i);

                    return;
                }
            }, 100);
        },
        thumbnailQueueTerminall() {
            let self = this;
            setInterval(() => {
                if (!self.isTerminalMode) {
                    return;
                }

                if (!self.terminalFiles) {
                    return;
                }

                if (self.terminalFilesReplacing) {
                    return;
                }

                if (self.terminalFiles.length === 0) {
                    return;
                }

                for (let i = 0, j = self.terminalFiles.length; i < j; i++) {
                    if (self.terminalFiles[i].thumbnailing) {
                        return;
                    }
                }

                for (let i = 0, j = self.terminalFiles.length; i < j; i++) {
                    if (self.terminalFiles[i].thumbnailUrl) {
                        continue;
                    }

                    if (self.terminalFiles[i].isDir) {
                        continue;
                    }

                    self.terminalFiles[i].thumbnailing = true;
                    self.fetchFileTerminal(i);

                    return;
                }
            }, 100);
        },
        uploadQueue() {
            let self = this;
            this.intervalUpload = setInterval(() => {
                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (self.pictures[i].inProgress) {
                        return;
                    }
                }

                for (let i = 0, j = self.pictures.length; i < j; i++) {
                    if (self.pictures[i].isDone) {
                        continue;
                    }

                    if (!self.pictures[i].file) {
                        continue;
                    }

                    if (!self.pictures[i].thumbnail) {
                        continue;
                    }

                    self.pictures[i].inProgress = true;
                    self.putRequestToken(self.pictures[i]);

                    return;
                }
            }, 100);
        },

        fetchFileTerminal(key) {
            const file = this.terminalFiles[key];

            let self = this;
            axios({
                method: 'get',
                url:
                    self.apiUrlTerminal +
                    '/files/' +
                    file.device +
                    file.path +
                    '/' +
                    file.name,
                responseType: 'blob',
            }).then(function (response) {
                let object = new File([response.data], file.name);
                Vue.set(self.terminalFiles[key], 'file', object);
                self.imageScaleDownTerminal(key);
            });
        },
        // the queue waits while any picture is thumbnailing: one that fails
        // has to leave, or every picture after it stays without a thumbnail
        setThumbnailError(item, error = unreadableError) {
            Vue.set(item, 'error', error);
            Vue.set(item, 'thumbnailing', false);
        },
        imageScaleDownTerminal(key) {
            const file = this.terminalFiles[key].file;
            let self = this;

            const blobURL = URL.createObjectURL(file);
            const img = new Image();
            img.src = blobURL;
            img.onerror = function () {
                URL.revokeObjectURL(this.src);
                Vue.set(self.terminalFiles[key], 'error', 'cannot load image');
            };
            img.onload = function () {
                URL.revokeObjectURL(this.src);
                const [newWidth, newHeight] = self.calculateAspectRatioFit(
                    img.width,
                    img.height,
                    436,
                    638
                );

                const canvas = document.createElement('canvas');
                canvas.width = newWidth;
                canvas.height = newHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
                canvas.toBlob(
                    (blob) => {
                        Vue.set(self.terminalFiles[key], 'thumbnail', blob);
                        Vue.set(
                            self.terminalFiles[key],
                            'thumbnailUrl',
                            self.getLocalFileUrl(blob)
                        );
                        Vue.set(self.terminalFiles[key], 'thumbnailing', false);
                    },
                    file.type,
                    0.5
                );
            };
        },
        imageScaleDown(key) {
            // held by reference, the index shifts when a picture is deleted
            // while this one is still being scaled
            const picture = this.pictures[key];
            const file = picture.file;
            let self = this;

            const blobURL = URL.createObjectURL(file);
            const img = new Image();
            img.src = blobURL;
            img.onerror = function () {
                URL.revokeObjectURL(this.src);
                self.setThumbnailError(picture);
            };
            img.onload = function () {
                URL.revokeObjectURL(this.src);

                Vue.set(picture, 'width', img.width);
                Vue.set(picture, 'height', img.height);
                const [newWidth, newHeight, ratio] =
                    self.calculateAspectRatioFit(
                        img.width,
                        img.height,
                        436,
                        638
                    );
                Vue.set(picture, 'ratio', ratio);

                const canvas = document.createElement('canvas');
                canvas.width = newWidth;
                canvas.height = newHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
                canvas.toBlob(
                    (blob) => {
                        // null when the canvas could not be encoded: memory,
                        // so a retry may work
                        if (!blob) {
                            self.setThumbnailError(
                                picture,
                                'cannot encode thumbnail'
                            );
                            return;
                        }

                        Vue.set(picture, 'thumbnail', blob);
                        Vue.set(picture, 'thumbnailWidth', canvas.width);
                        Vue.set(picture, 'thumbnailHeight', canvas.height);
                        Vue.set(
                            picture,
                            'thumbnailUrl',
                            self.getLocalFileUrl(blob)
                        );
                        Vue.set(picture, 'thumbnailing', false);

                        const currentKey = self.pictures.indexOf(picture);
                        if (currentKey >= 0 && !picture.coordinates) {
                            self.setPictureCoordinates(
                                currentKey,
                                self.getDefaultCoordinates(currentKey)
                            );
                        }
                    },
                    file.type,
                    0.5
                );
            };
        },
        getMimeType(file, fallback = null) {
            const byteArray = new Uint8Array(file).subarray(0, 4);
            let header = '';
            for (let i = 0; i < byteArray.length; i++) {
                header += byteArray[i].toString(16);
            }
            switch (header) {
                case '89504e47':
                    return 'image/png';
                case '47494638':
                    return 'image/gif';
                case 'ffd8ffe0':
                case 'ffd8ffe1':
                case 'ffd8ffe2':
                case 'ffd8ffe3':
                case 'ffd8ffe8':
                    return 'image/jpeg';
                default:
                    return fallback;
            }
        },
        calculateSize(img, maxWidth, maxHeight) {
            let width = img.width;
            let height = img.height;

            if (width > height) {
                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }
            }

            return [width, height];
        },
        setValidContactFields(valid, fieldKey) {
            this.contactFields[fieldKey].valid = valid;
        },
        setValidAddressFields(valid, fieldKey) {
            this.addressFields[fieldKey].valid = valid;
        },
        setTermsValid(valid) {
            this.termsValid = valid;
        },
        async complete() {
            if (this.loadingOrder) {
                return;
            }

            this.triggerContactFields++;
            this.triggerTerms++;

            if (!this.isPicturesValid) {
                const invalid = this.groupedPictures.find((picture) =>
                    this.isPictureTooLength(picture)
                );
                if (invalid) {
                    this.scrollToSection('picture-' + invalid.id);
                }
                return;
            }

            if (!this.isContactDetailsValid) {
                this.scrollToSection('checkout-contact');
                return;
            }

            if (this.isStoreRequired && !this.storeId) {
                this.scrollToSection('checkout-delivery');
                return;
            }

            let street = null;
            let zip = null;
            let city = null;
            let country = null;
            if (this.isAddressShown) {
                this.triggerAddressFields++;

                for (let i = 0, j = this.addressFields.length; i < j; i++) {
                    if (!this.addressFields[i].valid) {
                        this.scrollToSection(
                            this.isAddressInPayment
                                ? 'payment-method'
                                : 'checkout-delivery'
                        );
                        return;
                    }
                }

                street = this.addressFields[0].value;
                zip = this.addressFields[1].value;
                city = this.addressFields[2].value;
                country = this.country;
            }

            if (!this.termsValid) {
                this.scrollToSection('checkout-terms');
                return;
            }

            if (
                this.paymentMethod === 'online' &&
                !this.isQRCodePayment &&
                !this.paymentCompleted
            ) {
                this.scrollToSection('payment-method');
                // focusing the collapsed list would open its first method
                if (this.paymentPicked) {
                    this.$refs.paymentRef.focus();
                } else {
                    this.paymentPickRequired = true;
                }
                return;
            }

            // the amount must match the order as it is now, not as it was when
            // the payment form appeared
            if (this.paymentMethod === 'online' && !this.isQRCodePayment) {
                clearTimeout(this.paymentRefreshTimeout);
                await this.postOrderRequestPayment();

                if (!this.paymentCompleted) {
                    this.paymentPickRequired = true;
                    this.scrollToSection('payment-method');
                    return;
                }
            }

            this.loadingOrder = true;

            let parsedPictures = [];
            for (let i = 0, j = this.pictures.length; i < j; i++) {
                if (this.pictures[i].error !== null) {
                    continue;
                }

                const thumbnailCoordinates = this.pictures[i].coordinates;
                const pictureCoordinates = this.getOriginDimensions(
                    thumbnailCoordinates,
                    this.pictures[i].ratio
                );

                let width = pictureCoordinates.width;
                let height = pictureCoordinates.height;

                if (width > this.pictures[i].width) {
                    width = this.pictures[i].width;
                }
                if (height > this.pictures[i].height) {
                    height = this.pictures[i].height;
                }

                let top = parseInt(pictureCoordinates.top);
                if (top < 0) {
                    top = 0;
                }

                let left = parseInt(pictureCoordinates.left);
                if (left < 0) {
                    left = 0;
                }

                let thumbnailTop = parseInt(thumbnailCoordinates.top);
                if (thumbnailTop < 0) {
                    thumbnailTop = 0;
                }

                let thumbnailLeft = parseInt(thumbnailCoordinates.left);
                if (thumbnailLeft < 0) {
                    thumbnailLeft = 0;
                }

                parsedPictures.push({
                    id: this.pictures[i].id,
                    amount: this.pictures[i].amount,
                    articleId: this.pictures[i].articleID,
                    printBorderId: this.pictures[i].printBorderID,
                    sort: this.pictures[i].sort,
                    measurementValue: this.pictures[i].measurementValue,

                    pictureCroppedWidth: parseInt(width),
                    pictureCroppedHeight: parseInt(height),
                    pictureCroppedTop: top,
                    pictureCroppedLeft: left,

                    thumbnailCroppedHeight: parseInt(
                        thumbnailCoordinates.height
                    ),
                    thumbnailCroppedWidth: parseInt(thumbnailCoordinates.width),
                    thumbnailCroppedTop: thumbnailTop,
                    thumbnailCroppedLeft: thumbnailLeft,
                });
            }

            let platform = 'browser';
            if (this.isApp) {
                platform = 'app';
            }

            if (this.isTerminalMode) {
                platform = 'terminal';
            }

            let http = axios.create();
            http.post(this.apiUrl + '/orders', {
                requestId: this.requestToken,
                name: this.contactFields[0].value,
                email: this.contactFields[1].value,
                storeId: this.deliveryType === 'pickup' ? this.storeId : '',
                paymentMethod: this.paymentMethod,
                deliveryType: this.deliveryType,
                comments: this.comments,
                pictures: parsedPictures,
                language: this.$i18n.locale,
                platform: platform,
                qrCodePayment: this.isQRCodePayment,

                street,
                zip,
                city,
                country,
            })
                .then(async (response) => {
                    let self = this;

                    if (this.isQRCodePayment) {
                        this.showCheckout(response.data.checkoutUrl);
                    } else if (this.paymentMethod === 'online') {
                        this.disableLeaveProtection = true;

                        try {
                            const result =
                                await this.$refs.paymentRef.stripe.confirmPayment(
                                    {
                                        elements:
                                            this.$refs.paymentRef.elements,
                                        confirmParams: {
                                            // without an e-mail the order page
                                            // must not promise a confirmation
                                            return_url:
                                                (window.location.href.endsWith(
                                                    '/'
                                                )
                                                    ? window.location.href +
                                                      'orders/' +
                                                      this.requestToken +
                                                      '/'
                                                    : window.location.href +
                                                      '/orders/' +
                                                      this.requestToken +
                                                      '/') +
                                                (this.contactFields[1].value
                                                    ? ''
                                                    : '?withoutemail=1'),
                                        },
                                        // redirect: 'if_required',
                                    }
                                );

                            if (result.error) {
                                self.loadingOrder = false;
                                // UIkit.scroll('#payment-method', {offset: 0}).scrollTo('#payment-method');
                                this.$refs.paymentRef.focus();
                            } else {
                                self.loadingOrder = false;

                                self.errorOrder = null;
                            }
                        } catch (e) {
                            self.loadingOrder = false;
                            this.$refs.paymentRef.focus();
                            this.paymentError = e;
                        }
                    } else {
                        this.showOrderSuccess();
                    }
                })
                .catch((e) => {
                    if (e.response && e.response.status === 409) {
                        this.errorOrder = null;
                        this.loadingOrder = false;
                        this.successOrder = true;
                        this.scrollToTop();
                        return;
                    }

                    this.checkGenericError(e);
                    this.errorOrder = e;
                    this.loadingOrder = false;
                    this.successOrder = false;

                    if (
                        this.paymentMethod === 'online' &&
                        (!e.response ||
                            !e.response.data ||
                            !e.response.data.error)
                    ) {
                        this.postOrderRequestPayment();
                    }
                });
        },
        showOrderSuccess() {
            this.errorOrder = null;
            this.successOrder = true;
            this.loadingOrder = false;

            if (this.isTerminalWithAds) {
                let self = this;
                setTimeout(function () {
                    UIkit.modal(
                        '#' + self.modalIdTerminalAdsScreensaver
                    ).show();
                }, 10);
                return;
            }

            this.scrollToTop();
        },
        // the order is in; it goes through once Stripe reports the phone's
        // payment (ProcessOrderBackups), the page only waits for that
        showCheckout(url) {
            // an API without the QR code payment would leave the button
            // spinning for a code that never comes
            if (!url) {
                this.loadingOrder = false;
                this.errorOrder = new Error('checkoutUrl missing');
                return;
            }

            this.checkoutUrl = url;
            this.checkoutExpired = false;
            // a reload now must keep the upload: the customer may be paying
            // on the phone already (onunload would delete the request)
            this.disableLeaveProtection = true;
            document.documentElement.classList.add('studio-editor-open');

            this.$nextTick(() => {
                new QRCode(this.$refs.checkoutQrcode, {
                    text: url,
                    width: 320,
                    height: 320,
                });
            });

            this.pollCheckout();
        },
        pollCheckout() {
            clearTimeout(this.checkoutPollTimeout);
            this.checkoutPollTimeout = setTimeout(() => {
                const url = this.checkoutUrl;

                axios
                    .get(this.apiUrl + '/requests/' + this.requestToken + '/paid')
                    .then((response) => {
                        // cancelled, or a new code, in the meantime
                        if (this.checkoutUrl !== url) {
                            return;
                        }

                        if (response.data.paid) {
                            this.closeCheckout();
                            this.showOrderSuccess();
                            return;
                        }

                        if (response.data.expired) {
                            this.checkoutExpired = true;
                            return;
                        }

                        this.pollCheckout();
                    })
                    .catch((e) => {
                        if (this.checkoutUrl !== url) {
                            return;
                        }

                        // the backup is gone: expired and cleaned up
                        if (e.response && e.response.status === 400) {
                            this.checkoutExpired = true;
                            return;
                        }

                        this.pollCheckout();
                    });
            }, 3000);
        },
        // the code taken back, the customer can pay another way; paid in
        // that moment, the order goes through
        cancelCheckout() {
            clearTimeout(this.checkoutPollTimeout);
            this.loadingCheckoutCancel = true;

            axios
                .delete(
                    this.apiUrl + '/requests/' + this.requestToken + '/checkout'
                )
                .then(() => {
                    this.closeCheckout();
                })
                .catch((e) => {
                    this.closeCheckout();

                    if (e.response && e.response.status === 409) {
                        this.showOrderSuccess();
                    }
                });
        },
        closeCheckout() {
            clearTimeout(this.checkoutPollTimeout);
            this.checkoutUrl = '';
            this.checkoutExpired = false;
            this.loadingCheckoutCancel = false;
            this.loadingOrder = false;
            this.disableLeaveProtection = false;

            if (!this.editorPicture && !this.formatSheet) {
                document.documentElement.classList.remove('studio-editor-open');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
// LunaCart family (white round cards, the generic components) in a slim
// single page: top bar, pictures, order, legal footer
$page: #f5f4f1;
$line: #e8e8e6;
$muted: #707070;
$nav: 64px;

.studio {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: $page;
    color: $site-background;
}

.studio-container {
    box-sizing: border-box;
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 0 24px;
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-container {
        padding: 0 14px;
    }
}

.studio-muted {
    color: $muted;
}

.studio-small {
    font-size: 13px;
}

.studio-strong {
    font-weight: 500;
}

.studio-danger {
    color: $danger-color;
}

.studio-accent {
    color: $info-color;
}

.studio-link {
    color: inherit;
    text-decoration: underline !important;
    cursor: pointer;
}

.studio-title {
    margin: 0;
    color: $site-background;
    font-size: 22px;
    font-weight: 500;
    line-height: 1.3;

    .studio-muted {
        font-weight: 400;
    }
}

.studio-card-title {
    margin-bottom: 12px;
    color: $site-background;
    font-size: 17px;
    font-weight: 500;
}

// top bar

.studio-nav {
    position: sticky;
    top: 0;
    z-index: 980;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: saturate(180%) blur(12px);
    border-bottom: 1px solid $line;
}

.studio-nav-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: $nav;
}

.studio-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.studio-logo {
    max-height: 36px;
    max-width: 140px;
    border-radius: $border-radius-next;
    object-fit: contain;
}

.studio-name {
    overflow: hidden;
    font-size: 17px;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.studio-nav-end {
    display: flex;
    flex: none;
    align-items: center;
    gap: 14px;
}

.studio-nav-end ::v-deep .uk-button {
    border-radius: 20px;
}

// white on the white bar read as text: filled like .studio-soft-button
.studio-nav-cancel ::v-deep .uk-button {
    border-color: transparent;
    background: #f0efeb;

    // Button's own :focus turns it white; pressed still wins below
    &:focus {
        background: #f0efeb;
    }

    &:hover,
    &:active {
        background: #e5e4df;
    }
}

.studio-nav-total {
    white-space: nowrap;
}

.studio-nav-progress {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    width: 100%;
    height: 3px !important;
    margin: 0 !important;
    border-radius: 0 !important;
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-nav-inner {
        height: 56px;
    }

    .studio-logo {
        max-height: 30px;
        max-width: 90px;
    }

    .studio-name {
        font-size: 15px;
    }
}

// page

.studio-main {
    flex: 1;
    padding-top: 32px;
    padding-bottom: 40px;
}

.studio-notice {
    margin-bottom: 20px;
    padding: 12px 16px;
    border-radius: 16px;
    background: #fff4d6;
    color: #5c4400;
    text-align: center;
}

.studio-section {
    scroll-margin-top: $nav + 16px;

    & + .studio-section {
        margin-top: 48px;
    }
}

.studio-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;

    // the action right after the title, in line with "Für alle Bilder" below
    &.is-start {
        justify-content: flex-start;
    }
}

// empty state

.studio-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 56px 20px;
    border: 2px dashed #d4d4d0;
    border-radius: 16px;
    background: #fff;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover {
        border-color: $info-color;
    }
}

.studio-dropzone-title {
    margin-top: 14px;
    font-size: 20px;
    font-weight: 500;
}

// drag and drop only exists with a mouse
.studio-if-touch {
    display: none;
}

@media (hover: none) {
    .studio-if-pointer {
        display: none;
    }

    .studio-if-touch {
        display: inline;
    }
}

.studio-prices-head {
    margin-top: 20px;
    text-align: center;
}

// in a modal, so the drop zone stays the one thing on the empty page
.studio-prices {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    min-height: 0;
    padding: 0 20px 20px 20px;
    overflow-y: auto;
}

// title and hint read as one block, the code stands apart
.studio-editor-panel.studio-cancel-ask {
    max-width: 560px;
}

// the browser footer's pills: one size for both
.studio-cancel-ask-actions {
    display: flex;
    justify-content: flex-end;
    gap: 14px;
    padding: 4px 20px 20px 20px;

    ::v-deep .uk-button {
        min-height: 52px !important;
        border-radius: 26px;
        font-size: 16px !important;
        line-height: 52px;
    }
}

.studio-smartphone-title {
    margin-bottom: 6px;
    color: $site-background;
    font-size: 24px;
    font-weight: 500;
}

.studio-smartphone-code {
    margin-top: 14px;
}

.studio-price {
    padding: 16px 18px;
    border-radius: 16px;
    background: #f5f4f1;
}

.studio-price-format {
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 500;
}

.studio-price-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 5px 0;
    border-top: 1px solid $line;
    color: $muted;
    font-size: 14px;

    .studio-strong {
        color: $site-background;
        white-space: nowrap;
    }
}

.studio-price .studio-overview-tiers {
    padding-bottom: 6px;
}

.studio-add-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 16px 0 6px;
    border: 0;
    border-radius: 19px;
    background: #fff;
    color: $site-background;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
}

.studio-add-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: $site-background;
    color: #fff;

    // the icon's inline spans sit on the text baseline, which pushes it down
    ::v-deep .icon,
    ::v-deep .uk-icon,
    ::v-deep svg {
        display: block;
    }
}

.studio-title-sub {
    display: block;
    margin-top: 4px;
    font-size: 16px;
}

// terminal start: the media as big cards, the hero instead of a drop zone
.studio-sources {
    margin-bottom: 20px;
}

.studio-source-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 18px;
}

.studio-source {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    min-height: 220px;
    padding: 24px 16px;
    border: 0;
    border-radius: 16px;
    background: #fff;
    // UIkit's default card and its hover, like the checkout card
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    color: $site-background;
    font: inherit;
    font-size: 20px;
    font-weight: 500;
    cursor: pointer;
    transition: box-shadow 0.2s;

    &:active {
        box-shadow: 0 14px 25px rgba(0, 0, 0, 0.16);
    }

    // a tap on the kiosk would leave the card lifted
    @media (hover: hover) {
        &:hover {
            box-shadow: 0 14px 25px rgba(0, 0, 0, 0.16);
        }
    }
}

.studio-source-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background: #f0efeb;

    ::v-deep .icon,
    ::v-deep .uk-icon,
    ::v-deep svg {
        display: block;
    }
}

// auto-fit: two options still share the whole width
.studio-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;

    & > span {
        display: block;
    }
}

.studio-option {
    padding: 8px 0;
}

// bulk line

.studio-bulk {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
}

// a pill showing the value, its uk-dropdown sits right after it

.studio-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.studio-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    height: 30px;
    padding: 0 9px 0 11px;
    border: 0;
    border-radius: 15px;
    background: #f0efeb;
    color: $site-background;
    font: inherit;
    font-size: 13px;
    white-space: nowrap;
    cursor: pointer;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &.is-strong {
        font-weight: 500;
    }

    // no fill marks it optional; grey text, a pale line and the thin plus
    // looked blurred (2026-10-25)
    &.is-ghost {
        background: none;
        box-shadow: inset 0 0 0 1px #cfcec8;
    }

    &:hover,
    &[aria-expanded='true'] {
        background: #e5e4df;
    }
}

.studio-bulk .studio-chip {
    background: #fff;
    box-shadow: inset 0 0 0 1px #e3e3e0;

    &:hover,
    &[aria-expanded='true'] {
        box-shadow: inset 0 0 0 1px $site-background;
    }
}

// every format with its papers and prices, picked in one go

.studio-dropdown {
    width: 340px;
    max-width: calc(100vw - 32px);
    max-height: 60vh;
    padding: 6px;
    border-radius: 14px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
    overflow-y: auto;

    // opening upwards (the crop modal's), the shadow falls up: downwards it
    // lay on the tool row and looked like a shadow on the amount stepper
    &.is-upward {
        box-shadow: 0 -12px 32px rgba(0, 0, 0, 0.16);
    }

    &.is-narrow {
        width: 220px;

        // the filled items are stacked, not in the grid with its gap
        .studio-overview-paper + .studio-overview-paper {
            margin-top: 6px;
        }
    }

    // UIkit hides it only once it has picked up the new element; until then a
    // new tile (a copy, an upload) is the whole list high, and the copy's
    // scroll measured that and jumped
    &:not(.uk-open) {
        display: none;
    }
}

.studio-overview-row {
    padding: 6px 6px 8px 6px;

    & + & {
        border-top: 1px solid #f0efeb;
    }
}

.studio-overview-format {
    margin-bottom: 4px;
    padding: 0 6px;
    color: $site-background;
    font-size: 13px;
    font-weight: 500;
}

.studio-overview-papers {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    align-items: start;
    gap: 6px;
}

// a uk-accordion, one group open at a time; the group name is the toggle
.studio-overview {
    margin: 0;

    > .studio-overview-row {
        margin-top: 0;
    }

    // an <a href="#"> as UIkit expects: a button would also fire click on
    // Space, which UIkit already toggles on keydown
    .uk-accordion-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0;
        padding: 4px 6px;
        line-height: 1.4;
        text-decoration: none;

        &::before {
            display: none;
        }

        svg {
            transition: transform 0.2s;
        }
    }

    .uk-open > .uk-accordion-title svg {
        transform: rotate(180deg);
    }

    .uk-accordion-content {
        margin-top: 4px;
    }

    // a badge pill stands 7px above its button: room for it inside the
    // wrapper UIkit clips while the group opens
    .uk-accordion-content:has(.has-badge) {
        padding-top: 3px;
    }
}

.studio-overview-paper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 7px 10px;
    border: 0;
    border-radius: 10px;
    background: #f0efeb;
    color: $site-background;
    font: inherit;
    font-size: 14px;
    text-align: left;
    cursor: pointer;

    small {
        color: $muted;
        font-size: 12px;
        white-space: nowrap;
    }

    &:hover {
        background: #e5e4df;
    }

    &.active {
        background: $site-background;
        color: #fff;

        small {
            color: rgba(255, 255, 255, 0.7);
        }

        // a black frame's swatch would vanish on the dark item
        .studio-swatch {
            box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6);
        }

        .studio-overview-tiers {
            color: rgba(255, 255, 255, 0.7);
        }
    }

    // name and price on top (the price wraps under a long name), the tier
    // line below over the full width
    &.has-tiers {
        flex-wrap: wrap;
        align-items: baseline;
        row-gap: 0;

        small {
            margin-left: auto;
        }
    }

    .is-narrow & {
        justify-content: flex-start;
    }
}

@include article-badge-colors;

// an article the admin gave a badge (one article per badge, API
// allowedArticleBadges): a gradient frame, the padding 2px less so it keeps
// its size, and the badge as a pill on the top edge
.studio-overview-paper.has-badge {
    position: relative;
    padding: 5px 8px;
    border: 2px solid transparent;
    background: linear-gradient(#f0efeb, #f0efeb) padding-box,
        linear-gradient(120deg, var(--badge-from), var(--badge-to)) border-box;

    &:hover {
        background: linear-gradient(#e5e4df, #e5e4df) padding-box,
            linear-gradient(120deg, var(--badge-from), var(--badge-to))
                border-box;
    }

    // picked, the gradient frame around the black fill clashed (2026-10-27):
    // plain black like any picked item, the pill alone shows the badge
    &.active {
        background: $site-background;
    }
}

.studio-overview-badge {
    position: absolute;
    top: -7px;
    right: 8px;
    padding: 2px 8px;
    border-radius: 20px;
    background: linear-gradient(120deg, var(--badge-from), var(--badge-to));
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.6px;
    line-height: 1.3;
    text-transform: uppercase;
    white-space: nowrap;
    pointer-events: none;
}

// the top graduated price, one line
.studio-overview-tiers {
    flex-basis: 100%;
    margin-top: 2px;
    color: #1d7a58;
    font-size: 12px;
    line-height: 1.5;
}

.studio-swatch {
    flex: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
}

// tiles: the print as it will look, its settings right below

.studio-grid {
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 18px;
}

// like UIkit's filter: a tile that changes group slides to its new place, new
// ones fade in, removed ones fade out (a <transition-group>, fixLeavingTile).
// Under .studio-grid, or the tile's own `transition` below would win
.studio-grid {
    .studio-grid-move {
        transition: transform 0.35s ease, box-shadow 0.2s;
    }

    // the add tile belongs to no group: sliding, it crossed the group in
    // between for the whole move, so it is at its new place at once and
    // fades in there
    .studio-tile-add.studio-grid-move {
        transition: none;
        animation: studio-fade-in 0.35s ease;
    }

    .studio-grid-enter-active,
    .studio-grid-leave-active {
        transition: opacity 0.35s ease, transform 0.35s ease;
    }

    .studio-grid-enter,
    .studio-grid-leave-to {
        opacity: 0;
        transform: scale(0.96);
    }
}

@media (prefers-reduced-motion: reduce) {
    .studio-grid {
        .studio-grid-move,
        .studio-grid-enter-active,
        .studio-grid-leave-active {
            transition: none;
            animation: none;
        }
    }
}

// a full grid row, so every article's tiles start on a new line
.studio-group-title {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    gap: 12px;
    color: $site-background;
    font-size: 15px;
    font-weight: 500;

    // centring the line box puts the line 0.11em above the middle of the
    // digits and capitals (measured, Segoe UI); this moves it onto the "·"
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #e3e3e0;
        transform: translateY(0.11em);
    }

    &:not(:first-child) {
        margin-top: 12px;
    }
}

// two tiles only once both get 230px, on a phone one tile per row
@media (max-width: $breakpoint-xsmall-max) {
    .studio-grid {
        gap: 10px;
    }
}

.studio-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 8px 22px rgba(0, 0, 0, 0.1);
    }

    &.has-warning {
        box-shadow: 0 0 0 2px $danger-color;
    }

    // a fresh copy: the same photo as its neighbour, the ring tells which
    &.is-new {
        box-shadow: 0 0 0 3px $info-color;
        transition: box-shadow 0.6s;
    }
}

.studio-tile-stage {
    position: relative;
    margin: 8px 8px 0 8px;
    padding-bottom: 80%;
    border-radius: 8px;
    background: #f3f2ee;
    overflow: hidden;
    cursor: pointer;
}

// the whole picture; the box marks the crop and carries the frame preview
.studio-crop-frame {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 4px;
    background-color: #e5e5e5;
    background-repeat: no-repeat;
    background-size: 100% 100%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15), 0 6px 14px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    // what is cut off is darker and struck through, as in the crop modal
    // (.studio-cropper); the crop box paints its part again above it
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
                -45deg,
                rgba(255, 255, 255, 0.28) 0 2px,
                transparent 2px 12px
            ),
            rgba(0, 0, 0, 0.62);
    }
}

.studio-crop-box {
    position: absolute;
    background-repeat: no-repeat;
    box-shadow: inset 0 0 0 2px #fff;
}

.studio-skeleton {
    position: absolute;
    inset: 16%;
    border-radius: 4px;
    background: linear-gradient(90deg, #e9e9e9 0%, #f7f7f7 50%, #e9e9e9 100%);
    background-size: 200% 100%;
    animation: studio-shimmer 1.2s linear infinite;
}

@keyframes studio-fade-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes studio-shimmer {
    from {
        background-position: 200% 0;
    }
    to {
        background-position: -200% 0;
    }
}

.studio-tile-hover {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(18, 18, 18, 0.28);
    opacity: 0;
    transition: opacity 0.2s;

    span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 14px;
        border-radius: 17px;
        background: #fff;
        font-size: 14px;
        font-weight: 500;
    }

    .studio-tile-stage:hover & {
        opacity: 1;
    }
}

.studio-tile-tools {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    display: flex;
    gap: 6px;

    // always shown, with a pointer too (2026-10-25)
    button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border: 0;
        border-radius: 50%;
        background: #fff;
        color: $site-background;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
        cursor: pointer;

        // duplicating: shown, but greyed, its tooltip says so
        &.is-busy {
            opacity: 0.5;
            cursor: progress;
        }
    }
}

@media (hover: none) {
    .studio-tile-hover {
        display: none;
    }
}

.studio-flag {
    position: absolute;
    left: 8px;
    bottom: 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;

    &.warning {
        background: $warning-color;
        color: #3d3200;
    }

    &.danger {
        background: $danger-color;
        color: #fff;
    }
}

.studio-tile-progress {
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 4px;
    width: calc(100% - 20px);
    height: 4px !important;
    margin: 0 !important;
}

.studio-tile-error {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    background: rgba(239, 39, 75, 0.94);
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    cursor: default;
}

.studio-tile-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 10px;
    padding: 12px 12px 12px 12px;
}

.studio-tile-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: auto;
    padding-top: 2px;
}

.studio-tile-price {
    text-align: right;
}

.studio-tier-hint {
    display: block;
    color: #1d7a58;
    font-size: 12px;
    white-space: nowrap;
}

.studio-stepper {
    display: inline-flex;
    align-items: center;
    height: 32px;
    border-radius: 16px;
    background: #f0efeb;
    overflow: hidden;

    button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 100%;
        border: 0;
        background: none;
        color: $site-background;
        cursor: pointer;

        &:hover:not(:disabled) {
            background: #e5e4df;
        }

        &:disabled {
            color: #ccc;
            cursor: not-allowed;
        }
    }

    input {
        width: 26px;
        height: 100%;
        border: 0;
        padding: 0;
        background: none;
        color: $site-background;
        font: inherit;
        font-weight: 500;
        text-align: center;
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-tile-stage {
        margin: 6px 6px 0 6px;
        border-radius: 10px;
    }

    .studio-tile-body {
        padding: 10px 8px;
        gap: 8px;
    }

    .studio-tile-footer {
        flex-wrap: wrap;
    }
}

.studio-tile-add {
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 260px;
    border: 2px dashed #d4d4d0;
    background: transparent;
    box-shadow: none;
    color: $muted;
    font-weight: 500;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;

    &:hover {
        border-color: $info-color;
        box-shadow: none;
        color: $info-color;
    }

    small {
        padding: 0 10px;
        font-size: 12px;
    }

    @media (max-width: $breakpoint-xsmall-max) {
        min-height: 120px;
    }
}

// order: as wide as the pictures and read top to bottom, the pay button
// right after the summary like LunaCart. Each block has its title on the
// left and its fields on the right

// no overflow: hidden, UIkit's scrollIntoView would take the card for a
// scroll container and drop the top bar offset for the blocks inside it
.studio-checkout-card {
    border-radius: 16px;
}

// the one keyboard, docked like a device keyboard over the page bottom;
// over the editor, the sheets and the control centre's modal
.studio-docked-keyboard {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1030;
    padding: 12px 16px;
    background: #fff;
    box-shadow: 0 -12px 32px rgba(0, 0, 0, 0.16);
    // in <body>, which carries the old dark theme's light text
    color: $site-background;

    // App.vue squares the on-screen keyboard with !important for the old views
    ::v-deep .hg-theme-default {
        max-width: 1100px;
        margin: 0 auto;
        border-radius: 16px !important;
    }

    // terminal only, so always at kiosk size
    ::v-deep .hg-button {
        height: 56px;
        border-radius: 12px !important;
        font-size: 20px;
    }

    &.is-numpad ::v-deep .hg-theme-default {
        max-width: 420px;
    }
}

// on a phone the fields line up with the photos in the tiles above
@media (max-width: $breakpoint-xsmall-max) {
    .studio-checkout-card ::v-deep .uk-card-body {
        padding: 18px 8px;
    }
}

.studio-block {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    column-gap: 40px;
    align-items: start;
    scroll-margin-top: $nav + 16px;

    & > * {
        grid-column: 2;
    }

    & > .studio-card-title {
        grid-column: 1;
        grid-row: 1 / span 20;
    }

    & + & {
        margin-top: 28px;
        padding-top: 28px;
        border-top: 1px solid $line;
    }
}

@media (max-width: $breakpoint-small-max) {
    .studio-block {
        grid-template-columns: minmax(0, 1fr);

        & > *,
        & > .studio-card-title {
            grid-column: 1;
            grid-row: auto;
        }
    }
}

// the generic components are only slightly rounded, here they match the tiles
.studio-checkout .studio-checkout-card {
    ::v-deep .card-select {
        border-radius: 14px;
    }

    ::v-deep .uk-input,
    ::v-deep .uk-select,
    ::v-deep .uk-textarea {
        border-radius: 12px;
    }
}

.paper-text {
    font-weight: 500;
    word-break: break-word;
}

.paper-price {
    font-size: 14px;
}

.badge {
    font-size: 13px;
    padding: 9px 11px;
    margin-top: -3px;
    margin-right: 5px;
    background-color: $info-color !important;
}

.studio-summary-row + .studio-summary-row {
    margin-top: 14px;
}

// fee and shipping stand apart from the pictures, like the total
.studio-summary-row:not(.is-extra) + .studio-summary-row.is-extra {
    margin-top: 22px;
}

.total-costs-text {
    font-weight: 500;
    margin-top: 22px !important;
}

.vat {
    font-size: 13px;
    margin-top: 10px;
    color: $muted;
}

.studio-payment-pick {
    margin-bottom: 12px;
    font-size: 14px;
}

.studio-pay-amount::before {
    content: '· ';
}

// on a phone the amount gets its own line instead of breaking anywhere
@media (max-width: $breakpoint-xsmall-max) {
    .studio-pay ::v-deep .uk-button {
        padding: 8px 20px !important;
        font-size: 16px !important;
        font-weight: 400 !important;
        line-height: 22px;
    }

    .studio-pay-amount {
        display: block;
        font-size: 14px;
        font-weight: 400;

        &::before {
            content: none;
        }
    }
}

.studio-terms {
    margin-bottom: 24px;

    ::v-deep .uk-checkbox {
        width: 22px;
        height: 22px;
    }
}

.studio-hint-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    padding: 2px 10px;
    border-radius: 10px;
    background: rgba(43, 179, 130, 0.12);
    color: #1d7a58;
    font-size: 12px;
}

.studio-progress {
    height: 6px !important;
    margin: 6px 0 !important;
}

.studio-round {
    border-radius: $border-radius-next;
}

// success

.studio-success {
    max-width: 560px;
    margin: 40px auto 0 auto;
}

.studio-success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    margin-bottom: 18px;
    border-radius: 50%;
    background: $success-color;
    color: #fff;
}

// legal footer, always visible

.studio-footer {
    padding: 22px 0 28px 0;
    border-top: 1px solid $line;
    font-size: 14px;

    .studio-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 8px 20px;
    }
}

// crop view: an extended modal, the cropper on a dark stage inside it

.studio-editor {
    position: fixed;
    inset: 0;
    z-index: 1010;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    background: rgba(18, 18, 18, 0.6);
}

.studio-editor-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1000px;
    height: 100%;
    max-height: 820px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;

    // as high as its content, the prices list
    &.is-auto {
        max-width: 760px;
        height: auto;
        max-height: 100%;
    }
}

// the uk-modal animation: the backdrop fades, the panel slides in from above
.studio-editor-enter-active,
.studio-editor-leave-active {
    transition: opacity 0.15s linear;

    .studio-editor-panel {
        transition: opacity 0.3s linear, transform 0.3s linear;
    }
}

.studio-editor-enter,
.studio-editor-leave-to {
    opacity: 0;

    .studio-editor-panel {
        opacity: 0;
        transform: translateY(-100px);
    }
}

.studio-editor-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
}

.studio-editor-title {
    min-width: 0;
    font-size: 17px;
    font-weight: 500;
}

.studio-editor-sub {
    color: $muted;
    font-size: 13px;
    font-weight: 400;
}

.studio-editor-nav {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;

    // the tooltips sit on these wrappers: a disabled button gets no hover,
    // so it lets the pointer through to its span
    & > span {
        display: inline-flex;
    }

    button:disabled {
        pointer-events: none;
    }
}

.studio-editor-close {
    margin-left: 8px;
}

.studio-round-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 50%;
    background: #f0efeb;
    color: $site-background;
    cursor: pointer;

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: #e5e4df;
    }
}

// the stage and, on the terminal, the format list right of it
.studio-editor-body {
    display: flex;
    flex: 1;
    gap: 20px;
    min-height: 0;
    padding: 0 20px;
}

.studio-editor-stage {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-width: 0;
    min-height: 0;
    border-radius: 12px;
    background: #121212;
    overflow: hidden;
}

.studio-cropper {
    width: 100%;
    height: 100%;

    // what is cut off is darker and struck through, so it reads as gone
    ::v-deep .vue-advanced-cropper__foreground {
        opacity: 1;
        background: repeating-linear-gradient(
                -45deg,
                rgba(255, 255, 255, 0.28) 0 2px,
                transparent 2px 12px
            ),
            rgba(0, 0, 0, 0.62);
    }
}

.studio-editor-tools {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px 20px 20px 20px;
}

.studio-editor-hint {
    color: $muted;
    font-size: 13px;
}

.studio-segmented {
    display: inline-flex;
    padding: 3px;
    border-radius: 21px;
    background: #f0efeb;

    button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 34px;
        padding: 0 14px;
        border: 0;
        border-radius: 17px;
        background: none;
        color: $muted;
        font: inherit;
        font-size: 14px;
        cursor: pointer;

        &.active {
            background: #fff;
            color: $site-background;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        }
    }

}

// the tile's format and frame chips, as high as the other tools in the row
.studio-editor-format .studio-chip,
.studio-editor-frame .studio-chip {
    height: 40px;
    padding: 0 14px 0 16px;
    border-radius: 20px;
    font-size: 14px;
}

// what is ordered (format, frame, amount) above, the crop tools below
.studio-editor-break {
    flex-basis: 100%;
    height: 0;
}

.studio-editor-amount {
    display: flex;
    align-items: center;
    gap: 10px;

    .studio-stepper {
        height: 40px;
        border-radius: 20px;
    }
}

.studio-soft-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 16px;
    border: 0;
    border-radius: 20px;
    background: #f0efeb;
    color: $site-background;
    font: inherit;
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: #e5e4df;
    }
}

// the crop applies live; this only says so, the X closes the same way
.studio-editor-done ::v-deep .uk-button {
    height: 40px;
    padding: 0 32px;
    border-radius: 20px;
    line-height: 40px;
}

// the visible height: on phones the browser bar covers the bottom of 100vh
@supports (height: 100dvh) {
    .studio-editor {
        box-sizing: border-box;
        bottom: auto;
        height: 100dvh;
    }
}

@media (max-width: $breakpoint-xsmall-max) {
    .studio-editor {
        padding: 10px;
    }

    // the tools can take more room than is left, the panel scrolls to them
    .studio-editor-panel {
        overflow-y: auto;
    }

    .studio-editor-body {
        flex: 1 0 300px;
        padding: 0 10px;
    }

    .studio-editor-bar {
        padding: 12px;
    }
}

// drop overlay

.studio-dropoverlay {
    position: fixed;
    inset: 0;
    z-index: 1020;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(20, 24, 59, 0.85);

    * {
        pointer-events: none;
    }
}

.studio-dropoverlay-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 600px;
    padding: 60px 24px;
    border: 3px dashed rgba(255, 255, 255, 0.7);
    border-radius: 16px;
    color: #fff;
    font-size: 22px;
    font-weight: 500;
    text-align: center;
}

// terminal format sheet: the gallery's big cards, format and paper side by
// side so every paper and price is one tap away

.studio-editor-panel.studio-sheet {
    max-width: 1240px;
}

// the groups side by side, so the wide screen shows most formats without
// scrolling; the rest scrolls inside the sheet
.studio-sheet-body {
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
    align-content: start;
    gap: 0 28px;
    min-height: 0;
    padding: 0 20px 20px 20px;
    overflow-y: auto;
}

.studio-sheet-group {
    padding: 12px 0;
    border-top: 1px solid #f0efeb;
}

.studio-sheet-group-title {
    margin-bottom: 10px;
    color: $site-background;
    font-size: 18px;
    font-weight: 500;
}

// room for a badge pill, which stands 7px above its card
.studio-sheet-papers:has(.has-badge) {
    padding-top: 8px;
}

// the papers fill the column, a lone one too
.studio-sheet-papers {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    align-content: start;
    align-items: start;
    gap: 12px;
}

.studio-overview-paper.studio-sheet-paper {
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    min-height: 104px;
    padding: 16px 12px;
    border-radius: 14px;
    text-align: center;

    &.has-badge {
        padding: 14px 10px;
    }

    small {
        margin: 0;
        color: inherit;
        font-size: 17px;
    }

    .studio-overview-tiers {
        flex-basis: auto;
        font-size: 14px;
    }
}

.studio-sheet-paper-name {
    font-size: 18px;
    font-weight: 500;
}

// TERMINAL_ADS_ENABLED: the ad strip on top and the slideshow below in the
// studio's rounded tiles; the components stay square in the old views
.studio-ads-header ::v-deep .uk-card {
    overflow: hidden;
    border-radius: 16px;
    background: #f3f2ee;

    img,
    video {
        display: block;
        margin: 0 auto;
    }
}

.studio-ads-footer ::v-deep .uk-slideshow-items {
    overflow: hidden;
    border-radius: 16px;
}

// online on the terminal: a hint under the methods, then the QR code panel
.studio-qr-pay-hint {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border-radius: 16px;
    background: #f3f2ee;
    font-size: 17px;
}

.studio-editor-panel.studio-qr-pay {
    max-width: 560px;
}

.studio-qr-pay-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: 0 28px 28px 28px;
    text-align: center;
}

// the library draws a canvas and an img; white around it so phones read it
.studio-qr-pay-code {
    padding: 16px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 0 0 1px #e8e8e6;

    ::v-deep img,
    ::v-deep canvas {
        display: block;
    }
}

.studio-qr-pay-text {
    font-size: 18px;
}

.studio-qr-pay-wait {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
}

// crop modal on the terminal: the formats as tiles like the file browser's
// folders, a tap opens the format's papers with a back tile on top
.studio-editor-side {
    display: flex;
    flex: none;
    flex-direction: column;
    gap: 10px;
    width: 400px;
    min-height: 0;
    overflow-y: auto;

    .studio-sheet-group-title {
        margin: 4px 0 0 0;
    }
}

.studio-editor-side-tile {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 64px;
    padding: 0 20px;
    border: 0;
    border-radius: 16px;
    background: #f3f2ee;
    color: $site-background;
    font: inherit;
    font-size: 18px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;

    &.active {
        background: $site-background;
        color: #fff;
    }

    &.is-back {
        justify-content: flex-start;
    }
}


// terminal file browser: the media as segments, the folders as pills, photos
// as tiles that get a check

.studio-editor-panel.studio-browser {
    max-width: 1400px;
    max-height: none;
}

.studio-browser-sources {
    flex-wrap: wrap;
    min-width: 0;
}

.studio-browser-path {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 0 20px 14px 20px;
}

.studio-browser-body {
    flex: 1;
    min-height: 0;
    padding: 4px 20px 20px 20px;
    overflow-y: auto;
}

.studio-browser-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    height: 100%;
    min-height: 240px;
    color: $muted;
    font-size: 20px;
    text-align: center;
}

.studio-browser-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 14px;
}

// an empty subfolder: the hint beside the back tile
.studio-browser-note {
    display: flex;
    align-items: center;
    grid-column: span 2;
    color: $muted;
    font-size: 20px;
}

.studio-browser-folder,
.studio-browser-file {
    position: relative;
    aspect-ratio: 1.25;
    border: 0;
    border-radius: 16px;
    background: #f3f2ee;
    color: $site-background;
    font: inherit;
    cursor: pointer;
}

.studio-browser-folder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px;
    font-size: 17px;
    font-weight: 500;

    span {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.studio-browser-file {
    padding: 8px;

    img {
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 8px;
        object-fit: contain;
        transition: opacity 0.2s;
    }

    &.is-selected {
        box-shadow: inset 0 0 0 4px $info-color;
    }

    // the tiles' skeleton until the thumbnail is in
    &.is-loading {
        background: linear-gradient(90deg, #e9e9e9 0%, #f7f7f7 50%, #e9e9e9 100%);
        background-size: 200% 100%;
        animation: studio-shimmer 1.2s linear infinite;

        img {
            opacity: 0;
        }
    }
}

// a white dot vanished on light photos: a white ring on a dark veil,
// filled blue once picked, reads on any photo
.studio-browser-check {
    position: absolute;
    top: 10px;
    right: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(18, 18, 18, 0.3);
    box-shadow: inset 0 0 0 1.5px #fff, 0 1px 4px rgba(0, 0, 0, 0.3);
    color: transparent;

    .is-selected & {
        background: $info-color;
        color: #fff;
    }

    ::v-deep .icon,
    ::v-deep .uk-icon,
    ::v-deep svg {
        display: block;
    }
}

.studio-browser-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;
    padding: 16px 20px 20px 20px;
    border-top: 1px solid $line;

    .studio-soft-button:disabled {
        opacity: 0.45;
        cursor: default;
    }

    // between Abbrechen on the left and the pick buttons on the right
    .studio-browser-hint {
        flex: 1;
        color: $muted;
        font-size: 16px;
        text-align: right;
    }

    // the add button as the soft pills beside it (52px, radius 26, 16px at
    // kiosk size): Button's large, round look made them two designs.
    // Button's large size is !important
    ::v-deep .uk-button {
        position: relative;
        min-height: 52px !important;
        border-radius: 26px;
        font-size: 16px !important;
        line-height: 52px;
    }

    // Button's -30px offset assumes UIkit's line height; at 52px the
    // spinner's line made the button grow while loading
    ::v-deep .loading-item {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        line-height: normal;
    }

    // a disabled button gets no pointer events: its wrapper shows the tooltip
    .studio-browser-why {
        display: inline-flex;

        :disabled,
        ::v-deep .uk-button-disabled {
            pointer-events: none;
        }
    }
}

// terminal: the same page at touch size. Only sizes change here, the look
// stays the studio's

.studio.is-kiosk {
    font-size: 18px;

    // a kiosk screen is wide: bigger tiles rather than more of them
    .studio-container {
        max-width: 1440px;
    }

    .studio-nav-inner {
        height: 80px;
    }

    .studio-logo {
        max-height: 48px;
        max-width: 200px;
    }

    .studio-name {
        font-size: 20px;
    }

    .studio-nav-end ::v-deep .uk-button {
        height: 52px;
        padding: 0 26px;
        border-radius: 26px;
        font-size: 17px;
        line-height: 52px;
    }

    .studio-section {
        scroll-margin-top: 96px;
    }

    .studio-title {
        font-size: 28px;
    }

    .studio-title-sub {
        font-size: 18px;
    }

    .studio-card-title {
        font-size: 20px;
    }

    .studio-small {
        font-size: 15px;
    }

    .studio-add-button {
        height: 52px;
        padding: 0 22px 0 8px;
        border-radius: 26px;
        font-size: 17px;
    }

    .studio-add-icon {
        width: 36px;
        height: 36px;
    }

    .studio-chips {
        gap: 10px;
    }

    .studio-chip {
        gap: 8px;
        height: 48px;
        padding: 0 16px 0 18px;
        border-radius: 24px;
        font-size: 16px;
    }

    // the format chip over the whole tile, the main thing to tap
    .studio-tile-body .studio-chip.is-strong {
        flex: 1 1 100%;
        justify-content: space-between;
    }

    .studio-grid {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 24px;
    }

    .studio-group-title {
        font-size: 18px;
    }

    .studio-tile-hover {
        display: none;
    }

    .studio-tile-tools {
        top: 10px;
        right: 10px;
        gap: 10px;

        button {
            width: 48px;
            height: 48px;
        }
    }

    .studio-flag {
        padding: 4px 12px;
        border-radius: 13px;
        font-size: 14px;
    }

    .studio-tile-body {
        gap: 14px;
        padding: 16px;
    }

    .studio-stepper {
        height: 52px;
        border-radius: 26px;

        button {
            width: 52px;
        }

        input {
            width: 40px;
            font-size: 18px;
        }
    }

    .studio-tile-price {
        font-size: 19px;
    }

    .studio-tier-hint {
        font-size: 14px;
    }

    .studio-tile-add {
        min-height: 360px;
        font-size: 19px;
    }

    .studio-round-button {
        width: 56px;
        height: 56px;
    }

    .studio-editor {
        padding: 24px;
    }

    // the crop view as big as the screen allows, the page still around it
    .studio-editor-panel.is-crop {
        max-width: 1440px;
        max-height: 960px;
    }

    .studio-editor-bar {
        padding: 20px 24px;
    }

    .studio-editor-title {
        font-size: 20px;
    }

    .studio-editor-sub {
        font-size: 15px;
    }

    .studio-editor-hint {
        font-size: 15px;
    }

    .studio-segmented {
        padding: 4px;
        border-radius: 28px;

        button {
            height: 48px;
            padding: 0 20px;
            border-radius: 24px;
            font-size: 16px;
        }
    }

    .studio-soft-button {
        height: 52px;
        padding: 0 22px;
        border-radius: 26px;
        font-size: 16px;
    }

    .studio-editor-done ::v-deep .uk-button {
        height: 52px;
        padding: 0 40px;
        border-radius: 26px;
        font-size: 17px;
        line-height: 52px;
    }

    .studio-editor-format .studio-chip,
    .studio-editor-frame .studio-chip {
        height: 52px;
        padding: 0 18px 0 20px;
        border-radius: 26px;
        font-size: 16px;
    }

    .studio-editor-amount .studio-stepper {
        height: 52px;
        border-radius: 26px;
    }

    // the frame list stays a dropdown, at finger size
    .studio-dropdown.is-narrow {
        width: 300px;

        .studio-overview-paper {
            padding: 14px 16px;
            border-radius: 12px;
            font-size: 16px;
        }
    }

    .studio-swatch {
        width: 20px;
        height: 20px;
    }

    // checkout: option cards, fields and the pay button big enough to tap
    .studio-block {
        grid-template-columns: 260px minmax(0, 1fr);
    }

    .studio-option {
        padding: 18px 0;
    }

    .paper-text {
        font-size: 18px;
    }

    .paper-price {
        font-size: 16px;
    }

    .studio-checkout-card {
        ::v-deep .uk-input,
        ::v-deep .uk-select {
            height: 56px;
            font-size: 18px;
        }

        ::v-deep .uk-textarea {
            font-size: 18px;
        }

        ::v-deep .uk-form-label,
        ::v-deep label {
            font-size: 16px;
        }
    }

    .studio-terms ::v-deep .uk-checkbox {
        width: 32px;
        height: 32px;
    }

    .studio-pay ::v-deep .uk-button {
        padding: 18px 24px !important;
        font-size: 20px !important;
        line-height: 28px;
    }

    .studio-success ::v-deep .uk-button {
        font-size: 18px;
    }

    .studio-footer {
        font-size: 16px;
    }
}

@media (max-width: $breakpoint-small-max) {
    .studio.is-kiosk .studio-block {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>

<style lang="scss">
// the screensaver is a UIkit modal moved to <body>, out of reach of the scoped
// styles; only the new views (this one, Main.vue) set studio-page, so
// MainLegacy keeps its square start button
html.studio-page .start-struct .uk-button {
    border-radius: 35px;
}
</style>
